import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Copy,
  Download,
  Eye,
  FileText,
  Layers3,
  List,
  MessageCircle,
  PlusCircle,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  Stethoscope,
  Users,
  X,
} from 'lucide-react';
import { toast } from 'react-toastify';
import ActionButton from '../../components/ui/ActionButton';
import AssessmentCreateDialog from '../../features/assessments-v2/components/AssessmentCreateDialog';
import { supabase } from '../../config/supabase';
import { useAuth } from '../../context/AuthContext';
import {
  buildMedicalRecordAuthorSnapshot,
  fetchMedicalRecordAuthors,
  requiresProfessionalRegistration,
} from '../../utils/medicalRecordAuthor';
import { buildSignedDocumentMetadata, buildVerificationUrl, getAssessmentSignatureMetadata } from '../../utils/documentVerification';
import { getPatientSearchScore } from '../../utils/patientSearch';
import { toLocalDateTimeInput } from '../../utils/medicalRecordVitalSigns';
import AssessmentResultStatusModal from '../../features/assessments-v2/components/AssessmentResultStatusModal';
import AssessmentReportEditorModal from '../../features/assessments-v2/components/AssessmentReportEditorModal';
import {
  type AssessmentHistoryComparisonEntry,
  type AssessmentHistoryComparisonTestKey,
} from '../../features/assessments-v2/components/AssessmentHistoryComparisonCard';
import TC6ResultEditor from '../../features/assessments-v2/components/TC6ResultEditor';
import {
  ASSESSMENT_V2_COMPOSITE_REPORT_KEY,
  ASSESSMENT_V2_REPORT_KEY,
  ASSESSMENT_V2_ENABLED_TESTS,
  ASSESSMENT_V2_CREATABLE_TESTS,
  ASSESSMENT_V2_MODAL_GROUPS,
  countAssessmentV2CatalogSection,
  filterAssessmentV2CatalogGroups,
  getAssessmentV2Label,
  getAssessmentV2ResultLabel,
  isAssessmentV2LegacyReadOnlyResult,
  isAssessmentV2ReportKey,
  isAssessmentV2QuestionnaireTest,
  type AssessmentV2TestKey,
} from '../../features/assessments-v2/registry/assessmentRegistry';
import {
  buildClinicalReferenceDecision,
  getCurrentAssessmentTestVersion,
} from '../../features/assessments-v2/references/clinicalReferenceRegistry';
import ACTResultRenderer, {
  getACTSeverityLabel,
  getACTSeverityTone,
} from '../../features/assessments-v2/renderers/ACTResultRenderer';
import AQLQResultRenderer, {
  getAQLQSeverityLabel,
  getAQLQSeverityTone,
} from '../../features/assessments-v2/renderers/AQLQResultRenderer';
import CATResultRenderer, { getCATSeverityLabel, getCATSeverityTone } from '../../features/assessments-v2/renderers/CATResultRenderer';
import HADSResultRenderer, {
  getHADSOverallSeverityLabel,
  getHADSOverallSeverityTone,
} from '../../features/assessments-v2/renderers/HADSResultRenderer';
import PSQIResultRenderer, {
  getPSQISeverityLabel,
  getPSQISeverityTone,
} from '../../features/assessments-v2/renderers/PSQIResultRenderer';
import HandgripResultRenderer, {
  getHandgripSeverityLabel,
  getHandgripSeverityTone,
} from '../../features/assessments-v2/renderers/HandgripResultRenderer';
import PeripheralMuscleStrengthResultRenderer, {
  getPeripheralStrengthSeverityLabel,
  getPeripheralStrengthSeverityTone,
} from '../../features/assessments-v2/renderers/PeripheralMuscleStrengthResultRenderer';
import OneRMResultRenderer, {
  getOneRMSeverityLabel,
  getOneRMSeverityTone,
} from '../../features/assessments-v2/renderers/OneRMResultRenderer';
import PimaxResultRenderer, {
  getPimaxSeverityLabel,
  getPimaxSeverityTone,
} from '../../features/assessments-v2/renderers/PimaxResultRenderer';
import PemaxResultRenderer, {
  getPemaxSeverityLabel,
  getPemaxSeverityTone,
} from '../../features/assessments-v2/renderers/PemaxResultRenderer';
import SIndexResultRenderer, {
  getSIndexSeverityLabel,
  getSIndexSeverityTone,
} from '../../features/assessments-v2/renderers/SIndexResultRenderer';
import RespiratoryEnduranceResultRenderer, {
  getRespiratoryEnduranceSeverityLabel,
  getRespiratoryEnduranceSeverityTone,
} from '../../features/assessments-v2/renderers/RespiratoryEnduranceResultRenderer';
import LCADLResultRenderer, {
  getLCADLSeverityLabel,
  getLCADLSeverityTone,
} from '../../features/assessments-v2/renderers/LCADLResultRenderer';
import MLHFQResultRenderer, {
  getMLHFQSeverityLabel,
  getMLHFQSeverityTone,
} from '../../features/assessments-v2/renderers/MLHFQResultRenderer';
import PFSDQMResultRenderer, {
  getPFSDQSeverityLabel,
  getPFSDQSeverityTone,
} from '../../features/assessments-v2/renderers/PFSDQMResultRenderer';
import SAQResultRenderer, {
  getSAQOverallScore,
  getSAQSeverityLabel,
  getSAQSeverityTone,
} from '../../features/assessments-v2/renderers/SAQResultRenderer';
import SGRQResultRenderer, {
  getSGRQSeverityLabel,
  getSGRQSeverityTone,
} from '../../features/assessments-v2/renderers/SGRQResultRenderer';
import STSResultRenderer from '../../features/assessments-v2/renderers/STSResultRenderer';
import TC6ResultRenderer, {
  getTC6SeverityLabel,
  getTC6SeverityTone,
} from '../../features/assessments-v2/renderers/TC6ResultRenderer';
import ISWTResultRenderer, {
  getISWTSeverityLabel,
  getISWTSeverityTone,
} from '../../features/assessments-v2/renderers/ISWTResultRenderer';
import ESWTResultRenderer, {
  getESWTSeverityLabel,
  getESWTSeverityTone,
} from '../../features/assessments-v2/renderers/ESWTResultRenderer';
import MST6ResultRenderer, {
  get6MSTSeverityLabel,
  get6MSTSeverityTone,
} from '../../features/assessments-v2/renderers/MST6ResultRenderer';
import ChesterStepResultRenderer, {
  getChesterSeverityLabel,
  getChesterSeverityTone,
} from '../../features/assessments-v2/renderers/ChesterStepResultRenderer';
import GaitSpeedResultRenderer, { getGaitSpeedSeverityTone } from '../../features/assessments-v2/renderers/GaitSpeedResultRenderer';
import PeakFlowResultRenderer from '../../features/assessments-v2/renderers/PeakFlowResultRenderer';
import PeakCoughFlowResultRenderer from '../../features/assessments-v2/renderers/PeakCoughFlowResultRenderer';
import PosturalVitalCapacityResultRenderer, { getPosturalVitalCapacitySeverityLabel, getPosturalVitalCapacitySeverityTone } from '../../features/assessments-v2/renderers/PosturalVitalCapacityResultRenderer';
import SnipResultRenderer, { getSnipSeverityLabel, getSnipSeverityTone } from '../../features/assessments-v2/renderers/SnipResultRenderer';
import PeakCoughFlowTest from '../../features/assessments-v2/tests/PeakCoughFlowTest';
import PosturalVitalCapacityTest from '../../features/assessments-v2/tests/PosturalVitalCapacityTest';
import SnipTest from '../../features/assessments-v2/tests/SnipTest';
import { normalizePeakCoughFlowPayload, resolvePeakCoughFlowPatientContext, summarizePeakCoughFlowPayload, type PeakCoughFlowResultPayload } from '../../features/assessments-v2/peak-cough-flow/peakCoughFlowDomain';
import { normalizePosturalVitalCapacityPayload, summarizePosturalVitalCapacityPayload, normalizeSnipPayload, summarizeSnipPayload, type PosturalVitalCapacityResultPayload, type SnipResultPayload } from '../../features/assessments-v2/respiratory-muscle/neuromuscularDomain';
import PeakFlowDiaryManager from '../../features/assessments-v2/peak-flow-diary/PeakFlowDiaryManager';
import PeakFlowDiaryDialog from '../../features/assessments-v2/peak-flow-diary/PeakFlowDiaryDialog';
import type { PeakFlowDiaryExportData } from '../../features/assessments-v2/peak-flow-diary/peakFlowDiaryDomain';
import TUGResultRenderer, { getTUGSeverityTone } from '../../features/assessments-v2/renderers/TUGResultRenderer';
import InitialAssessmentResultRenderer from '../../features/assessments-v2/renderers/InitialAssessmentResultRenderer';
import CompositeReportRenderer from '../../features/assessments-v2/renderers/CompositeReportRenderer';
import AssessmentReportRenderer from '../../features/assessments-v2/renderers/AssessmentReportRenderer';
import MMRCResultRenderer, { getMMRCSeverityLabel, getMMRCSeverityTone } from '../../features/assessments-v2/renderers/MMRCResultRenderer';
import { AssessmentPDFV2Service } from '../../features/assessments-v2/services/AssessmentPDFV2Service';
import { AssessmentInvitesV2Service } from '../../features/assessments-v2/services/AssessmentInvitesV2Service';
import {
  AssessmentResultsV2Service,
  type AssessmentResultV2Revision,
  type AssessmentTestUsage,
} from '../../features/assessments-v2/services/AssessmentResultsV2Service';
import { AssessmentReportV2Service } from '../../features/assessments-v2/services/AssessmentReportV2Service';
import { AssessmentSessionsV2Service } from '../../features/assessments-v2/services/AssessmentSessionsV2Service';
import { ClientReportSettingsService } from '../../features/assessments-v2/services/ClientReportSettingsService';
import { TC6ChartCaptureService } from '../../features/assessments-v2/services/TC6ChartCaptureService';
import ACTTest, { type ACTResultPayload } from '../../features/assessments-v2/tests/ACTTest';
import AQLQTest, { type AQLQResultPayload } from '../../features/assessments-v2/tests/AQLQTest';
import CATTest, { type CATResultPayload } from '../../features/assessments-v2/tests/CATTest';
import HADSTest, { type HADSResultPayload } from '../../features/assessments-v2/tests/HADSTest';
import PSQITest, { type PSQIResultPayload } from '../../features/assessments-v2/tests/PSQITest';
import HandgripTest, { type HandgripResultPayload } from '../../features/assessments-v2/tests/HandgripTest';
import PeripheralMuscleStrengthTest, {
  type PeripheralMuscleStrengthResultPayload,
} from '../../features/assessments-v2/tests/PeripheralMuscleStrengthTest';
import OneRMTest, { type OneRMResultPayload } from '../../features/assessments-v2/tests/OneRMTest';
import PimaxTest, { type PimaxResultPayload } from '../../features/assessments-v2/tests/PimaxTest';
import PemaxTest, { type PemaxResultPayload } from '../../features/assessments-v2/tests/PemaxTest';
import SIndexTest, { type SIndexResultPayload } from '../../features/assessments-v2/tests/SIndexTest';
import RespiratoryEnduranceTest, {
  type RespiratoryEnduranceResultPayload,
} from '../../features/assessments-v2/tests/RespiratoryEnduranceTest';
import PeakFlowTest, { type PeakFlowResultPayload } from '../../features/assessments-v2/tests/PeakFlowTest';
import LCADLTest, { type LCADLResultPayload } from '../../features/assessments-v2/tests/LCADLTest';
import MLHFQTest, { type MLHFQResultPayload } from '../../features/assessments-v2/tests/MLHFQTest';
import MMRCTest, { type MMRCResultPayload } from '../../features/assessments-v2/tests/MMRCTest';
import PFSDQMTest, { type PFSDQMResultPayload } from '../../features/assessments-v2/tests/PFSDQMTest';
import SAQTest, { type SAQResultPayload } from '../../features/assessments-v2/tests/SAQTest';
import SGRQTest, { type SGRQResultPayload } from '../../features/assessments-v2/tests/SGRQTest';
import FiveSTSTest, { type FiveSTSResultPayload } from '../../features/assessments-v2/tests/FiveSTSTest';
import STS1MinTest, { type STS1MinResultPayload } from '../../features/assessments-v2/tests/STS1MinTest';
import STS30sTest, { type STS30sResultPayload } from '../../features/assessments-v2/tests/STS30sTest';
import TC6Test from '../../features/assessments-v2/tests/TC6Test';
import ISWTTest from '../../features/assessments-v2/tests/ISWTTest';
import ESWTTest from '../../features/assessments-v2/tests/ESWTTest';
import MST6Test from '../../features/assessments-v2/tests/MST6Test';
import ChesterStepTest from '../../features/assessments-v2/tests/ChesterStepTest';
import GaitSpeedTest, { type GaitSpeedResultPayload } from '../../features/assessments-v2/tests/GaitSpeedTest';
import TUGTest, { type TugResultPayload } from '../../features/assessments-v2/tests/TUGTest';
import InitialAssessmentTest, {
  type InitialAssessmentAnamnesisCreationRequest,
  type InitialAssessmentResultPayload,
} from '../../features/assessments-v2/tests/InitialAssessmentTest';
import { InitialAssessmentAnamnesisService } from '../../features/assessments-v2/initial-assessment/InitialAssessmentAnamnesisService';
import { AnamnesisAssessmentLinkService } from '../../features/assessments-v2/services/AnamnesisAssessmentLinkService';
import { InitialAssessmentIntegrityService } from '../../features/assessments-v2/services/InitialAssessmentIntegrityService';
import { completeAssessmentDraft } from '../../features/assessments-v2/services/completeAssessmentDraft';
import {
  isInitialAssessmentComplete,
  normalizeInitialAssessmentPayload,
  summarizeInitialAssessment,
} from '../../features/assessments-v2/initial-assessment/initialAssessmentDomain';
import {
  normalizeHandgripPayload,
  summarizeHandgripResult,
  type HandgripChartImages,
} from '../../features/assessments-v2/handgrip/handgripDomain';
import {
  normalizePeripheralMuscleStrengthPayload,
  summarizePeripheralMuscleStrengthResult,
} from '../../features/assessments-v2/peripheral-muscle/peripheralMuscleDomain';
import {
  ONE_RM_PROTOCOL_STEPS,
  ONE_RM_REFERENCE_NOTES,
  buildOneRMPayload,
  normalizeOneRMPayload,
  summarizeOneRMPayload,
} from '../../features/assessments-v2/one-rm/oneRmDomain';
import {
  buildEnduranceInterpretation,
  extractHandgripMaxFromPayload,
  extractPimaxMaxFromPayload,
  type PimaxPositionSeries,
  summarizePimaxPayload,
  summarizePemaxPayload,
  summarizeRespiratoryEndurancePayload,
  summarizeSIndexPayload,
} from '../../features/assessments-v2/respiratory-muscle/respiratoryMuscleDomain';
import {
  formatPeakFlowValue,
  getPeakFlowSeverityTone,
  normalizePeakFlowPayload,
  summarizePeakFlowPayload,
} from '../../features/assessments-v2/peak-flow/peakFlowDomain';
import {
  getAssessmentResultIdentifier,
  getPreferredPatientIdentifier,
} from '../../features/assessments-v2/utils/patientIdentifier';
import {
  getAssessmentInterpretationDraft,
  withAssessmentInterpretationOverride,
} from '../../features/assessments-v2/utils/interpretationOverride';
import { normalizePatientHeightCm, normalizePatientWeightKg, toFiniteNumber } from '../../features/assessments-v2/utils/patientMetrics';
import {
  isTc6WorkflowCompleted,
  recalculateTc6Payload,
  resolveTC6Interpretation,
  summarizeTc6Result,
  type Tc6ChartImages,
  type Tc6ResultPayload,
} from '../../features/assessments-v2/tc6/tc6Domain';
import {
  resolveESWTInterpretation,
  resolveISWTInterpretation,
  summarizeESWTResult,
  summarizeISWTResult,
  type ISWTResultPayload as ShuttleIswtResultPayload,
  type ESWTResultPayload as ShuttleEswtResultPayload,
} from '../../features/assessments-v2/shuttle-walk/shuttleWalkDomain';
import {
  resolveChesterInterpretation,
  resolveSixMSTInterpretation,
  summarizeChesterResult,
  summarizeSixMSTResult,
  type ChesterResultPayload,
  type SixMSTResultPayload,
} from '../../features/assessments-v2/step-tests/stepTestsDomain';
import {
  GAIT_SPEED_REFERENCE_NOTE,
  formatGaitSpeed,
  isGaitSpeedCompleted,
  normalizeGaitSpeedPayload,
  summarizeGaitSpeedResult,
} from '../../features/assessments-v2/gait-speed/gaitSpeedDomain';
import {
  TUG_REFERENCE_NOTE,
  formatTugSeconds,
  isTugWorkflowCompleted,
  normalizeTugPayload,
  summarizeTugResult,
} from '../../features/assessments-v2/tug/tugDomain';
import {
  getSTSSeverityLabel,
  getSTSSeverityTone,
  isStsWorkflowCompleted,
  normalizeFiveSTSPayload,
  normalizeStsRepetitionPayload,
  summarizeStsResult,
  type StsTestKey,
} from '../../features/assessments-v2/sts/stsDomain';
import type {
  AssessmentInviteV2Status,
  AssessmentResultLifecycleEntry,
  AssessmentSessionV2,
  AssessmentResultV2,
  ResolvedClientReportBranding,
} from '../../features/assessments-v2/types';
import { DoctorService } from '../../services/DoctorService';
import type { Patient as LegacyPatient } from '../../types';
import type { UnifiedPatient } from '../../types/unified-financial';
import { buildWhatsAppUrl } from '../../utils/contact';
import {
  createAssessmentReportPayloadV2,
  createInitialAssessmentReportForm,
  countAssessmentReportFindings,
  getAssessmentReportPeriod,
  normalizeAssessmentReportPayload,
  validateAssessmentReportForm,
  type AssessmentReportEditorForm,
  type AssessmentReportPayloadV2,
  type AssessmentReportSourceSnapshotV2,
} from '../../features/assessments-v2/reports/assessmentReportDomain';
// ASSESSMENT_REPORT: orquestração do editor, das sugestões e do snapshot clínico.
import { buildAssessmentReportSourceSuggestion } from '../../features/assessments-v2/reports/assessmentReportSourceSuggestions';
import { reconcileAssessmentReportSuggestions } from '../../features/assessments-v2/reports/assessmentReportSuggestions';
import type { AssessmentReportDomain } from '../../features/assessments-v2/reports/assessmentReportCatalogs';

type AssessmentV2Patient = UnifiedPatient &
  LegacyPatient & {
    patientFullName?: string;
    patientCpf?: string;
    patientRg?: string;
    patientDateOfBirth?: string | null;
    patientGender?: string | null;
    patientHeightCm?: number | null;
    patientWeightKg?: number | null;
  };

type V2InfrastructureSnapshot = {
  branding: ResolvedClientReportBranding | null;
  sessionCount: number;
  resultCount: number;
};

type AssessmentV2FormState = {
  selectedTests: AssessmentV2TestKey[];
  patientId: string;
  datetime: string;
  notes: string;
};

type RunnerTestStatus = 'pending' | 'in_progress' | 'completed';
type AssessmentResultsViewMode = 'list' | 'cards' | 'patient';

const ASSESSMENT_HISTORY_COMPARISON_TEST_KEYS = new Set<string>([
  'PIMAX',
  'PEMAX',
  'S_INDEX',
  'RESP_ENDURANCE',
  'PEAK_FLOW',
  'PEAK_COUGH_FLOW',
  'CV_POSTURAL',
  'SNIP',
  '5xSTS',
  'STS_30S',
  'STS_1MIN',
  '4MGS',
]);

const supportsAssessmentHistoryComparison = (testKey?: string | null): testKey is AssessmentHistoryComparisonTestKey | 'PEAK_COUGH_FLOW' | 'CV_POSTURAL' | 'SNIP' =>
  Boolean(testKey && ASSESSMENT_HISTORY_COMPARISON_TEST_KEYS.has(testKey));

type AssessmentPatientContext = {
  birthDate: string | null;
  ageYears: number | null;
  sex: string | null;
  weightKg: number | null;
  heightCm: number | null;
  identifier: string | null;
};

type DraftSessionSummary = {
  session: AssessmentSessionV2;
  results: AssessmentResultV2[];
  patientName: string;
};

type PublicInviteMonitorState = {
  inviteId: string;
  clientId: string;
  patientName: string;
  patientPhone: string | null;
  tenantName: string;
  selectedTests: AssessmentV2TestKey[];
  url: string;
  shareMessage: string;
  expiresAt: string;
  status: AssessmentInviteV2Status;
  openedAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
};

const initialSnapshot: V2InfrastructureSnapshot = {
  branding: null,
  sessionCount: 0,
  resultCount: 0,
};

const initialForm = (): AssessmentV2FormState => ({
  selectedTests: [],
  patientId: '',
  datetime: toLocalDateTimeInput(),
  notes: '',
});

const normalizeSelectedTests = (tests: string[]): AssessmentV2TestKey[] =>
  tests.filter((testKey): testKey is AssessmentV2TestKey => ASSESSMENT_V2_CREATABLE_TESTS.includes(testKey as AssessmentV2TestKey));

const ASSESSMENT_V2_QUICK_TESTS = [
  'INITIAL_ASSESSMENT',
  'TC6',
  'CAT',
  'mMRC',
  'TUG',
  'HANDGRIP',
  '5xSTS',
  'HADS',
] as const satisfies readonly AssessmentV2TestKey[];

const ASSESSMENT_RESULTS_PAGE_SIZES = [50, 100, 200] as const;
type AssessmentResultsPageSize = (typeof ASSESSMENT_RESULTS_PAGE_SIZES)[number];

const isLegacyCompositeReportResult = (result?: Pick<AssessmentResultV2, 'testKey'> | null): boolean =>
  result?.testKey === ASSESSMENT_V2_COMPOSITE_REPORT_KEY;

const isAssessmentReportResult = (result?: Pick<AssessmentResultV2, 'testKey'> | null): boolean =>
  result?.testKey === ASSESSMENT_V2_REPORT_KEY;

const isAnyAssessmentReportResult = (result?: Pick<AssessmentResultV2, 'testKey'> | null): boolean =>
  isAssessmentV2ReportKey(result?.testKey);

const isDisplayableAssessmentResult = (result: AssessmentResultV2): boolean =>
  ASSESSMENT_V2_ENABLED_TESTS.includes(result.testKey as AssessmentV2TestKey) || isAnyAssessmentReportResult(result);

const hydrateAssessmentResultProfessionalSnapshot = (
  result: AssessmentResultV2,
  currentAuthor?: Awaited<ReturnType<typeof fetchMedicalRecordAuthors>> extends Map<string, infer T> ? T : never,
): AssessmentResultV2 => {
  if (!currentAuthor) {
    return result;
  }

  const shouldForceInternalRole = currentAuthor.role === 'interno';
  const nextProfessionalName =
    typeof result.professionalNameSnapshot === 'string' && result.professionalNameSnapshot.trim().length > 0
      ? result.professionalNameSnapshot
      : currentAuthor.name || 'Profissional';
  const nextProfessionalRole = shouldForceInternalRole
    ? 'interno'
    : result.professionalRoleSnapshot || currentAuthor.role || null;
  const nextProfessionalRegistration = shouldForceInternalRole
    ? null
    : result.professionalRegistrationSnapshot || currentAuthor.councilNumber || null;
  const nextProfessionalContext =
    result.payloadJson?.professionalContext && typeof result.payloadJson.professionalContext === 'object'
      ? {
          ...(result.payloadJson.professionalContext as Record<string, unknown>),
          userId: result.professionalUserId ?? currentAuthor.userId ?? null,
          name: nextProfessionalName,
          registration: nextProfessionalRegistration,
          role: nextProfessionalRole,
        }
      : undefined;

  return {
    ...result,
    professionalNameSnapshot: nextProfessionalName,
    professionalRegistrationSnapshot: nextProfessionalRegistration,
    professionalRoleSnapshot: nextProfessionalRole,
    payloadJson: nextProfessionalContext
      ? {
          ...result.payloadJson,
          professionalContext: nextProfessionalContext,
        }
      : result.payloadJson,
  };
};

const hydrateAssessmentResultsProfessionalSnapshots = async (
  results: AssessmentResultV2[],
  clientId: string,
): Promise<AssessmentResultV2[]> => {
  const professionalUserIds = [...new Set(results.map((result) => result.professionalUserId).filter(Boolean))] as string[];

  if (!professionalUserIds.length) {
    return results;
  }

  const authorDirectory = await fetchMedicalRecordAuthors(professionalUserIds, clientId);

  return results.map((result) =>
    hydrateAssessmentResultProfessionalSnapshot(
      result,
      result.professionalUserId ? authorDirectory.get(result.professionalUserId) : undefined,
    ),
  );
};

const getAssessmentReportDomainForTest = (testKey: string): AssessmentReportDomain => {
  switch (testKey) {
    case 'INITIAL_ASSESSMENT':
      return 'complementary_finding';
    case 'TC6':
    case 'ISWT':
    case 'ESWT':
    case '6MST':
    case 'CHESTER':
      return 'exercise_capacity';
    case 'TUG':
    case '4MGS':
    case '5xSTS':
    case 'STS_30S':
    case 'STS_1MIN':
      return 'functional_mobility';
    case 'HANDGRIP':
    case 'PERIPHERAL_MUSCLE_STRENGTH':
    case 'ONE_RM':
      return 'peripheral_muscle_strength';
    case 'PIMAX':
    case 'PEMAX':
    case 'S_INDEX':
    case 'RESP_ENDURANCE':
    case 'CV_POSTURAL':
    case 'SNIP':
      return 'respiratory_muscle_function';
    case 'CAT':
    case 'ACT':
    case 'mMRC':
      return 'symptoms_and_control';
    case 'SGRQ':
    case 'AQLQ':
    case 'LCADL':
    case 'MLHFQ':
    case 'SAQ':
    case 'PFSDQ-M':
      return 'quality_of_life_and_function';
    case 'HADS':
      return 'emotional_health';
    case 'PSQI':
      return 'sleep_and_recovery';
    case 'PEAK_FLOW':
    case 'PEAK_COUGH_FLOW':
      return 'expiratory_flow';
    default:
      return 'complementary_finding';
  }
};

const formatDurationSeconds = (seconds: number): string => {
  const safe = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(safe / 60);
  const remainder = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
};

const resolveRunnerPayload = (
  testKey: AssessmentV2TestKey,
  result?: Partial<AssessmentResultV2> | null,
): any => {
  if (!result) return undefined;
  const payload = result.payloadJson && typeof result.payloadJson === 'object' ? { ...result.payloadJson } : {};

  if (testKey === 'HANDGRIP') {
    const chartImages =
      result.assetsJson?.chartImages && typeof result.assetsJson.chartImages === 'object'
        ? (result.assetsJson.chartImages as HandgripChartImages)
        : null;

    return chartImages ? { ...payload, chartImages } : payload;
  }

  if (testKey === 'PIMAX' || testKey === 'PEMAX' || testKey === 'S_INDEX' || testKey === 'RESP_ENDURANCE') {
    const hydratedPayload = {
      ...payload,
      patientContext: buildEditableResultPatientContext(result as AssessmentResultV2, payload),
      ...(testKey === 'PIMAX' && Array.isArray(payload.positionSeries) ? {
        positionSeries: payload.positionSeries.map((series: PimaxPositionSeries) => ({
          ...series,
          result: { ...series.result, chartImages: result.assetsJson?.positionChartImages?.[series.id] ?? series.result.chartImages },
        })),
      } : {}),
    };
    const chartImages =
      result.assetsJson?.chartImages && typeof result.assetsJson.chartImages === 'object'
        ? (result.assetsJson.chartImages as Record<string, string>)
        : null;

    return chartImages ? { ...hydratedPayload, chartImages } : hydratedPayload;
  }

  if (testKey !== 'TC6') return payload;

  const chartImages =
    result.assetsJson?.chartImages && typeof result.assetsJson.chartImages === 'object'
      ? (result.assetsJson.chartImages as Tc6ChartImages)
      : null;

  return chartImages ? { ...payload, chartImages } : payload;
};

const formatTc6ComparisonDate = (value?: string | null): string => {
  if (!value) return 'Data não informada';

  const isoDate = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDate) return `${isoDate[3]}/${isoDate[2]}/${isoDate[1]}`;

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'Data não informada' : parsed.toLocaleDateString('pt-BR');
};

const toNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const buildEditableTc6ResultContent = (
  result: AssessmentResultV2,
  rawPayload: unknown,
  editor: {
    userId?: string | null;
    userName?: string | null;
    userEmail?: string | null;
  },
) => {
  const payloadRecord =
    rawPayload && typeof rawPayload === 'object' ? ({ ...(rawPayload as Record<string, any>) } as Record<string, any>) : {};
  delete payloadRecord.chartImages;

  const previousPayload =
    result.payloadJson && typeof result.payloadJson === 'object' ? (result.payloadJson as Record<string, any>) : {};
  const patientContextSource =
    payloadRecord.patientContext && typeof payloadRecord.patientContext === 'object'
      ? (payloadRecord.patientContext as Record<string, any>)
      : previousPayload.patientContext && typeof previousPayload.patientContext === 'object'
        ? (previousPayload.patientContext as Record<string, any>)
        : {};
  const professionalContextSource =
    payloadRecord.professionalContext && typeof payloadRecord.professionalContext === 'object'
      ? (payloadRecord.professionalContext as Record<string, any>)
      : previousPayload.professionalContext && typeof previousPayload.professionalContext === 'object'
        ? (previousPayload.professionalContext as Record<string, any>)
        : {};

  const birthDate =
    typeof patientContextSource.birthDate === 'string' && patientContextSource.birthDate.trim().length > 0
      ? patientContextSource.birthDate
      : result.patientBirthDateSnapshot || null;
  const patientHeightCm = normalizePatientHeightCm(
    toNullableNumber(patientContextSource.heightCm) ?? result.patientHeightSnapshot ?? null,
  );
  const patientWeightKg = toNullableNumber(patientContextSource.weightKg) ?? result.patientWeightSnapshot ?? null;
  const patientAgeYears =
    toNullableNumber(patientContextSource.ageYears) ?? calculateAgeAtDate(birthDate, result.assessmentDate);
  const patientContext = {
    birthDate,
    ageYears: patientAgeYears,
    sex:
      typeof patientContextSource.sex === 'string' && patientContextSource.sex.trim().length > 0
        ? patientContextSource.sex
        : result.patientSexSnapshot || null,
    weightKg: patientWeightKg,
    heightCm: patientHeightCm,
    identifier:
      typeof patientContextSource.identifier === 'string' && patientContextSource.identifier.trim().length > 0
        ? patientContextSource.identifier
        : result.patientMedicalRecordSnapshot || null,
  };
  const professionalContext = {
    name:
      typeof professionalContextSource.name === 'string' && professionalContextSource.name.trim().length > 0
        ? professionalContextSource.name
        : result.professionalNameSnapshot || 'Profissional',
    registration:
      typeof professionalContextSource.registration === 'string' && professionalContextSource.registration.trim().length > 0
        ? professionalContextSource.registration
        : result.professionalRegistrationSnapshot || null,
    role:
      typeof professionalContextSource.role === 'string' && professionalContextSource.role.trim().length > 0
        ? professionalContextSource.role
        : result.professionalRoleSnapshot || null,
  };

  const normalizedPayload: Tc6ResultPayload = {
    ...recalculateTc6Payload(payloadRecord as Tc6ResultPayload),
    patientContext,
    professionalContext,
    finishedAt:
      typeof payloadRecord.finishedAt === 'string' && payloadRecord.finishedAt.trim().length > 0
        ? payloadRecord.finishedAt
        : typeof previousPayload.finishedAt === 'string' && previousPayload.finishedAt.trim().length > 0
          ? previousPayload.finishedAt
          : result.assessmentDate,
  };

  const interpretation = resolveTC6Interpretation(normalizedPayload);
  const reportSource =
    normalizedPayload.report && typeof normalizedPayload.report === 'object'
      ? (normalizedPayload.report as Record<string, any>)
      : {};
  const summary = summarizeTc6Result(normalizedPayload);

  return {
    summaryJson: {
      attemptCount: summary.attemptCount,
      workflowStatus: summary.workflowStatus,
      bestAttempt: summary.bestAttempt,
      bestDistance_m: summary.bestDistance_m,
      selectedReferenceKey: summary.selectedReferenceKey,
      selectedReferenceLabel: summary.selectedReferenceLabel,
      predictedDistance_m: summary.predictedDistance_m,
      percentPredicted: summary.percentPredicted,
      meanSpeed_kmh: summary.meanSpeed_kmh,
      minSpo2: summary.minSpo2,
      spo2Drop: summary.spo2Drop,
      totalStopped_s: summary.totalStopped_s,
      hasSignificantDesaturation: summary.hasSignificantDesaturation,
      severity: summary.severity,
      patientAgeYears,
      patientSex: patientContext.sex,
      patientWeightKg,
      patientHeightCm,
    },
    payloadJson: {
      ...normalizedPayload,
      report: interpretation ? { ...reportSource, interpretation } : reportSource,
    },
    assetsJson: {
      ...(result.assetsJson && typeof result.assetsJson === 'object' ? result.assetsJson : {}),
      chartImages: {},
      tc6WorkflowStatus: summary.workflowStatus,
      payloadEditorAudit: {
        editedAt: new Date().toISOString(),
        userId: editor.userId || null,
        userName: editor.userName || null,
        userEmail: editor.userEmail || null,
      },
    },
  };
};

const buildEditableResultPatientContext = (result: AssessmentResultV2, rawPayload: unknown): AssessmentPatientContext => {
  const payloadRecord =
    rawPayload && typeof rawPayload === 'object' ? (rawPayload as Record<string, unknown>) : {};
  const patientContextSource =
    payloadRecord.patientContext && typeof payloadRecord.patientContext === 'object'
      ? (payloadRecord.patientContext as Record<string, unknown>)
      : {};

  const birthDate =
    typeof patientContextSource.birthDate === 'string' && patientContextSource.birthDate.trim().length > 0
      ? patientContextSource.birthDate
      : result.patientBirthDateSnapshot || null;
  const ageYears =
    toFiniteNumber(patientContextSource.ageYears) ??
    calculateAgeAtDate(birthDate, result.assessmentDate) ??
    calculateAgeAtDate(result.patientBirthDateSnapshot, result.assessmentDate);

  return {
    birthDate,
    ageYears,
    sex:
      typeof patientContextSource.sex === 'string' && patientContextSource.sex.trim().length > 0
        ? patientContextSource.sex
        : result.patientSexSnapshot || null,
    weightKg: normalizePatientWeightKg(toFiniteNumber(patientContextSource.weightKg) ?? result.patientWeightSnapshot ?? null),
    heightCm: normalizePatientHeightCm(toFiniteNumber(patientContextSource.heightCm) ?? result.patientHeightSnapshot ?? null),
    identifier:
      typeof patientContextSource.identifier === 'string' && patientContextSource.identifier.trim().length > 0
        ? patientContextSource.identifier
        : result.patientMedicalRecordSnapshot || null,
  };
};

const buildEditableResultProfessionalContext = (result: AssessmentResultV2, rawPayload: unknown) => {
  const payloadRecord =
    rawPayload && typeof rawPayload === 'object' ? (rawPayload as Record<string, unknown>) : {};
  const professionalContextSource =
    payloadRecord.professionalContext && typeof payloadRecord.professionalContext === 'object'
      ? (payloadRecord.professionalContext as Record<string, unknown>)
      : {};

  return {
    name:
      typeof professionalContextSource.name === 'string' && professionalContextSource.name.trim().length > 0
        ? professionalContextSource.name
        : result.professionalNameSnapshot || 'Profissional',
    registration:
      typeof professionalContextSource.registration === 'string' && professionalContextSource.registration.trim().length > 0
        ? professionalContextSource.registration
        : result.professionalRegistrationSnapshot || null,
    role:
      typeof professionalContextSource.role === 'string' && professionalContextSource.role.trim().length > 0
        ? professionalContextSource.role
        : result.professionalRoleSnapshot || null,
  };
};

const buildEditableResultAssetsJson = (
  result: AssessmentResultV2,
  editor: {
    userId?: string | null;
    userName?: string | null;
    userEmail?: string | null;
  },
) => ({
  ...(result.assetsJson && typeof result.assetsJson === 'object' ? result.assetsJson : {}),
  payloadEditorAudit: {
    editedAt: new Date().toISOString(),
    userId: editor.userId || null,
    userName: editor.userName || null,
    userEmail: editor.userEmail || null,
  },
});

const buildEditablePeripheralResultContent = (
  result: AssessmentResultV2,
  rawPayload: PeripheralMuscleStrengthResultPayload,
  editor: {
    userId?: string | null;
    userName?: string | null;
    userEmail?: string | null;
  },
) => {
  const patientContext = buildEditableResultPatientContext(result, rawPayload);
  const professionalContext = buildEditableResultProfessionalContext(result, rawPayload);
  const normalized =
    normalizePeripheralMuscleStrengthPayload({
      ...rawPayload,
      patientContext,
      professionalContext,
      finishedAt: rawPayload.finishedAt || result.assessmentDate,
    }) ?? null;

  if (!normalized) {
    throw new Error('Não foi possível normalizar o payload da força muscular periférica.');
  }

  const summary = summarizePeripheralMuscleStrengthResult(normalized);
  const severity = getPeripheralStrengthSeverityLabel(normalized);

  return {
    summaryJson: {
      selectedMusclesCount: summary.selectedMusclesCount,
      completedMuscles: summary.completedMuscles,
      reproducibleMuscles: summary.reproducibleMuscles,
      averagePercentPredicted: summary.averagePercentPredicted,
      lowestPercentPredicted: summary.lowestPercentPredicted,
      lowestMuscleKey: summary.lowestMuscleKey,
      lowestMuscleLabel: summary.lowestMuscleLabel,
      bmi: summary.bmi,
      dominantSide: summary.dominantSide,
      dominantSideLabel: summary.dominantSideLabel,
      severity,
      patientAgeYears: patientContext.ageYears,
      patientSex: patientContext.sex,
      patientWeightKg: patientContext.weightKg,
      patientHeightCm: patientContext.heightCm,
    },
    payloadJson: normalized,
    assetsJson: buildEditableResultAssetsJson(result, editor),
  };
};

const buildEditableOneRMResultContent = (
  result: AssessmentResultV2,
  rawPayload: OneRMResultPayload,
  editor: {
    userId?: string | null;
    userName?: string | null;
    userEmail?: string | null;
  },
) => {
  const patientContext = buildEditableResultPatientContext(result, rawPayload);
  const professionalContext = buildEditableResultProfessionalContext(result, rawPayload);
  const normalized =
    normalizeOneRMPayload({
      ...rawPayload,
      patientContext,
      professionalContext,
      finishedAt: rawPayload?.finishedAt || result.assessmentDate,
      protocolSteps: rawPayload?.protocolSteps?.length ? rawPayload.protocolSteps : Array.from(ONE_RM_PROTOCOL_STEPS),
      referenceNotes: rawPayload?.referenceNotes?.length ? rawPayload.referenceNotes : Array.from(ONE_RM_REFERENCE_NOTES),
    }) ??
    buildOneRMPayload({
      entries: Array.isArray(rawPayload?.entries) ? rawPayload.entries : [],
      notes: rawPayload?.notes,
      patientContext,
      professionalContext,
      finishedAt: rawPayload?.finishedAt || result.assessmentDate,
    });

  if (!normalized) {
    throw new Error('Não foi possível normalizar o payload do 1RM.');
  }

  const summary = summarizeOneRMPayload(normalized);

  return {
    summaryJson: {
      groupCount: summary.groupCount,
      directCount: summary.directCount,
      estimatedCount: summary.estimatedCount,
      bothMethodsCount: summary.bothMethodsCount,
      bestDirect1RMKg: summary.bestDirect1RMKg,
      bestEstimated1RMKg: summary.bestEstimated1RMKg,
      bestOverallKg: summary.bestOverallKg,
      bestGroupLabel: summary.bestGroupLabel,
      severity: getOneRMSeverityLabel(normalized),
      patientAgeYears: patientContext.ageYears,
      patientSex: patientContext.sex,
      patientWeightKg: patientContext.weightKg,
      patientHeightCm: patientContext.heightCm,
    },
    payloadJson: normalized,
    assetsJson: buildEditableResultAssetsJson(result, editor),
  };
};

const isRunnerTestPayloadCompleted = (testKey: AssessmentV2TestKey, payload: any): boolean => {
  if (!payload) return false;
  if (testKey === 'INITIAL_ASSESSMENT') {
    return isInitialAssessmentComplete(payload);
  }
  if (testKey === 'TC6') {
    return isTc6WorkflowCompleted(payload);
  }
  if (testKey === 'TUG') {
    return isTugWorkflowCompleted(payload);
  }
  if (testKey === '4MGS') {
    return isGaitSpeedCompleted(payload);
  }
  if (testKey === '5xSTS' || testKey === 'STS_30S' || testKey === 'STS_1MIN') {
    return isStsWorkflowCompleted(testKey, payload);
  }
  return true;
};

const calculateAgeAtDate = (birthDate?: string | null, referenceDate?: string | null): number | null => {
  if (!birthDate || !referenceDate) return null;

  const birth = new Date(birthDate);
  const reference = new Date(referenceDate);

  if (Number.isNaN(birth.getTime()) || Number.isNaN(reference.getTime())) return null;

  let age = reference.getFullYear() - birth.getFullYear();
  const monthDiff = reference.getMonth() - birth.getMonth();
  const dayDiff = reference.getDate() - birth.getDate();

  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age -= 1;
  }

  return age >= 0 ? age : null;
};

const buildTugPreviewPayload = (result: AssessmentResultV2, currentPatient?: AssessmentV2Patient | null): TugResultPayload => {
  const rawPayload =
    result.payloadJson && typeof result.payloadJson === 'object' ? (result.payloadJson as TugResultPayload) : ({} as TugResultPayload);
  const storedContext = buildEditableResultPatientContext(result, rawPayload);
  const birthDate = storedContext.birthDate ?? currentPatient?.patientDateOfBirth ?? null;
  const patientContext: AssessmentPatientContext = {
    birthDate,
    ageYears: storedContext.ageYears ?? calculateAgeAtDate(birthDate, result.assessmentDate),
    sex: storedContext.sex ?? currentPatient?.patientGender ?? null,
    // O peso é específico da avaliação; não substituímos um dado histórico pelo peso atual do cadastro.
    weightKg: storedContext.weightKg,
    heightCm: storedContext.heightCm ?? normalizePatientHeightCm(currentPatient?.patientHeightCm),
    identifier: storedContext.identifier ?? getPreferredPatientIdentifier(currentPatient),
  };

  return (
    normalizeTugPayload({
      ...rawPayload,
      // Interpretações personalizadas ficam em report.interpretation; o campo direto legado era automático.
      interpretation: undefined,
      patientContext,
    }) ?? { ...rawPayload, patientContext }
  );
};

const buildStsPreviewPayload = (result: AssessmentResultV2, currentPatient?: AssessmentV2Patient | null): Record<string, unknown> => {
  const rawPayload = result.payloadJson && typeof result.payloadJson === 'object' ? (result.payloadJson as Record<string, unknown>) : {};
  const storedContext = buildEditableResultPatientContext(result, rawPayload);
  const birthDate = storedContext.birthDate ?? currentPatient?.patientDateOfBirth ?? null;
  const patientContext: AssessmentPatientContext = {
    birthDate,
    ageYears: storedContext.ageYears ?? calculateAgeAtDate(birthDate, result.assessmentDate),
    sex: storedContext.sex ?? currentPatient?.patientGender ?? null,
    // O peso é aferido para a avaliação; dados históricos não recebem o peso atual do cadastro.
    weightKg: storedContext.weightKg,
    heightCm: storedContext.heightCm ?? normalizePatientHeightCm(currentPatient?.patientHeightCm),
    identifier: storedContext.identifier ?? getPreferredPatientIdentifier(currentPatient),
  };
  const mergedPayload = { ...rawPayload, patientContext };

  if (result.testKey === '5xSTS') {
    return normalizeFiveSTSPayload(mergedPayload) ?? mergedPayload;
  }
  if (result.testKey === 'STS_30S' || result.testKey === 'STS_1MIN') {
    return normalizeStsRepetitionPayload(result.testKey, mergedPayload) ?? mergedPayload;
  }
  return mergedPayload;
};

const getAssessmentResultStatusHistory = (result?: AssessmentResultV2 | null): AssessmentResultLifecycleEntry[] => {
  const history = result?.assetsJson?.statusHistory;
  if (!Array.isArray(history)) return [];

  return history
    .filter((item): item is AssessmentResultLifecycleEntry => !!item && typeof item === 'object')
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
};

const getLatestAssessmentResultStatusEntry = (
  result?: AssessmentResultV2 | null,
  action?: AssessmentResultLifecycleEntry['action'],
): AssessmentResultLifecycleEntry | null => {
  const history = getAssessmentResultStatusHistory(result);
  if (!action) return history[0] ?? null;
  return history.find((entry) => entry.action === action) ?? null;
};

const getResultPrimaryMetric = (result: AssessmentResultV2): string => {
  if (isAnyAssessmentReportResult(result)) {
    const includedCount = typeof result.summaryJson?.includedCount === 'number' ? result.summaryJson.includedCount : null;
    return includedCount != null ? `${includedCount} avaliação${includedCount === 1 ? '' : 'ões'}` : 'Relatório';
  }

  if (result.testKey === 'INITIAL_ASSESSMENT') {
    return summarizeInitialAssessment(result.payloadJson).statusLabel;
  }

  if (result.testKey === 'TC6') {
    const bestDistance =
      typeof result.summaryJson?.bestDistance_m === 'number'
        ? result.summaryJson.bestDistance_m
        : summarizeTc6Result(resolveRunnerPayload('TC6', result)).bestDistance_m;
    return bestDistance != null ? `${bestDistance} m` : '—';
  }

  if (result.testKey === 'ISWT') {
    const bestDistance =
      typeof result.summaryJson?.bestDistanceMeters === 'number'
        ? result.summaryJson.bestDistanceMeters
        : summarizeISWTResult(resolveRunnerPayload('ISWT', result)).bestDistanceMeters;
    return bestDistance > 0 ? `${bestDistance} m` : '—';
  }

  if (result.testKey === 'ESWT') {
    const durationSeconds =
      typeof result.summaryJson?.durationSeconds === 'number'
        ? result.summaryJson.durationSeconds
        : summarizeESWTResult(resolveRunnerPayload('ESWT', result)).durationSeconds;
    return durationSeconds > 0 ? formatDurationSeconds(durationSeconds) : '—';
  }

  if (result.testKey === '6MST') {
    const totalSteps =
      typeof result.summaryJson?.totalSteps === 'number'
        ? result.summaryJson.totalSteps
        : summarizeSixMSTResult(resolveRunnerPayload('6MST', result)).totalSteps;
    return totalSteps > 0 ? `${totalSteps} degraus` : '—';
  }

  if (result.testKey === 'CHESTER') {
    const achievedLevel =
      typeof result.summaryJson?.achievedLevel === 'number'
        ? result.summaryJson.achievedLevel
        : summarizeChesterResult(resolveRunnerPayload('CHESTER', result)).achievedLevel;
    return achievedLevel > 0 ? `Nível ${achievedLevel}/5` : '—';
  }

  if (result.testKey === 'HANDGRIP') {
    const summary = summarizeHandgripResult(normalizeHandgripPayload(resolveRunnerPayload('HANDGRIP', result)));
    const bestOverall =
      typeof result.summaryJson?.bestOverall === 'number'
        ? result.summaryJson.bestOverall
        : summary.bestOverall;
    return bestOverall != null ? `${bestOverall.toFixed(1)} kg` : '—';
  }

  if (result.testKey === 'PERIPHERAL_MUSCLE_STRENGTH') {
    const summary = summarizePeripheralMuscleStrengthResult(
      normalizePeripheralMuscleStrengthPayload(resolveRunnerPayload('PERIPHERAL_MUSCLE_STRENGTH', result)),
    );
    return summary.completedMuscles > 0
      ? `${summary.completedMuscles} grupo${summary.completedMuscles === 1 ? '' : 's'} muscular${summary.completedMuscles === 1 ? '' : 'es'} avaliado${summary.completedMuscles === 1 ? '' : 's'}`
      : '—';
  }

  if (result.testKey === 'ONE_RM') {
    const summary = summarizeOneRMPayload(resolveRunnerPayload('ONE_RM', result));
    const groupCount =
      typeof result.summaryJson?.groupCount === 'number'
        ? result.summaryJson.groupCount
        : summary.groupCount;
    return groupCount > 0 ? `${groupCount} grupo${groupCount === 1 ? '' : 's'}` : '—';
  }

  if (result.testKey === 'PIMAX') {
    const summary = summarizePimaxPayload(resolveRunnerPayload('PIMAX', result));
    return summary.max != null ? `${summary.max.toFixed(1)} cmH2O` : '—';
  }

  if (result.testKey === 'PEMAX') {
    const summary = summarizePemaxPayload(resolveRunnerPayload('PEMAX', result));
    return summary.max != null ? `${summary.max.toFixed(1)} cmH2O` : '—';
  }

  if (result.testKey === 'S_INDEX') {
    const summary = summarizeSIndexPayload(resolveRunnerPayload('S_INDEX', result));
    return summary.maxPressure != null ? `${summary.maxPressure.toFixed(1)} cmH2O` : '—';
  }

  if (result.testKey === 'RESP_ENDURANCE') {
    const summary = summarizeRespiratoryEndurancePayload(resolveRunnerPayload('RESP_ENDURANCE', result));
    return summary.tlimFormatted ?? '—';
  }

  if (result.testKey === 'PEAK_COUGH_FLOW') {
    const summary = summarizePeakCoughFlowPayload(result.payloadJson);
    return summary.bestFlow != null ? `${formatPeakFlowValue(summary.bestFlow)} L/min` : 'Sem medida aceita';
  }
  if (result.testKey === 'CV_POSTURAL') {
    const summary = summarizePosturalVitalCapacityPayload(result.payloadJson);
    return summary.reductionPercent != null ? `${formatPeakFlowValue(Math.abs(summary.reductionPercent))}% de ${summary.reductionPercent >= 0 ? 'redução' : 'aumento'} postural` : 'Comparação incompleta';
  }
  if (result.testKey === 'SNIP') {
    const summary = summarizeSnipPayload(result.payloadJson);
    return summary.max != null ? `${formatPeakFlowValue(summary.max)} cmH2O` : 'Sem medida aceita';
  }

  if (result.testKey === 'PEAK_FLOW') {
    const summary = summarizePeakFlowPayload(resolveRunnerPayload('PEAK_FLOW', result));
    const bestPreFlow =
      typeof result.summaryJson?.bestPreFlow === 'number'
        ? result.summaryJson.bestPreFlow
        : summary.bestPreFlow;
    return bestPreFlow != null ? `${formatPeakFlowValue(bestPreFlow)} L/min` : '—';
  }

  if (result.testKey === 'TUG') {
    const summary = summarizeTugResult(resolveRunnerPayload('TUG', result));
    const timeSeconds =
      typeof result.summaryJson?.timeSeconds === 'number'
        ? result.summaryJson.timeSeconds
        : summary.bestTimeSeconds;
    return timeSeconds != null ? `${formatTugSeconds(timeSeconds)} s` : 'Não concluiu';
  }

  if (result.testKey === '4MGS') {
    const summary = summarizeGaitSpeedResult(resolveRunnerPayload('4MGS', result));
    const speedMs =
      typeof result.summaryJson?.speedMs === 'number'
        ? result.summaryJson.speedMs
        : summary.bestSpeedMs;
    return speedMs != null ? `${formatGaitSpeed(speedMs)} m/s` : 'Não concluiu';
  }

  if (result.testKey === '5xSTS' || result.testKey === 'STS_30S' || result.testKey === 'STS_1MIN') {
    const summary = summarizeStsResult(result.testKey as StsTestKey, resolveRunnerPayload(result.testKey as AssessmentV2TestKey, result));
    if (result.testKey === '5xSTS') {
      const best = typeof result.summaryJson?.best === 'number' ? result.summaryJson.best : summary.best;
      return best != null ? `${best.toFixed(2)} s` : '—';
    }
    const best = typeof result.summaryJson?.best === 'number' ? result.summaryJson.best : summary.best;
    return best != null ? `${Math.round(best)} rep` : '—';
  }

  if (result.testKey === 'AQLQ') {
    const total = typeof result.summaryJson?.total === 'number' ? result.summaryJson.total : null;
    return total != null ? `${total.toFixed(2)}/7` : '—';
  }

  if (result.testKey === 'ACT') {
    const total = typeof result.summaryJson?.total === 'number' ? result.summaryJson.total : null;
    return total != null ? `${total}/25` : '—';
  }

  if (result.testKey === 'SGRQ') {
    const total = typeof result.summaryJson?.total === 'number' ? result.summaryJson.total : null;
    return total != null ? `${total.toFixed(1)}/100` : '—';
  }

  if (result.testKey === 'SAQ') {
    const overallScore =
      typeof result.summaryJson?.overallScore === 'number'
        ? result.summaryJson.overallScore
        : getSAQOverallScore(result.payloadJson?.domainScores);
    return overallScore != null ? `${overallScore.toFixed(1)}/100` : '—';
  }

  if (result.testKey === 'PFSDQ-M') {
    const total = typeof result.summaryJson?.total === 'number' ? result.summaryJson.total : null;
    return total != null ? `${total}/300` : '—';
  }

  if (result.testKey === 'MLHFQ') {
    const total = typeof result.summaryJson?.total === 'number' ? result.summaryJson.total : null;
    const maxScore = typeof result.summaryJson?.maxScore === 'number' ? result.summaryJson.maxScore : 105;
    return total != null ? `${total}/${maxScore}` : '—';
  }

  if (result.testKey === 'LCADL') {
    const totalScore = typeof result.summaryJson?.totalScore === 'number' ? result.summaryJson.totalScore : null;
    const maxScore = typeof result.summaryJson?.maxScore === 'number' ? result.summaryJson.maxScore : null;
    return totalScore != null && maxScore != null ? `${totalScore}/${maxScore}` : '—';
  }

  if (result.testKey === 'HADS') {
    const anxietyScore = typeof result.summaryJson?.anxietyScore === 'number' ? result.summaryJson.anxietyScore : null;
    const depressionScore = typeof result.summaryJson?.depressionScore === 'number' ? result.summaryJson.depressionScore : null;
    return `A ${anxietyScore ?? '—'} • D ${depressionScore ?? '—'}`;
  }

  if (result.testKey === 'PSQI') {
    const total = typeof result.summaryJson?.total === 'number' ? result.summaryJson.total : null;
    return total != null ? `${total}/21` : '—';
  }

  if (result.testKey === 'mMRC') {
    const grade = typeof result.summaryJson?.grade === 'number' ? result.summaryJson.grade : null;
    return grade != null ? `Grau ${grade}` : '—';
  }

  const total = typeof result.summaryJson?.total === 'number' ? result.summaryJson.total : null;
  return total != null ? `${total} pontos` : '—';
};

const getQuestionnaireNumericResult = (result: AssessmentResultV2): number | null => {
  const payload = result.payloadJson ?? {};
  const summary = result.summaryJson ?? {};
  const readNumber = (...values: unknown[]): number | null => {
    for (const value of values) {
      if (typeof value === 'number' && Number.isFinite(value)) return value;
    }
    return null;
  };

  if (result.testKey === 'mMRC') return readNumber(payload.grade, summary.grade);
  if (result.testKey === 'LCADL') return readNumber(payload.totalScore, summary.totalScore);
  if (result.testKey === 'SAQ') {
    return readNumber(summary.overallScore, getSAQOverallScore(payload.domainScores));
  }
  return readNumber(payload.total, summary.total);
};

const formatQuestionnaireDifference = (current: AssessmentResultV2, previous: AssessmentResultV2): string => {
  if (current.testKey === 'HADS') {
    const currentAnxiety = current.payloadJson?.anxietyScore ?? current.summaryJson?.anxietyScore;
    const previousAnxiety = previous.payloadJson?.anxietyScore ?? previous.summaryJson?.anxietyScore;
    const currentDepression = current.payloadJson?.depressionScore ?? current.summaryJson?.depressionScore;
    const previousDepression = previous.payloadJson?.depressionScore ?? previous.summaryJson?.depressionScore;
    if (
      typeof currentAnxiety !== 'number' ||
      typeof previousAnxiety !== 'number' ||
      typeof currentDepression !== 'number' ||
      typeof previousDepression !== 'number'
    ) {
      return '—';
    }
    const formatSigned = (value: number) => `${value > 0 ? '+' : ''}${value.toLocaleString('pt-BR')}`;
    return `Ansiedade ${formatSigned(currentAnxiety - previousAnxiety)} • Depressão ${formatSigned(currentDepression - previousDepression)}`;
  }

  const currentValue = getQuestionnaireNumericResult(current);
  const previousValue = getQuestionnaireNumericResult(previous);
  if (currentValue == null || previousValue == null) return '—';
  const difference = currentValue - previousValue;
  const decimals = current.testKey === 'AQLQ' ? 2 : current.testKey === 'SGRQ' || current.testKey === 'SAQ' ? 1 : 0;
  const formatted = Math.abs(difference).toLocaleString('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${difference > 0 ? '+' : difference < 0 ? '−' : ''}${formatted} ${Math.abs(difference) === 1 ? 'ponto' : 'pontos'}`;
};

const getResultSeverityLabel = (result: AssessmentResultV2): string => {
  if (isAnyAssessmentReportResult(result)) {
    return typeof result.summaryJson?.severity === 'string' ? result.summaryJson.severity : 'Relatório finalizado';
  }

  if (result.testKey === 'INITIAL_ASSESSMENT') {
    return summarizeInitialAssessment(result.payloadJson).classification;
  }

  if (result.testKey === 'TC6') {
    const percentPredicted =
      typeof result.summaryJson?.percentPredicted === 'number'
        ? result.summaryJson.percentPredicted
        : summarizeTc6Result(resolveRunnerPayload('TC6', result)).percentPredicted;
    return getTC6SeverityLabel(percentPredicted);
  }

  if (result.testKey === 'ISWT') {
    return getISWTSeverityLabel(resolveRunnerPayload('ISWT', result));
  }

  if (result.testKey === 'ESWT') {
    return getESWTSeverityLabel(resolveRunnerPayload('ESWT', result));
  }

  if (result.testKey === '6MST') {
    return get6MSTSeverityLabel(resolveRunnerPayload('6MST', result));
  }

  if (result.testKey === 'CHESTER') {
    return getChesterSeverityLabel(resolveRunnerPayload('CHESTER', result));
  }

  if (result.testKey === 'HANDGRIP') {
    const summary = summarizeHandgripResult(normalizeHandgripPayload(resolveRunnerPayload('HANDGRIP', result)));
    const percentile =
      typeof result.summaryJson?.absolutePercentile === 'number'
        ? result.summaryJson.absolutePercentile
        : summary.absolutePercentile;
    return getHandgripSeverityLabel(percentile);
  }

  if (result.testKey === 'PERIPHERAL_MUSCLE_STRENGTH') {
    return getPeripheralStrengthSeverityLabel(resolveRunnerPayload('PERIPHERAL_MUSCLE_STRENGTH', result));
  }

  if (result.testKey === 'ONE_RM') {
    return getOneRMSeverityLabel(resolveRunnerPayload('ONE_RM', result));
  }

  if (result.testKey === 'PIMAX') {
    return getPimaxSeverityLabel(resolveRunnerPayload('PIMAX', result));
  }

  if (result.testKey === 'PEMAX') {
    return getPemaxSeverityLabel(resolveRunnerPayload('PEMAX', result));
  }

  if (result.testKey === 'S_INDEX') {
    return getSIndexSeverityLabel(resolveRunnerPayload('S_INDEX', result));
  }

  if (result.testKey === 'RESP_ENDURANCE') {
    return getRespiratoryEnduranceSeverityLabel(resolveRunnerPayload('RESP_ENDURANCE', result));
  }

  if (result.testKey === 'PEAK_FLOW') {
    return summarizePeakFlowPayload(resolveRunnerPayload('PEAK_FLOW', result)).severity;
  }
  if (result.testKey === 'PEAK_COUGH_FLOW') return summarizePeakCoughFlowPayload(result.payloadJson).classification;
  if (result.testKey === 'CV_POSTURAL') return getPosturalVitalCapacitySeverityLabel(result.payloadJson);
  if (result.testKey === 'SNIP') return getSnipSeverityLabel(result.payloadJson);

  if (result.testKey === 'TUG') {
    const summary = summarizeTugResult(resolveRunnerPayload('TUG', result));
    return summary.severity;
  }

  if (result.testKey === '4MGS') {
    const summary = summarizeGaitSpeedResult(resolveRunnerPayload('4MGS', result));
    return summary.severity;
  }

  if (result.testKey === '5xSTS' || result.testKey === 'STS_30S' || result.testKey === 'STS_1MIN') {
    return typeof result.summaryJson?.classification === 'string'
      ? result.summaryJson.classification
      : getSTSSeverityLabel(result.testKey as StsTestKey, resolveRunnerPayload(result.testKey as AssessmentV2TestKey, result));
  }

  if (result.testKey === 'AQLQ') {
    const total = typeof result.summaryJson?.total === 'number' ? result.summaryJson.total : null;
    return getAQLQSeverityLabel(total);
  }

  if (result.testKey === 'ACT') {
    const total = typeof result.summaryJson?.total === 'number' ? result.summaryJson.total : null;
    return getACTSeverityLabel(total);
  }

  if (result.testKey === 'SGRQ') {
    const total = typeof result.summaryJson?.total === 'number' ? result.summaryJson.total : null;
    return getSGRQSeverityLabel(total);
  }

  if (result.testKey === 'SAQ') {
    const overallScore =
      typeof result.summaryJson?.overallScore === 'number'
        ? result.summaryJson.overallScore
        : getSAQOverallScore(result.payloadJson?.domainScores);
    return getSAQSeverityLabel(overallScore);
  }

  if (result.testKey === 'PFSDQ-M') {
    const total = typeof result.summaryJson?.total === 'number' ? result.summaryJson.total : null;
    return getPFSDQSeverityLabel(total);
  }

  if (result.testKey === 'MLHFQ') {
    const total = typeof result.summaryJson?.total === 'number' ? result.summaryJson.total : null;
    return getMLHFQSeverityLabel(total);
  }

  if (result.testKey === 'LCADL') {
    const percentage = typeof result.summaryJson?.percentage === 'number' ? result.summaryJson.percentage : null;
    return getLCADLSeverityLabel(percentage);
  }

  if (result.testKey === 'HADS') {
    const anxietyScore = typeof result.summaryJson?.anxietyScore === 'number' ? result.summaryJson.anxietyScore : null;
    const depressionScore = typeof result.summaryJson?.depressionScore === 'number' ? result.summaryJson.depressionScore : null;
    return getHADSOverallSeverityLabel(anxietyScore, depressionScore);
  }

  if (result.testKey === 'PSQI') {
    const total = typeof result.summaryJson?.total === 'number' ? result.summaryJson.total : null;
    return getPSQISeverityLabel(total);
  }

  if (result.testKey === 'mMRC') {
    const grade = typeof result.summaryJson?.grade === 'number' ? result.summaryJson.grade : null;
    return getMMRCSeverityLabel(grade);
  }

  const total = typeof result.summaryJson?.total === 'number' ? result.summaryJson.total : null;
  return getCATSeverityLabel(total);
};

const getResultSeverityTone = (result: AssessmentResultV2): string => {
  if (result.status === 'cancelled') return 'bg-red-100 text-red-700';
  if (isAnyAssessmentReportResult(result)) return 'bg-violet-100 text-violet-700';

  if (result.testKey === 'INITIAL_ASSESSMENT') {
    return 'bg-teal-100 text-teal-800';
  }

  if (result.testKey === 'TC6') {
    const percentPredicted =
      typeof result.summaryJson?.percentPredicted === 'number'
        ? result.summaryJson.percentPredicted
        : summarizeTc6Result(resolveRunnerPayload('TC6', result)).percentPredicted;
    return getTC6SeverityTone(percentPredicted);
  }

  if (result.testKey === 'ISWT') {
    return getISWTSeverityTone(resolveRunnerPayload('ISWT', result));
  }

  if (result.testKey === 'ESWT') {
    return getESWTSeverityTone(resolveRunnerPayload('ESWT', result));
  }

  if (result.testKey === '6MST') {
    return get6MSTSeverityTone(resolveRunnerPayload('6MST', result));
  }

  if (result.testKey === 'CHESTER') {
    return getChesterSeverityTone(resolveRunnerPayload('CHESTER', result));
  }

  if (result.testKey === 'HANDGRIP') {
    const summary = summarizeHandgripResult(normalizeHandgripPayload(resolveRunnerPayload('HANDGRIP', result)));
    const percentile =
      typeof result.summaryJson?.absolutePercentile === 'number'
        ? result.summaryJson.absolutePercentile
        : summary.absolutePercentile;
    return getHandgripSeverityTone(percentile);
  }

  if (result.testKey === 'PERIPHERAL_MUSCLE_STRENGTH') {
    return getPeripheralStrengthSeverityTone(resolveRunnerPayload('PERIPHERAL_MUSCLE_STRENGTH', result));
  }

  if (result.testKey === 'ONE_RM') {
    return getOneRMSeverityTone(resolveRunnerPayload('ONE_RM', result));
  }

  if (result.testKey === 'PIMAX') {
    return getPimaxSeverityTone(resolveRunnerPayload('PIMAX', result));
  }

  if (result.testKey === 'PEMAX') {
    return getPemaxSeverityTone(resolveRunnerPayload('PEMAX', result));
  }

  if (result.testKey === 'S_INDEX') {
    return getSIndexSeverityTone(resolveRunnerPayload('S_INDEX', result));
  }

  if (result.testKey === 'RESP_ENDURANCE') {
    return getRespiratoryEnduranceSeverityTone(resolveRunnerPayload('RESP_ENDURANCE', result));
  }

  if (result.testKey === 'PEAK_FLOW') {
    return getPeakFlowSeverityTone(resolveRunnerPayload('PEAK_FLOW', result));
  }
  if (result.testKey === 'PEAK_COUGH_FLOW') {
    const severity = summarizePeakCoughFlowPayload(result.payloadJson).severity;
    return severity === 'positive' ? 'bg-emerald-100 text-emerald-700' : severity === 'warning' ? 'bg-amber-100 text-amber-800' : severity === 'urgent' ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-700';
  }
  if (result.testKey === 'CV_POSTURAL') return getPosturalVitalCapacitySeverityTone(result.payloadJson);
  if (result.testKey === 'SNIP') return getSnipSeverityTone(result.payloadJson);

  if (result.testKey === 'TUG') {
    const summary = summarizeTugResult(resolveRunnerPayload('TUG', result));
    const timeSeconds =
      typeof result.summaryJson?.timeSeconds === 'number'
        ? result.summaryJson.timeSeconds
        : summary.bestTimeSeconds;
    const completed = result.summaryJson?.completed !== false && summary.completed;
    const physicalAssistance = Boolean(result.summaryJson?.physicalAssistance ?? summary.physicalAssistance);
    return getTUGSeverityTone(timeSeconds, completed, physicalAssistance, summary.referenceComparison);
  }

  if (result.testKey === '4MGS') {
    const summary = summarizeGaitSpeedResult(resolveRunnerPayload('4MGS', result));
    const speedMs =
      typeof result.summaryJson?.speedMs === 'number'
        ? result.summaryJson.speedMs
        : summary.bestSpeedMs;
    const completed = result.summaryJson?.completed !== false && summary.completed;
    const physicalAssistance = Boolean(result.summaryJson?.physicalAssistance ?? summary.physicalAssistance);
    return getGaitSpeedSeverityTone(speedMs, completed, physicalAssistance, summary.referenceComparison);
  }

  if (result.testKey === '5xSTS' || result.testKey === 'STS_30S' || result.testKey === 'STS_1MIN') {
    return getSTSSeverityTone(result.testKey as StsTestKey, resolveRunnerPayload(result.testKey as AssessmentV2TestKey, result));
  }

  if (result.testKey === 'AQLQ') {
    const total = typeof result.summaryJson?.total === 'number' ? result.summaryJson.total : null;
    return getAQLQSeverityTone(total);
  }

  if (result.testKey === 'ACT') {
    const total = typeof result.summaryJson?.total === 'number' ? result.summaryJson.total : null;
    return getACTSeverityTone(total);
  }

  if (result.testKey === 'SGRQ') {
    const total = typeof result.summaryJson?.total === 'number' ? result.summaryJson.total : null;
    return getSGRQSeverityTone(total);
  }

  if (result.testKey === 'SAQ') {
    const overallScore =
      typeof result.summaryJson?.overallScore === 'number'
        ? result.summaryJson.overallScore
        : getSAQOverallScore(result.payloadJson?.domainScores);
    return getSAQSeverityTone(overallScore);
  }

  if (result.testKey === 'PFSDQ-M') {
    const total = typeof result.summaryJson?.total === 'number' ? result.summaryJson.total : null;
    return getPFSDQSeverityTone(total);
  }

  if (result.testKey === 'MLHFQ') {
    const total = typeof result.summaryJson?.total === 'number' ? result.summaryJson.total : null;
    return getMLHFQSeverityTone(total);
  }

  if (result.testKey === 'LCADL') {
    const percentage = typeof result.summaryJson?.percentage === 'number' ? result.summaryJson.percentage : null;
    return getLCADLSeverityTone(percentage);
  }

  if (result.testKey === 'HADS') {
    const anxietyScore = typeof result.summaryJson?.anxietyScore === 'number' ? result.summaryJson.anxietyScore : null;
    const depressionScore = typeof result.summaryJson?.depressionScore === 'number' ? result.summaryJson.depressionScore : null;
    return getHADSOverallSeverityTone(anxietyScore, depressionScore);
  }

  if (result.testKey === 'PSQI') {
    const total = typeof result.summaryJson?.total === 'number' ? result.summaryJson.total : null;
    return getPSQISeverityTone(total);
  }

  if (result.testKey === 'mMRC') {
    const grade = typeof result.summaryJson?.grade === 'number' ? result.summaryJson.grade : null;
    return getMMRCSeverityTone(grade);
  }

  const total = typeof result.summaryJson?.total === 'number' ? result.summaryJson.total : null;
  return getCATSeverityTone(total);
};

const NewAssessmentsV2Page = () => {
  const { user, selectedClient } = useAuth();
  const location = useLocation();
  const clientId = selectedClient?.clientId ?? null;
  const requestedPatientId = useMemo(() => {
    const patientId = new URLSearchParams(location.search).get('patientId')?.trim();
    return patientId || null;
  }, [location.search]);
  const activeClientIdRef = useRef<string | null>(clientId);
  const activeResultsPatientIdRef = useRef<string | null>(requestedPatientId);
  const activeDashboardRequestRef = useRef(0);
  const resultsSearchDebounceRef = useRef<number | null>(null);
  activeClientIdRef.current = clientId;
  activeResultsPatientIdRef.current = requestedPatientId;

  const [loading, setLoading] = useState(true);
  const [patientsLoading, setPatientsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const draftStepSaveInFlightRef = useRef(false);
  const [resultActionLoading, setResultActionLoading] = useState(false);
  const [signingResultId, setSigningResultId] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<V2InfrastructureSnapshot>(initialSnapshot);
  const [patients, setPatients] = useState<AssessmentV2Patient[]>([]);
  const [results, setResults] = useState<AssessmentResultV2[]>([]);
  const [assessmentTestUsage, setAssessmentTestUsage] = useState<AssessmentTestUsage[]>([]);
  const [draftSessions, setDraftSessions] = useState<DraftSessionSummary[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'cancelled' | 'all'>('active');
  const [resultsPatientFilterId, setResultsPatientFilterId] = useState<string>('all');
  const [resultsProfessionalFilter, setResultsProfessionalFilter] = useState<string>('all');
  const [resultsViewMode, setResultsViewMode] = useState<AssessmentResultsViewMode>('list');
  const [resultsPage, setResultsPage] = useState(1);
  const [resultsPageSize, setResultsPageSize] = useState<AssessmentResultsPageSize>(50);
  const [resultsTotal, setResultsTotal] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPeakFlowDiary, setShowPeakFlowDiary] = useState(false);
  const [diaryPatientId, setDiaryPatientId] = useState('');
  const [handledCreateRequestKey, setHandledCreateRequestKey] = useState('');
  const [initialAssessmentSources, setInitialAssessmentSources] = useState<AssessmentResultV2[]>([]);
  const [initialAssessmentSourcesLoading, setInitialAssessmentSourcesLoading] = useState(false);
  const [showRunnerModal, setShowRunnerModal] = useState(false);
  const [form, setForm] = useState<AssessmentV2FormState>(initialForm());
  const [assessmentPickerSearch, setAssessmentPickerSearch] = useState('');
  const [openAssessmentGroupIds, setOpenAssessmentGroupIds] = useState<string[]>([]);
  const [openAssessmentSectionIds, setOpenAssessmentSectionIds] = useState<string[]>([]);
  const [publicInviteMonitor, setPublicInviteMonitor] = useState<PublicInviteMonitorState | null>(null);
  const [runnerSession, setRunnerSession] = useState<AssessmentSessionV2 | null>(null);
  const [runnerResults, setRunnerResults] = useState<Partial<Record<AssessmentV2TestKey, any>>>({});
  const [runnerSavedResults, setRunnerSavedResults] = useState<Partial<Record<AssessmentV2TestKey, AssessmentResultV2>>>({});
  const [runnerActiveTest, setRunnerActiveTest] = useState<AssessmentV2TestKey | null>(null);
  const [selectedResult, setSelectedResult] = useState<AssessmentResultV2 | null>(null);
  const [selectedResultHistory, setSelectedResultHistory] = useState<AssessmentResultV2[]>([]);
  const [selectedTc6ComparisonId, setSelectedTc6ComparisonId] = useState<string>('__none__');
  const [loadingResultHistory, setLoadingResultHistory] = useState(false);
  const [tc6PdfExportState, setTc6PdfExportState] = useState<{
    action: 'download' | 'print';
    result: AssessmentResultV2;
    previousResults: AssessmentResultV2[];
  } | null>(null);
  const [statusModalState, setStatusModalState] = useState<{
    mode: 'invalidate' | 'restore';
    result: AssessmentResultV2;
  } | null>(null);
  const [showTc6PayloadEditor, setShowTc6PayloadEditor] = useState(false);
  const [showResultPayloadEditor, setShowResultPayloadEditor] = useState(false);
  const [resultInterpretationDraft, setResultInterpretationDraft] = useState('');

  useEffect(() => {
    setResultsPatientFilterId(requestedPatientId ?? 'all');
    if (!requestedPatientId) return;

    const params = new URLSearchParams(location.search);

    if (!['new', 'from-anamnesis'].includes(params.get('action') || '') || !clientId || patientsLoading) return;

    const requestKey = `${clientId}:${requestedPatientId}:${location.search}`;
    if (handledCreateRequestKey === requestKey) return;

    setHandledCreateRequestKey(requestKey);
    const patient = patients.find((item) => item.id === requestedPatientId);
    if (!patient) {
      toast.error('O paciente informado não está disponível para uma nova avaliação.');
      return;
    }

    setForm({ ...initialForm(), patientId: requestedPatientId,
      ...(params.get('action') === 'from-anamnesis' ? { selectedTests: ['INITIAL_ASSESSMENT'] as AssessmentV2TestKey[] } : {}),
    });
    setAssessmentPickerSearch('');
    setOpenAssessmentGroupIds(ASSESSMENT_V2_MODAL_GROUPS.map(group => group.id));
    setOpenAssessmentSectionIds(ASSESSMENT_V2_MODAL_GROUPS.flatMap(group => group.sections.map(section => section.id)));
    if (params.get('action') === 'from-anamnesis' && params.get('anamnesisId')) {
      const requestedClient = clientId;
      void (async () => {
        try {
          const linked = await AnamnesisAssessmentLinkService.ensure(params.get('anamnesisId')!, requestedClient, requestedPatientId);
          if (activeClientIdRef.current !== requestedClient) return;
          if (!linked) throw new Error('A anamnese não está ativa ou não tem conteúdo disponível.');
          if (linked.status === 'cancelled') {
            toast.info('A avaliação vinculada foi invalidada. Consulte o registro antes de iniciar outra avaliação.');
            setSelectedResult(linked);
            return;
          }
          if (linked.status === 'completed') {
            setSelectedResult(linked);
          } else {
            const [session, linkedResults] = await Promise.all([
              AssessmentSessionsV2Service.getById(linked.sessionId, requestedClient),
              AssessmentResultsV2Service.listBySessionId(requestedClient, linked.sessionId),
            ]);
            if (activeClientIdRef.current !== requestedClient) return;
            await openDraftSession({ session, results: linkedResults, patientName: patient.personalData?.name || patient.patientFullName || 'Paciente' });
          }
        } catch (error) {
          if (activeClientIdRef.current !== requestedClient) return;
          toast.error(error instanceof Error ? error.message : 'Não foi possível abrir a avaliação vinculada.');
          setShowCreateModal(true);
        }
      })();
    } else {
      setShowCreateModal(true);
    }
  }, [clientId, handledCreateRequestKey, location.search, patients, patientsLoading, requestedPatientId]);

  const returnToEvolutionAfterCompletion = (sessionId: string, patientId: string) => {
    const params = new URLSearchParams(location.search);
    if (params.get('source') !== 'evolution' || params.get('action') !== 'new') return;

    if (!window.opener || window.opener.closed) {
      toast.info('Avaliação concluída. Retorne à aba da evolução para continuar.');
      return;
    }

    window.opener.postMessage(
      {
        type: 'revahub:assessment-completed',
        clientId,
        patientId,
        sessionId,
      },
      window.location.origin,
    );
    window.opener.focus();
    window.setTimeout(() => window.close(), 80);
  };
  const [tc6PayloadSaving, setTc6PayloadSaving] = useState(false);
  const [resultPayloadSaving, setResultPayloadSaving] = useState(false);
  const [showAssessmentReportModal, setShowAssessmentReportModal] = useState(false);
  const [assessmentReportForm, setAssessmentReportForm] = useState<AssessmentReportEditorForm>(
    () => createInitialAssessmentReportForm(),
  );
  const [assessmentReportCandidates, setAssessmentReportCandidates] = useState<AssessmentResultV2[]>([]);
  const [assessmentReportCandidatesLoading, setAssessmentReportCandidatesLoading] = useState(false);
  const [assessmentReportSaving, setAssessmentReportSaving] = useState(false);
  const [assessmentReportEditingResultId, setAssessmentReportEditingResultId] = useState<string | null>(null);
  const [assessmentReportEditingRevision, setAssessmentReportEditingRevision] =
    useState<AssessmentResultV2Revision | null>(null);
  const [assessmentReportGeneratedAt, setAssessmentReportGeneratedAt] = useState<string | null>(null);
  const assessmentReportCandidateRequestRef = useRef(0);
  const assessmentReportSaveInFlightRef = useRef(false);

  const selectedPatient = useMemo(
    () => patients.find((patient) => patient.id === form.patientId),
    [patients, form.patientId],
  );

  const initialAssessmentPatientId = selectedResult?.testKey === 'INITIAL_ASSESSMENT'
    ? selectedResult.patientId : selectedPatient?.id;
  useEffect(() => {
    let active = true;
    setInitialAssessmentSources([]);
    setInitialAssessmentSourcesLoading(false);
    if (!clientId || !initialAssessmentPatientId) return;
    setInitialAssessmentSourcesLoading(true);
    void (async () => {
      try {
        const first = await AssessmentResultsV2Service.listCompletedForPatient(clientId, initialAssessmentPatientId, { page: 1, pageSize: 100 });
        const all = [...first.items];
        for (let page = 2; page <= Math.ceil(first.total / 100); page += 1) {
          if (!active) return;
          const next = await AssessmentResultsV2Service.listCompletedForPatient(clientId, initialAssessmentPatientId, { page, pageSize: 100 });
          all.push(...next.items);
        }
        if (active) setInitialAssessmentSources(all);
      } catch (error) {
        if (active) toast.error('Não foi possível consultar os testes disponíveis para a ficha clínica.');
        console.error('Erro ao consultar fontes da avaliação integrada:', error);
      } finally {
        if (active) setInitialAssessmentSourcesLoading(false);
      }
    })();
    return () => { active = false; };
  }, [clientId, initialAssessmentPatientId, showResultPayloadEditor, showRunnerModal]);

  const selectedAssessmentReportPatient = useMemo(
    () => patients.find((patient) => patient.id === assessmentReportForm.patient.patientId),
    [assessmentReportForm.patient.patientId, patients],
  );
  const canCreateTechnicalAssessmentReport =
    assessmentReportForm.authorship.professional.role === 'fisioterapeuta'
    && Boolean(assessmentReportForm.authorship.professional.registration);

  const patientLookup = useMemo(() => new Map(patients.map((patient) => [patient.id, patient])), [patients]);
  const diaryPatient = patientLookup.get(diaryPatientId);
  const exportPeakFlowDiary = async (data: PeakFlowDiaryExportData) => {
    if (!snapshot.branding || !diaryPatient) {
      toast.error('Aguarde os dados da clínica e do paciente para exportar.');
      return;
    }
    try {
      const pdf = await AssessmentPDFV2Service.generatePeakFlowDiaryPDF({
        data,
        branding: snapshot.branding,
        patientName: diaryPatient.personalData?.name || diaryPatient.patientFullName || 'Paciente',
        patientBirthDate: diaryPatient.patientDateOfBirth || null,
      });
      pdf.save(`Diario_Peak_Flow_${data.program.startsOn}_${data.program.endsOn}.pdf`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Não foi possível exportar o diário.');
    }
  };
  const filteredPatientLabel = useMemo(() => {
    if (resultsPatientFilterId === 'all') return null;
    const patient = patientLookup.get(resultsPatientFilterId);
    return patient?.personalData?.name || patient?.patientFullName || null;
  }, [patientLookup, resultsPatientFilterId]);
  const resultsProfessionalOptions = useMemo(
    () =>
      Array.from(
        new Set(
          results
            .map((result) => result.professionalNameSnapshot?.trim())
            .filter((name): name is string => Boolean(name)),
        ),
      ).sort((left, right) => left.localeCompare(right, 'pt-BR')),
    [results],
  );
  const filteredProfessionalLabel = useMemo(() => {
    if (resultsProfessionalFilter === 'all') return null;
    return resultsProfessionalOptions.find((name) => name === resultsProfessionalFilter) || null;
  }, [resultsProfessionalFilter, resultsProfessionalOptions]);
  const resultsPageCount = Math.max(1, Math.ceil(resultsTotal / resultsPageSize));
  const resultsRangeStart = resultsTotal === 0 ? 0 : (resultsPage - 1) * resultsPageSize + 1;
  const resultsRangeEnd = Math.min(resultsPage * resultsPageSize, resultsTotal);
  const tc6ComparisonHistory = useMemo(
    () =>
      selectedResult?.testKey === 'TC6'
        ? selectedResultHistory.map((historyItem) => ({
            id: historyItem.resultId,
            label: `${formatTc6ComparisonDate(historyItem.assessmentDate)} • ${getResultPrimaryMetric(historyItem)}`,
            assessmentDate: historyItem.assessmentDate,
            payload: resolveRunnerPayload('TC6', historyItem) as Tc6ResultPayload,
          }))
        : [],
    [selectedResult?.testKey, selectedResultHistory],
  );
  const structuredComparisonHistory = useMemo<AssessmentHistoryComparisonEntry[]>(() => {
    if (!selectedResult || !supportsAssessmentHistoryComparison(selectedResult.testKey)) return [];

    return selectedResultHistory
      .filter((historyItem) => historyItem.status === 'completed')
      .slice(0, 5)
      .map((historyItem) => ({
        id: historyItem.resultId,
        assessmentDate: historyItem.assessmentDate,
        payload:
          historyItem.testKey === '5xSTS' || historyItem.testKey === 'STS_30S' || historyItem.testKey === 'STS_1MIN'
            ? buildStsPreviewPayload(historyItem, patientLookup.get(historyItem.patientId))
            : resolveRunnerPayload(historyItem.testKey as AssessmentV2TestKey, historyItem),
      }));
  }, [patientLookup, selectedResult, selectedResultHistory]);

  const selectedResultSignature = useMemo(
    () => (selectedResult ? getAssessmentSignatureMetadata(selectedResult.assetsJson) : null),
    [selectedResult],
  );
  const selectedTc6Comparison = useMemo(
    () => tc6ComparisonHistory.find((option) => option.id === selectedTc6ComparisonId) ?? null,
    [selectedTc6ComparisonId, tc6ComparisonHistory],
  );
  const tc6PdfComparisonPreviewLabel = useMemo(() => {
    if (!tc6PdfExportState || tc6PdfExportState.result.testKey !== 'TC6') return null;

    if (selectedResult?.resultId === tc6PdfExportState.result.resultId && selectedTc6Comparison) {
      return selectedTc6Comparison.label;
    }

    const fallback = tc6PdfExportState.previousResults[0];
    return fallback ? `${formatTc6ComparisonDate(fallback.assessmentDate)} • ${getResultPrimaryMetric(fallback)}` : null;
  }, [selectedResult?.resultId, selectedTc6Comparison, tc6PdfExportState]);

  const runnableSelectedTests = useMemo(() => normalizeSelectedTests(form.selectedTests), [form.selectedTests]);
  const tenantQuickAssessmentTests = useMemo(
    () =>
      assessmentTestUsage
        .map((item) => item.testKey)
        .filter((testKey): testKey is AssessmentV2TestKey => ASSESSMENT_V2_CREATABLE_TESTS.includes(testKey as AssessmentV2TestKey))
        .slice(0, ASSESSMENT_V2_QUICK_TESTS.length),
    [assessmentTestUsage],
  );
  const hasTenantAssessmentUsage = tenantQuickAssessmentTests.length > 0;
  const quickAssessmentTests = hasTenantAssessmentUsage ? tenantQuickAssessmentTests : [...ASSESSMENT_V2_QUICK_TESTS];
  const assessmentTestUsageByKey = useMemo(() => new Map(assessmentTestUsage.map((item) => [item.testKey, item])), [assessmentTestUsage]);
  const filteredAssessmentGroups = useMemo(() => filterAssessmentV2CatalogGroups(assessmentPickerSearch), [assessmentPickerSearch]);
  const visibleAssessmentTestCount = useMemo(
    () => filteredAssessmentGroups.reduce(
      (total, group) => total + group.sections.reduce((groupTotal, section) => groupTotal + countAssessmentV2CatalogSection(section), 0),
      0,
    ),
    [filteredAssessmentGroups],
  );

  const loadPatients = async () => {
    const requestedClientId = clientId;
    if (!requestedClientId) {
      setPatients([]);
      setPatientsLoading(false);
      return;
    }

    try {
      setPatientsLoading(true);
      const { data, error } = await supabase
        .from('patients')
        .select(
          `
            patientId,
            patientFullName,
            patientCpf,
            patientRg,
            patientDateOfBirth,
            patientGender,
            patientHeightCm,
            patientWeightKg,
            patientLifecycleStatus,
            patient_clients!inner(clientId),
            financial_profiles(clientId, financialProfileIsActive)
          `,
        )
        .eq('patient_clients.clientId', requestedClientId)
        .order('patientFullName', { ascending: true });

      if (error) throw error;
      if (activeClientIdRef.current !== requestedClientId) return;

      const mappedPatients: AssessmentV2Patient[] = (data || []).map((patient: any) => {
        const lifecycleStatus = patient.patientLifecycleStatus || 'active';
        const financialProfiles = Array.isArray(patient.financial_profiles)
          ? patient.financial_profiles
          : patient.financial_profiles
            ? [patient.financial_profiles]
            : [];
        const currentFinancialProfile = financialProfiles.find(
          (profile: any) => profile?.clientId === requestedClientId,
        );
        const profileActive =
          typeof currentFinancialProfile?.financialProfileIsActive === 'boolean'
            ? currentFinancialProfile.financialProfileIsActive
            : true;
        const patientIdentifier = getPreferredPatientIdentifier(patient) ?? '';

        return {
          id: patient.patientId,
          patientFullName: patient.patientFullName || '(Nome não informado)',
          patientCpf: patient.patientCpf || '',
          patientRg: patient.patientRg || '',
          patientDateOfBirth: patient.patientDateOfBirth || null,
          patientGender: patient.patientGender || null,
          patientHeightCm: normalizePatientHeightCm(patient.patientHeightCm),
          patientWeightKg: patient.patientWeightKg != null ? Number(patient.patientWeightKg) : null,
          personalData: {
            name: patient.patientFullName || '(Nome não informado)',
            cpf: patient.patientCpf || '',
            rg: patient.patientRg || '',
            birthDate: patient.patientDateOfBirth || '',
          },
          prontuario: patientIdentifier,
          prontu_x00c1_rio: patientIdentifier,
          nome: patient.patientFullName?.split(' ')[0] || '(Nome)',
          sobrenome: patient.patientFullName?.split(' ').slice(1).join(' ') || '',
          patientLifecycleStatus: lifecycleStatus,
          lifecycleStatus,
          financialProfile: {
            active: profileActive,
            hasPackage: false,
          },
          appointments: [],
          invoices: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
      });

      setPatients(mappedPatients);
    } catch (error: any) {
      if (activeClientIdRef.current !== requestedClientId) return;
      console.error('Erro ao carregar pacientes da V2:', error);
      toast.error(`Erro ao carregar pacientes: ${error?.message || 'falha desconhecida'}`);
    } finally {
      if (activeClientIdRef.current === requestedClientId) {
        setPatientsLoading(false);
      }
    }
  };

  const loadDashboardData = async (options?: {
    page?: number;
    pageSize?: AssessmentResultsPageSize;
    search?: string;
    status?: 'active' | 'cancelled' | 'all';
  }) => {
    const requestId = activeDashboardRequestRef.current + 1;
    activeDashboardRequestRef.current = requestId;
    const requestedClientId = clientId;
    const requestedClient = selectedClient;
    const requestedResultsPatientId = requestedPatientId;
    const requestedPage = options?.page ?? resultsPage;
    const requestedPageSize = options?.pageSize ?? resultsPageSize;
    const requestedSearch = options?.search ?? search;
    const requestedStatus = options?.status ?? statusFilter;
    const matchingPatientIds = requestedSearch.trim()
      ? patients
        .map((patient) => ({
          id: patient.id,
          score: getPatientSearchScore(
            requestedSearch,
            patient.patientFullName || patient.personalData?.name,
            [
              patient.patientCpf,
              patient.patientRg,
              patient.prontuario,
              patient.id,
            ],
          ),
        }))
        .filter(({ score }) => Number.isFinite(score))
        .sort((a, b) => a.score - b.score)
        .slice(0, 50)
        .map(({ id }) => id)
      : [];
    if (!requestedClientId) {
      setSnapshot(initialSnapshot);
      setResults([]);
      setResultsTotal(0);
      setAssessmentTestUsage([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setAssessmentTestUsage([]);
      const usageSince = new Date();
      usageSince.setFullYear(usageSince.getFullYear() - 1);
      const usageRequest = AssessmentResultsV2Service.listTestUsageByClientId(requestedClientId, {
        since: usageSince.toISOString(),
        limit: ASSESSMENT_V2_QUICK_TESTS.length,
        testKeys: [...ASSESSMENT_V2_ENABLED_TESTS],
      }).catch((error) => {
        console.warn('Não foi possível personalizar os atalhos de avaliações para o tenant:', error);
        return [];
      });
      const [branding, sessionCount, resultCount, resultsPageData, testUsage] = await Promise.all([
        ClientReportSettingsService.resolveForClient(requestedClientId, requestedClient),
        AssessmentSessionsV2Service.countByClientId(requestedClientId),
        AssessmentResultsV2Service.countByClientId(requestedClientId),
        AssessmentResultsV2Service.listPageForDashboard(requestedClientId, {
          patientId: requestedResultsPatientId,
          page: requestedPage,
          pageSize: requestedPageSize,
          search: requestedSearch,
          matchingPatientIds,
          status: requestedStatus,
        }),
        usageRequest,
      ]);
      const hydratedResults = await hydrateAssessmentResultsProfessionalSnapshots(
        resultsPageData.items,
        requestedClientId,
      );
      if (
        activeDashboardRequestRef.current !== requestId ||
        activeClientIdRef.current !== requestedClientId ||
        activeResultsPatientIdRef.current !== requestedResultsPatientId
      ) return;

      const lastAvailablePage = Math.max(1, Math.ceil(resultsPageData.total / requestedPageSize));
      if (requestedPage > lastAvailablePage) {
        setResultsPage(lastAvailablePage);
        await loadDashboardData({
          page: lastAvailablePage,
          pageSize: requestedPageSize,
          search: requestedSearch,
          status: requestedStatus,
        });
        return;
      }

      setSnapshot({
        branding,
        sessionCount,
        resultCount,
      });
      setAssessmentTestUsage(testUsage);
      setResultsTotal(resultsPageData.total);
      setResults(hydratedResults.filter((result) => result.status !== 'draft' && isDisplayableAssessmentResult(result)));
    } catch (error: any) {
      if (
        activeDashboardRequestRef.current !== requestId ||
        activeClientIdRef.current !== requestedClientId ||
        activeResultsPatientIdRef.current !== requestedResultsPatientId
      ) return;
      console.error('Erro ao carregar dashboard da V2:', error);
      toast.error(`Erro ao carregar as avaliações: ${error?.message || 'falha desconhecida'}`);
    } finally {
      if (
        activeDashboardRequestRef.current === requestId &&
        activeClientIdRef.current === requestedClientId &&
        activeResultsPatientIdRef.current === requestedResultsPatientId
      ) {
        setLoading(false);
      }
    }
  };

  const loadDraftSessions = async () => {
    const requestedClientId = clientId;
    if (!requestedClientId) {
      setDraftSessions([]);
      return;
    }

    try {
      const sessions = await AssessmentSessionsV2Service.listByStatus(requestedClientId, 'draft', 20);
      const summaries = await Promise.all(
        sessions.map(async (session) => {
          const sessionResults = await AssessmentResultsV2Service.listBySessionId(
            requestedClientId,
            session.sessionId,
          );
          const patientName =
            sessionResults[0]?.patientNameSnapshot ||
            patients.find((patient) => patient.id === session.patientId)?.personalData?.name ||
            patients.find((patient) => patient.id === session.patientId)?.patientFullName ||
            'Paciente';

          return {
            session,
            results: sessionResults,
            patientName,
          };
        }),
      );
      if (activeClientIdRef.current !== requestedClientId) return;

      setDraftSessions(
        summaries.filter(({ session }) => normalizeSelectedTests(session.selectedTests).length > 0),
      );
    } catch (error) {
      if (activeClientIdRef.current !== requestedClientId) return;
      console.error('Erro ao carregar rascunhos da V2:', error);
      toast.error('Erro ao carregar os rascunhos das avaliações.');
    }
  };

  useEffect(() => {
    return () => {
      if (resultsSearchDebounceRef.current !== null) {
        window.clearTimeout(resultsSearchDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    assessmentReportCandidateRequestRef.current += 1;
    if (resultsSearchDebounceRef.current !== null) {
      window.clearTimeout(resultsSearchDebounceRef.current);
      resultsSearchDebounceRef.current = null;
    }
    setHandledCreateRequestKey('');
    setLoading(Boolean(clientId));
    setPatientsLoading(Boolean(clientId));
    setResultActionLoading(false);
    setSigningResultId(null);
    setLoadingResultHistory(false);
    setPatients([]);
    setResults([]);
    setResultsPage(1);
    setResultsTotal(0);
    setAssessmentTestUsage([]);
    setDraftSessions([]);
    setSnapshot(initialSnapshot);
    setSelectedResult(null);
    setSelectedResultHistory([]);
    setSelectedTc6ComparisonId('__none__');
    setTc6PdfExportState(null);
    setStatusModalState(null);
    setShowTc6PayloadEditor(false);
    setShowResultPayloadEditor(false);
    setShowAssessmentReportModal(false);
    setAssessmentReportEditingResultId(null);
    setAssessmentReportEditingRevision(null);
    setAssessmentReportGeneratedAt(null);
    setAssessmentReportCandidates([]);
    setAssessmentReportCandidatesLoading(false);
    setAssessmentReportForm(createInitialAssessmentReportForm());
    void Promise.all([loadPatients(), loadDashboardData({ page: 1 }), loadDraftSessions()]);
  }, [clientId, requestedPatientId]);

  useEffect(() => {
    let cancelled = false;
    const requestedClientId = clientId;
    const requestedResultId = selectedResult?.resultId ?? null;
    const loadHistory = async () => {
      if (!selectedResult || !requestedClientId || isAnyAssessmentReportResult(selectedResult)) {
        setSelectedResultHistory([]);
        return;
      }

      try {
        setLoadingResultHistory(true);
        const history = await AssessmentResultsV2Service.listHistoryForPatient(
          requestedClientId,
          selectedResult.patientId,
          selectedResult.testKey,
          {
            excludeResultId: selectedResult.resultId,
            beforeAssessmentDate: selectedResult.assessmentDate,
            limit: 10,
          },
        );
        if (
          cancelled
          || activeClientIdRef.current !== requestedClientId
          || requestedResultId !== selectedResult.resultId
        ) {
          return;
        }
        setSelectedResultHistory(history);
      } catch (error) {
        if (cancelled || activeClientIdRef.current !== requestedClientId) return;
        console.error('Erro ao carregar historico do resultado V2:', error);
        toast.error('Erro ao carregar o histórico desta avaliação.');
      } finally {
        if (!cancelled && activeClientIdRef.current === requestedClientId) {
          setLoadingResultHistory(false);
        }
      }
    };

    void loadHistory();
    return () => {
      cancelled = true;
    };
  }, [selectedResult, clientId]);

  useEffect(() => {
    if (selectedResult?.testKey !== 'TC6' || tc6ComparisonHistory.length === 0) {
      setSelectedTc6ComparisonId('__none__');
      return;
    }

    setSelectedTc6ComparisonId((current) =>
      current !== '__none__' && tc6ComparisonHistory.some((option) => option.id === current)
        ? current
        : tc6ComparisonHistory[0].id,
    );
  }, [selectedResult?.testKey, tc6ComparisonHistory]);

  useEffect(() => {
    setShowTc6PayloadEditor(false);
    setShowResultPayloadEditor(false);
    setResultInterpretationDraft('');
    setTc6PayloadSaving(false);
    setResultPayloadSaving(false);
  }, [selectedResult?.resultId]);

  const getPatientLifecycleStatus = (patient?: AssessmentV2Patient): 'active' | 'inactive' | 'archived' => {
    const status = patient?.patientLifecycleStatus ?? patient?.lifecycleStatus ?? 'active';
    if (status === 'inactive' || status === 'archived') return status;
    return 'active';
  };

  const getPatientNewCareBlockReason = (patient?: AssessmentV2Patient): 'lifecycle' | 'clinical_inactive' | null => {
    if (!patient) return 'lifecycle';
    const lifecycleStatus = getPatientLifecycleStatus(patient);
    if (lifecycleStatus !== 'active') return 'lifecycle';
    const financialActive = typeof patient?.financialProfile?.active === 'boolean' ? patient.financialProfile.active : true;
    if (!financialActive) return 'clinical_inactive';
    return null;
  };

  const resetForm = () => setForm(initialForm());
  const resetRunnerState = () => {
    setShowRunnerModal(false);
    setRunnerActiveTest(null);
    setRunnerResults({});
    setRunnerSavedResults({});
    setRunnerSession(null);
  };

  const openCreateModal = () => {
    resetRunnerState();
    setForm({ ...initialForm(), selectedTests: ['INITIAL_ASSESSMENT'] });
    setAssessmentPickerSearch('');
    setOpenAssessmentGroupIds(ASSESSMENT_V2_MODAL_GROUPS.map(group => group.id));
    setOpenAssessmentSectionIds(ASSESSMENT_V2_MODAL_GROUPS.flatMap(group => group.sections.map(section => section.id)));
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setAssessmentPickerSearch('');
    setOpenAssessmentGroupIds([]);
    setOpenAssessmentSectionIds([]);
    resetForm();
  };

  const openPeakFlowDiaryFromCreate = () => {
    const patient = patientLookup.get(form.patientId);
    if (form.patientId && (!patient || getPatientNewCareBlockReason(patient))) {
      toast.error('Selecione um paciente ativo para abrir o diário.');
      return;
    }
    setDiaryPatientId(form.patientId);
    setShowCreateModal(false);
    setShowPeakFlowDiary(true);
  };

  const returnToCreateFromDiary = () => {
    setShowPeakFlowDiary(false);
    setShowCreateModal(true);
  };

  const closePublicInviteMonitor = () => {
    setPublicInviteMonitor(null);
  };

  const buildPublicInviteShareMessage = (patientName: string, tenantName: string, url: string) => {
    const normalizedPatientName = patientName.trim() || 'Paciente';
    const normalizedTenantName = tenantName.trim() || 'nossa equipe';

    return [
      `Olá, ${normalizedPatientName}.`,
      `Você recebeu um link-convite de ${normalizedTenantName}.`,
      'Para prosseguir com sua avaliação, clique no link abaixo e siga as instruções no site:',
      url,
    ].join('\n\n');
  };

  const getPublicInviteMonitorCopy = (monitor: PublicInviteMonitorState) => {
    switch (monitor.status) {
      case 'started':
        return 'O link já foi aberto. O acompanhamento será atualizado automaticamente até a conclusão.';
      case 'completed':
        return 'O paciente concluiu o preenchimento. Os resultados já podem ser consultados na lista abaixo.';
      case 'expired':
        return 'O prazo do link terminou antes da conclusão do preenchimento.';
      case 'revoked':
        return 'O link foi revogado e não pode mais ser utilizado.';
      default:
        return monitor.patientPhone
          ? 'Mensagem pronta para WhatsApp gerada. Aguarde o paciente iniciar o preenchimento ou feche este acompanhamento quando quiser.'
          : 'Mensagem pronta com o link gerada. Aguarde o paciente iniciar o preenchimento ou feche este acompanhamento quando quiser.';
    }
  };

  const getPublicInviteMonitorTone = (status: AssessmentInviteV2Status) => {
    switch (status) {
      case 'completed':
        return {
          frame: 'border-emerald-200 bg-emerald-50',
          icon: 'bg-emerald-100 text-emerald-700',
          badge: 'bg-emerald-100 text-emerald-700',
        };
      case 'expired':
      case 'revoked':
        return {
          frame: 'border-rose-200 bg-rose-50',
          icon: 'bg-rose-100 text-rose-700',
          badge: 'bg-rose-100 text-rose-700',
        };
      case 'started':
        return {
          frame: 'border-amber-200 bg-amber-50',
          icon: 'bg-amber-100 text-amber-700',
          badge: 'bg-amber-100 text-amber-700',
        };
      default:
        return {
          frame: 'border-sky-200 bg-sky-50',
          icon: 'bg-sky-100 text-sky-700',
          badge: 'bg-sky-100 text-sky-700',
        };
    }
  };

  useEffect(() => {
    if (!clientId || !publicInviteMonitor) return;
    if (!['active', 'started'].includes(publicInviteMonitor.status)) return;

    let cancelled = false;

    const syncInviteStatus = async () => {
      try {
        const invite = await AssessmentInvitesV2Service.getById(publicInviteMonitor.inviteId, clientId);
        if (cancelled) return;

        setPublicInviteMonitor((current) => {
          if (!current || current.inviteId !== invite.inviteId) return current;
          return {
            ...current,
            status: invite.status,
            expiresAt: invite.expiresAt,
            openedAt: invite.openedAt ?? current.openedAt ?? null,
            startedAt: invite.startedAt ?? current.startedAt ?? null,
            completedAt: invite.completedAt ?? current.completedAt ?? null,
          };
        });

        if (invite.status === 'completed' && publicInviteMonitor.status !== 'completed') {
          toast.success('Paciente concluiu o preenchimento do link público.');
          await Promise.all([loadDashboardData(), loadDraftSessions()]);
        }
      } catch (error) {
        console.error('Erro ao acompanhar status do link público da avaliação V2:', error);
      }
    };

    void syncInviteStatus();
    const intervalId = window.setInterval(() => {
      void syncInviteStatus();
    }, 5000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [clientId, publicInviteMonitor?.inviteId, publicInviteMonitor?.status]);

  useEffect(() => {
    if (publicInviteMonitor?.status !== 'completed') return;

    const timeoutId = window.setTimeout(() => {
      setPublicInviteMonitor((current) => (current?.status === 'completed' ? null : current));
    }, 2500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [publicInviteMonitor?.status]);

  const toggleSelectedTest = (testKey: AssessmentV2TestKey) => {
    setForm((current) => {
      const isSelected = current.selectedTests.includes(testKey);
      const nextSelected = isSelected
        ? current.selectedTests.filter((item) => item !== testKey)
        : [...current.selectedTests, testKey];

      return {
        ...current,
        selectedTests: ASSESSMENT_V2_CREATABLE_TESTS.filter((item) => nextSelected.includes(item)),
      };
    });
  };

  const addPlannedAssessmentTests = (keys: string[]) => {
    const requested = new Set([...form.selectedTests, ...keys]);
    const selectedTests = ASSESSMENT_V2_CREATABLE_TESTS.filter((key) => requested.has(key));
    setForm((current) => ({ ...current, selectedTests }));
    toast.info('Testes incluídos nas etapas. Salve o rascunho para manter essa seleção.');
  };

  const toggleAssessmentGroup = (groupId: string) => {
    setOpenAssessmentGroupIds((current) =>
      current.includes(groupId) ? current.filter((item) => item !== groupId) : [...current, groupId],
    );
  };

  const toggleAssessmentSection = (sectionId: string) => {
    setOpenAssessmentSectionIds((current) =>
      current.includes(sectionId) ? current.filter((item) => item !== sectionId) : [...current, sectionId],
    );
  };

  const handleRefresh = async () => {
    if (resultsSearchDebounceRef.current !== null) {
      window.clearTimeout(resultsSearchDebounceRef.current);
      resultsSearchDebounceRef.current = null;
    }
    await Promise.all([loadPatients(), loadDashboardData(), loadDraftSessions()]);
  };

  const handleResultsSearchChange = (nextSearch: string) => {
    setSearch(nextSearch);
    setLoading(true);
    if (resultsSearchDebounceRef.current !== null) {
      window.clearTimeout(resultsSearchDebounceRef.current);
    }

    resultsSearchDebounceRef.current = window.setTimeout(() => {
      resultsSearchDebounceRef.current = null;
      setResultsPage(1);
      void loadDashboardData({ page: 1, search: nextSearch });
    }, 350);
  };

  const handleResultsPageChange = async (nextPage: number) => {
    if (nextPage < 1 || nextPage > resultsPageCount || nextPage === resultsPage) return;
    if (resultsSearchDebounceRef.current !== null) {
      window.clearTimeout(resultsSearchDebounceRef.current);
      resultsSearchDebounceRef.current = null;
    }
    setResultsPage(nextPage);
    await loadDashboardData({ page: nextPage });
  };

  const handleResultsPageSizeChange = async (nextPageSize: AssessmentResultsPageSize) => {
    if (!ASSESSMENT_RESULTS_PAGE_SIZES.includes(nextPageSize)) return;
    if (resultsSearchDebounceRef.current !== null) {
      window.clearTimeout(resultsSearchDebounceRef.current);
      resultsSearchDebounceRef.current = null;
    }
    setResultsPageSize(nextPageSize);
    setResultsPage(1);
    await loadDashboardData({ page: 1, pageSize: nextPageSize });
  };

  const handleStatusFilterChange = async (nextStatus: 'active' | 'cancelled' | 'all') => {
    if (resultsSearchDebounceRef.current !== null) {
      window.clearTimeout(resultsSearchDebounceRef.current);
      resultsSearchDebounceRef.current = null;
    }
    setStatusFilter(nextStatus);
    setResultsPage(1);
    await loadDashboardData({ page: 1, status: nextStatus });
  };

  const copyToClipboard = async (value: string, successMessage: string, fallbackMessage: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(successMessage);
    } catch (error) {
      console.error('Erro ao copiar conteúdo do invite público da avaliação:', error);
      toast.info(fallbackMessage);
    }
  };

  const copyGeneratedInviteLink = async (url: string) =>
    copyToClipboard(url, 'Link copiado para a área de transferência.', 'Link gerado. Copie manualmente abaixo.');

  const copyGeneratedInviteMessage = async (message: string) =>
    copyToClipboard(
      message,
      'Mensagem pronta para WhatsApp copiada para a área de transferência.',
      'Mensagem gerada. Copie manualmente abaixo.',
    );

  const handleGeneratePublicInvite = async () => {
    if (!user || !clientId || !selectedPatient || !selectedClient?.clientSlug) {
      toast.error('Não foi possível identificar tenant, usuário ou paciente para gerar o link.');
      return;
    }

    if (!runnableSelectedTests.length) {
      toast.error('Selecione pelo menos um questionário.');
      return;
    }

    if (!runnableSelectedTests.every((testKey) => isAssessmentV2QuestionnaireTest(testKey))) {
      toast.error('O link público aceita apenas questionários autoaplicáveis nesta etapa.');
      return;
    }

    if (!selectedPatient.patientDateOfBirth) {
      toast.error('O paciente precisa ter data de nascimento cadastrada para validar o link público.');
      return;
    }

    const blockReason = getPatientNewCareBlockReason(selectedPatient);
    if (blockReason) {
      toast.error(
        blockReason === 'clinical_inactive'
          ? 'Paciente está inativo assistencialmente e não pode receber novas avaliações.'
          : 'Paciente inativado ou arquivado não pode receber novas avaliações.',
      );
      return;
    }

    try {
      setSaving(true);
      const sharedContext = await buildAssessmentSharedContext();
      const invite = await AssessmentInvitesV2Service.create({
        clientId,
        clientSlug: selectedClient.clientSlug,
        patientId: selectedPatient.id,
        patientBirthDate: selectedPatient.patientDateOfBirth,
        selectedTests: runnableSelectedTests,
        sessionDate: sharedContext.assessmentDateIso,
        notes: form.notes || null,
        createdByUserId: user.userId,
        professionalContext: {
          userId: user.userId,
          name: user.userDisplayName || 'Profissional',
          registration: sharedContext.professionalRegistration,
          role: sharedContext.professionalRole,
        },
      });

      const patientName = selectedPatient.personalData?.name || selectedPatient.patientFullName || 'Paciente';
      const patientPhone = selectedPatient.personalData?.phone?.trim() || null;
      const tenantName = snapshot.branding?.displayName || selectedClient.clientName || 'Tenant ativo';
      const shareMessage = buildPublicInviteShareMessage(patientName, tenantName, invite.url);

      setPublicInviteMonitor({
        inviteId: invite.invite.inviteId,
        clientId,
        patientName,
        patientPhone,
        tenantName,
        selectedTests: runnableSelectedTests,
        url: invite.url,
        shareMessage,
        expiresAt: invite.invite.expiresAt,
        status: invite.invite.status,
        openedAt: invite.invite.openedAt ?? null,
        startedAt: invite.invite.startedAt ?? null,
        completedAt: invite.invite.completedAt ?? null,
      });
      await copyGeneratedInviteMessage(shareMessage);
      closeCreateModal();
      toast.success('Link público gerado. Acompanhando o preenchimento.');
      await loadDraftSessions();
    } catch (error: any) {
      console.error('Erro ao gerar link público da avaliação V2:', error);
      toast.error(`Erro ao gerar link público: ${error?.message || 'falha desconhecida'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleStartRunner = () => {
    if (!form.patientId) {
      toast.error('Selecione um paciente.');
      return;
    }

    const blockReason = getPatientNewCareBlockReason(selectedPatient);
    if (blockReason) {
      toast.error(
        blockReason === 'clinical_inactive'
          ? 'Paciente está inativo assistencialmente e não pode receber novas avaliações.'
          : 'Paciente inativado ou arquivado não pode receber novas avaliações.',
      );
      return;
    }

    if (!runnableSelectedTests.length) {
      toast.error('Selecione pelo menos um teste.');
      return;
    }

    setRunnerSession(null);
    setRunnerResults({});
    setRunnerSavedResults({});
    setRunnerActiveTest(runnableSelectedTests[0] ?? null);
    setShowCreateModal(false);
    setShowRunnerModal(true);
  };

  const buildAssessmentSharedContext = async () => {
    if (!user || !clientId || !selectedPatient) {
      throw new Error('Não foi possível identificar tenant, usuário ou paciente para salvar a avaliação.');
    }

    const professionalRole = selectedClient?.userRole || user.userRole || 'admin';
    const canUseProfessionalRegistration =
      professionalRole === 'doctor' || professionalRole === 'fisioterapeuta';
    const doctor = canUseProfessionalRegistration
      ? await DoctorService.getDoctorByuserId(user.userId).catch(() => null)
      : null;
    const professionalRegistration = canUseProfessionalRegistration
      ? doctor?.doctorCrm || doctor?.doctorCrefito || (user as any)?.crm || (user as any)?.crefito || null
      : null;
    const assessmentDateIso = new Date(form.datetime).toISOString();
    const patientAgeAtAssessment = calculateAgeAtDate(selectedPatient.patientDateOfBirth || null, assessmentDateIso);
    const patientContext: AssessmentPatientContext = {
      birthDate: selectedPatient.patientDateOfBirth || null,
      ageYears: patientAgeAtAssessment,
      sex: selectedPatient.patientGender || null,
      weightKg: normalizePatientWeightKg(selectedPatient.patientWeightKg),
      heightCm: normalizePatientHeightCm(selectedPatient.patientHeightCm),
      identifier: getPreferredPatientIdentifier(selectedPatient),
    };

    return {
      professionalName: user.userDisplayName || 'Profissional',
      professionalRegistration,
      professionalRole,
      assessmentDateIso,
      patientAgeAtAssessment,
      patientContext,
    };
  };

  const createAnamnesisFromAssessment = async (
    patientId: string,
    input: InitialAssessmentAnamnesisCreationRequest,
  ) => {
    if (!user || !clientId) {
      throw new Error('Não foi possível identificar a clínica ou o profissional responsável.');
    }

    return InitialAssessmentAnamnesisService.create({
      clientId,
      patientId,
      userId: user.userId,
      userName: user.userDisplayName || null,
      userRole: selectedClient?.userRole || user.userRole || null,
      recordDate: input.recordDate,
      sections: input.sections,
    });
  };

  const resolvePatientContextForResult = (rawResult: any, fallback: AssessmentPatientContext, testKey?: AssessmentV2TestKey): AssessmentPatientContext => {
    if (testKey === 'PEAK_COUGH_FLOW') return resolvePeakCoughFlowPatientContext(rawResult, fallback);
    const override =
      rawResult && typeof rawResult === 'object' && rawResult.patientContext && typeof rawResult.patientContext === 'object'
        ? (rawResult.patientContext as Record<string, unknown>)
        : null;

    if (!override) {
      return fallback;
    }

    return {
      birthDate: typeof override.birthDate === 'string' ? override.birthDate : fallback.birthDate,
      ageYears: toFiniteNumber(override.ageYears) ?? fallback.ageYears,
      sex: typeof override.sex === 'string' ? override.sex : fallback.sex,
      weightKg: normalizePatientWeightKg(override.weightKg) ?? fallback.weightKg,
      heightCm: normalizePatientHeightCm(override.heightCm) ?? fallback.heightCm,
      identifier: typeof override.identifier === 'string' ? override.identifier : fallback.identifier,
    };
  };

  const getRunnerTestInitialValue = (testKey: AssessmentV2TestKey) => {
    const storedResult = runnerSavedResults[testKey];
    if (!storedResult) return undefined;

    const payload = resolveRunnerPayload(testKey, storedResult);

    switch (testKey) {
      case 'INITIAL_ASSESSMENT':
      case 'TC6':
      case 'ISWT':
      case 'ESWT':
      case '6MST':
      case 'CHESTER':
      case '5xSTS':
      case 'STS_30S':
      case 'STS_1MIN':
      case 'HANDGRIP':
      case 'PERIPHERAL_MUSCLE_STRENGTH':
      case 'ONE_RM':
      case 'PIMAX':
      case 'PEMAX':
      case 'S_INDEX':
      case 'RESP_ENDURANCE':
      case 'PEAK_FLOW':
      case 'PEAK_COUGH_FLOW':
      case 'CV_POSTURAL':
      case 'SNIP':
      case 'TUG':
      case '4MGS':
      case 'AQLQ':
      case 'PSQI':
      case 'LCADL':
      case 'PFSDQ-M':
      case 'SAQ':
      case 'SGRQ':
        return payload;
      case 'CAT':
      case 'ACT':
      case 'HADS':
      case 'MLHFQ':
        return Array.isArray(payload.answers) ? payload.answers : undefined;
      case 'mMRC':
        return typeof payload.grade === 'number' ? payload.grade : undefined;
      default:
        return undefined;
    }
  };

  const getRunnerPatientContext = (testKey: AssessmentV2TestKey): AssessmentPatientContext => {
    const initialValue = getRunnerTestInitialValue(testKey) as { patientContext?: Record<string, unknown> } | undefined;
    const payloadContext =
      initialValue?.patientContext && typeof initialValue.patientContext === 'object' ? initialValue.patientContext : undefined;
    const assessmentDateIso = new Date(form.datetime).toISOString();

    if (testKey === 'PEAK_COUGH_FLOW') return resolvePeakCoughFlowPatientContext(initialValue, {
      birthDate: selectedPatient?.patientDateOfBirth || null,
      ageYears: calculateAgeAtDate(selectedPatient?.patientDateOfBirth || null, assessmentDateIso),
      sex: selectedPatient?.patientGender || null,
      weightKg: normalizePatientWeightKg(selectedPatient?.patientWeightKg),
      heightCm: normalizePatientHeightCm(selectedPatient?.patientHeightCm),
      identifier: getPreferredPatientIdentifier(selectedPatient),
    });

    return {
      birthDate:
        typeof payloadContext?.birthDate === 'string'
          ? payloadContext.birthDate
          : selectedPatient?.patientDateOfBirth || null,
      ageYears:
        toFiniteNumber(payloadContext?.ageYears) ??
        calculateAgeAtDate(selectedPatient?.patientDateOfBirth || null, assessmentDateIso),
      sex: typeof payloadContext?.sex === 'string' ? payloadContext.sex : selectedPatient?.patientGender || null,
      weightKg: normalizePatientWeightKg(payloadContext?.weightKg ?? selectedPatient?.patientWeightKg),
      heightCm: normalizePatientHeightCm(payloadContext?.heightCm ?? selectedPatient?.patientHeightCm),
      identifier:
        typeof payloadContext?.identifier === 'string'
          ? payloadContext.identifier
          : getPreferredPatientIdentifier(selectedPatient),
    };
  };

  const getRunnerPimaxReference = (): number | null =>
    extractPimaxMaxFromPayload(runnerResults.PIMAX) ??
    extractPimaxMaxFromPayload(resolveRunnerPayload('PIMAX', runnerSavedResults.PIMAX));

  const getRunnerHandgripReference = (): number | null =>
    extractHandgripMaxFromPayload(runnerResults.HANDGRIP) ??
    extractHandgripMaxFromPayload(resolveRunnerPayload('HANDGRIP', runnerSavedResults.HANDGRIP));

  const getRunnerISWTReference = (): {
    peakLevelReached: number | null;
    peakSpeedKmh: number | null;
  } | null => {
    const payload = runnerResults.ISWT ?? resolveRunnerPayload('ISWT', runnerSavedResults.ISWT);
    if (!payload) return null;
    const summary = summarizeISWTResult(payload);
    if (summary.bestDistanceMeters <= 0 && summary.bestSpeedKmh == null) return null;
    return {
      peakLevelReached: summary.bestLevelReached || null,
      peakSpeedKmh: summary.bestSpeedKmh,
    };
  };

  const getNextPendingTest = (
    selectedTests: AssessmentV2TestKey[],
    savedResults: Partial<Record<AssessmentV2TestKey, any>>,
    currentTest?: AssessmentV2TestKey | null,
  ): AssessmentV2TestKey | null => {
    if (!selectedTests.length) return null;

    const currentIndex = currentTest ? selectedTests.indexOf(currentTest) : -1;
    for (let index = currentIndex + 1; index < selectedTests.length; index += 1) {
      const testKey = selectedTests[index];
      if (!isRunnerTestPayloadCompleted(testKey, savedResults[testKey])) return testKey;
    }

    return selectedTests.find((testKey) => !isRunnerTestPayloadCompleted(testKey, savedResults[testKey])) ?? selectedTests[selectedTests.length - 1] ?? null;
  };

  const getRunnerTestStatuses = (): Record<AssessmentV2TestKey, RunnerTestStatus> =>
    runnableSelectedTests.reduce((accumulator, testKey, index) => {
      if (isRunnerTestPayloadCompleted(testKey, runnerResults[testKey] ?? resolveRunnerPayload(testKey, runnerSavedResults[testKey]))) {
        accumulator[testKey] = 'completed';
        return accumulator;
      }

      accumulator[testKey] =
        runnerActiveTest === testKey || (!runnerActiveTest && index === 0) ? 'in_progress' : 'pending';
      return accumulator;
    }, {} as Record<AssessmentV2TestKey, RunnerTestStatus>);

  const buildResultContent = (
    testKey: AssessmentV2TestKey,
    rawResult: any,
    sharedContext: Awaited<ReturnType<typeof buildAssessmentSharedContext>>,
  ) => {
    const professionalContext = {
      name: sharedContext.professionalName,
      registration: sharedContext.professionalRegistration,
      role: sharedContext.professionalRole,
    };

    switch (testKey) {
      case 'INITIAL_ASSESSMENT': {
        const initialAssessment = normalizeInitialAssessmentPayload(rawResult as InitialAssessmentResultPayload);
        if (!initialAssessment) {
          throw new Error('Registre ao menos uma informação da avaliação.');
        }
        const summary = summarizeInitialAssessment(initialAssessment);

        return {
          summaryJson: {
            statusLabel: summary.statusLabel,
            classification: summary.classification,
            severity: summary.classification,
            filledSections: summary.filledSections,
            hasAnamnesisSnapshot: summary.hasAnamnesisSnapshot,
            assessmentReason: summary.assessmentReason,
            interpretation: summary.professionalSynthesis,
            assessmentMoment: initialAssessment.assessmentMoment,
            episodeLabel: initialAssessment.episodeLabel,
            title: summary.statusLabel,
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg: sharedContext.patientContext.weightKg,
            patientHeightCm: sharedContext.patientContext.heightCm,
          },
          payloadJson: {
            ...initialAssessment,
            patientContext: sharedContext.patientContext,
            professionalContext,
          },
          assetsJson: {},
        };
      }
      case 'TC6': {
        const tc6Result = rawResult as Tc6ResultPayload & {
          chartImages?: Tc6ChartImages;
        };
        const chartImages = tc6Result.chartImages && typeof tc6Result.chartImages === 'object' ? tc6Result.chartImages : {};
        const payloadWithoutCharts = {
          ...tc6Result,
          patientContext: sharedContext.patientContext,
          professionalContext,
        };
        delete (payloadWithoutCharts as { chartImages?: Tc6ChartImages }).chartImages;

        const summary = summarizeTc6Result(tc6Result);
        const interpretation = resolveTC6Interpretation(payloadWithoutCharts);

        return {
          summaryJson: {
            attemptCount: summary.attemptCount,
            workflowStatus: summary.workflowStatus,
            bestAttempt: summary.bestAttempt,
            bestDistance_m: summary.bestDistance_m,
            selectedReferenceKey: summary.selectedReferenceKey,
            selectedReferenceLabel: summary.selectedReferenceLabel,
            predictedDistance_m: summary.predictedDistance_m,
            percentPredicted: summary.percentPredicted,
            meanSpeed_kmh: summary.meanSpeed_kmh,
            minSpo2: summary.minSpo2,
            spo2Drop: summary.spo2Drop,
            totalStopped_s: summary.totalStopped_s,
            hasSignificantDesaturation: summary.hasSignificantDesaturation,
            severity: summary.severity,
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg: sharedContext.patientContext.weightKg,
            patientHeightCm: sharedContext.patientContext.heightCm,
          },
          payloadJson: {
            ...payloadWithoutCharts,
            report: interpretation ? { ...(payloadWithoutCharts.report ?? {}), interpretation } : payloadWithoutCharts.report,
          },
          assetsJson: {
            chartImages,
            tc6WorkflowStatus: summary.workflowStatus,
          },
        };
      }
      case 'ISWT': {
        const iswtResult = rawResult as ShuttleIswtResultPayload;
        const payloadWithContext = {
          ...iswtResult,
          patientContext: sharedContext.patientContext,
          professionalContext,
        };
        const summary = summarizeISWTResult(payloadWithContext);
        const interpretation = resolveISWTInterpretation(payloadWithContext);

        return {
          summaryJson: {
            attemptCount: summary.attemptCount,
            bestAttemptNumber: summary.bestAttemptNumber,
            bestDistanceMeters: summary.bestDistanceMeters,
            bestLevelReached: summary.bestLevelReached,
            bestLevelLabel: summary.bestLevelLabel,
            bestSpeedKmh: summary.bestSpeedKmh,
            predictedDistanceMeters: summary.predictedDistanceMeters,
            percentPredicted: summary.percentPredicted,
            secondAttemptDeltaMeters: summary.secondAttemptDeltaMeters,
            learningEffectDetected: summary.learningEffectDetected,
            minSpo2: summary.minSpo2,
            peakHr: summary.peakHr,
            severity: summary.severity,
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg: sharedContext.patientContext.weightKg,
            patientHeightCm: sharedContext.patientContext.heightCm,
          },
          payloadJson: {
            ...payloadWithContext,
            report: {
              ...(payloadWithContext.report ?? {}),
              interpretation,
            },
          },
          assetsJson: {},
        };
      }
      case 'ESWT': {
        const eswtResult = rawResult as ShuttleEswtResultPayload;
        const payloadWithContext = {
          ...eswtResult,
          patientContext: sharedContext.patientContext,
          professionalContext,
        };
        const summary = summarizeESWTResult(payloadWithContext);
        const interpretation = resolveESWTInterpretation(payloadWithContext);

        return {
          summaryJson: {
            durationSeconds: summary.durationSeconds,
            distanceMeters: summary.distanceMeters,
            speedKmh: summary.speedKmh,
            speedPercentOfIswt: summary.speedPercentOfIswt,
            minSpo2: summary.minSpo2,
            peakHr: summary.peakHr,
            withinRecommendedWindow: summary.withinRecommendedWindow,
            severity: summary.severity,
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg: sharedContext.patientContext.weightKg,
            patientHeightCm: sharedContext.patientContext.heightCm,
          },
          payloadJson: {
            ...payloadWithContext,
            report: {
              ...(payloadWithContext.report ?? {}),
              interpretation,
            },
          },
          assetsJson: {},
        };
      }
      case '6MST': {
        const sixMstResult = rawResult as SixMSTResultPayload;
        const payloadWithContext = {
          ...sixMstResult,
          patientContext: sharedContext.patientContext,
          professionalContext,
        };
        const summary = summarizeSixMSTResult(payloadWithContext);
        const interpretation = resolveSixMSTInterpretation(payloadWithContext);

        return {
          summaryJson: {
            totalSteps: summary.totalSteps,
            predictedSteps: summary.predictedSteps,
            percentPredicted: summary.percentPredicted,
            normativeMean: summary.normativeMean,
            normativeP10: summary.normativeP10,
            normativeBandLabel: summary.normativeBandLabel,
            belowNormativeLowerLimit: summary.belowNormativeLowerLimit,
            minSpo2: summary.minSpo2,
            spo2Drop: summary.spo2Drop,
            peakHr: summary.peakHr,
            totalStopped_s: summary.totalStopped_s,
            interruptionCount: summary.interruptionCount,
            severity: summary.severity,
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg: sharedContext.patientContext.weightKg,
            patientHeightCm: sharedContext.patientContext.heightCm,
          },
          payloadJson: {
            ...payloadWithContext,
            report: {
              ...(payloadWithContext.report ?? {}),
              interpretation,
            },
          },
          assetsJson: {},
        };
      }
      case 'CHESTER': {
        const chesterResult = rawResult as ChesterResultPayload;
        const payloadWithContext = {
          ...chesterResult,
          patientContext: sharedContext.patientContext,
          professionalContext,
        };
        const summary = summarizeChesterResult(payloadWithContext);
        const interpretation = resolveChesterInterpretation(payloadWithContext);

        return {
          summaryJson: {
            totalSteps: summary.totalSteps,
            achievedLevel: summary.achievedLevel,
            completedLevels: summary.completedLevels,
            completedAllLevels: summary.completedAllLevels,
            maxCadence: summary.maxCadence,
            minSpo2: summary.minSpo2,
            peakHr: summary.peakHr,
            terminationReasonLabel: summary.terminationReasonLabel,
            severity: summary.severity,
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg: sharedContext.patientContext.weightKg,
            patientHeightCm: sharedContext.patientContext.heightCm,
          },
          payloadJson: {
            ...payloadWithContext,
            report: {
              ...(payloadWithContext.report ?? {}),
              interpretation,
            },
          },
          assetsJson: {},
        };
      }
      case 'HANDGRIP': {
        const handgripRaw = rawResult as HandgripResultPayload & {
          chartImages?: HandgripChartImages;
        };
        const handgripResult = normalizeHandgripPayload(handgripRaw) ?? handgripRaw;
        const chartImages = handgripRaw.chartImages && typeof handgripRaw.chartImages === 'object' ? handgripRaw.chartImages : {};
        const payloadWithoutCharts = {
          ...handgripResult,
          patientContext: sharedContext.patientContext,
          professionalContext,
          finishedAt: handgripResult.finishedAt || new Date().toISOString(),
        };
        delete (payloadWithoutCharts as { chartImages?: HandgripChartImages }).chartImages;
        const summary = summarizeHandgripResult(handgripResult);
        const severity = getHandgripSeverityLabel(summary.absolutePercentile);

        return {
          summaryJson: {
            bestOverall: summary.bestOverall,
            bestRight: summary.bestRight,
            bestLeft: summary.bestLeft,
            meanRight: summary.meanRight,
            meanLeft: summary.meanLeft,
            dominantHand: summary.dominantHand,
            dominantHandLabel: summary.dominantHandLabel,
            normalizedOverall: summary.normalizedOverall,
            absolutePercentile: summary.absolutePercentile,
            absolutePercentPredicted: summary.absolutePercentPredicted,
            normalizedPercentile: summary.normalizedPercentile,
            normalizedPercentPredicted: summary.normalizedPercentPredicted,
            severity,
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg: sharedContext.patientContext.weightKg,
            patientHeightCm: sharedContext.patientContext.heightCm,
          },
          payloadJson: payloadWithoutCharts,
          assetsJson: {
            chartImages,
          },
        };
      }
      case 'PERIPHERAL_MUSCLE_STRENGTH': {
        const peripheralRaw = rawResult as PeripheralMuscleStrengthResultPayload;
        const patientContext = resolvePatientContextForResult(peripheralRaw, sharedContext.patientContext);
        const normalized =
          normalizePeripheralMuscleStrengthPayload({
            ...peripheralRaw,
            patientContext,
            professionalContext,
            finishedAt: peripheralRaw.finishedAt || new Date().toISOString(),
          }) ?? peripheralRaw;
        const summary = summarizePeripheralMuscleStrengthResult(normalized);
        const severity = getPeripheralStrengthSeverityLabel(normalized);

        return {
          summaryJson: {
            selectedMusclesCount: summary.selectedMusclesCount,
            completedMuscles: summary.completedMuscles,
            reproducibleMuscles: summary.reproducibleMuscles,
            averagePercentPredicted: summary.averagePercentPredicted,
            lowestPercentPredicted: summary.lowestPercentPredicted,
            lowestMuscleKey: summary.lowestMuscleKey,
            lowestMuscleLabel: summary.lowestMuscleLabel,
            bmi: summary.bmi,
            dominantSide: summary.dominantSide,
            dominantSideLabel: summary.dominantSideLabel,
            severity,
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: patientContext.sex,
            patientWeightKg: patientContext.weightKg,
            patientHeightCm: patientContext.heightCm,
          },
          payloadJson: normalized,
          assetsJson: {},
        };
      }
      case 'ONE_RM': {
        const oneRmRaw = rawResult as OneRMResultPayload;
        const patientContext = resolvePatientContextForResult(oneRmRaw, sharedContext.patientContext);
        const normalized =
          normalizeOneRMPayload({
            ...oneRmRaw,
            patientContext,
            professionalContext,
            finishedAt: oneRmRaw?.finishedAt || new Date().toISOString(),
            protocolSteps: oneRmRaw?.protocolSteps?.length ? oneRmRaw.protocolSteps : Array.from(ONE_RM_PROTOCOL_STEPS),
            referenceNotes: oneRmRaw?.referenceNotes?.length ? oneRmRaw.referenceNotes : Array.from(ONE_RM_REFERENCE_NOTES),
          }) ??
          buildOneRMPayload({
            entries: Array.isArray(oneRmRaw?.entries) ? oneRmRaw.entries : [],
            notes: oneRmRaw?.notes,
            patientContext,
            professionalContext,
            finishedAt: oneRmRaw?.finishedAt || new Date().toISOString(),
          });

        if (!normalized) {
          throw new Error('Não foi possível normalizar o payload do 1RM.');
        }

        const summary = summarizeOneRMPayload(normalized);

        return {
          summaryJson: {
            groupCount: summary.groupCount,
            directCount: summary.directCount,
            estimatedCount: summary.estimatedCount,
            bothMethodsCount: summary.bothMethodsCount,
            bestDirect1RMKg: summary.bestDirect1RMKg,
            bestEstimated1RMKg: summary.bestEstimated1RMKg,
            bestOverallKg: summary.bestOverallKg,
            bestGroupLabel: summary.bestGroupLabel,
            severity: getOneRMSeverityLabel(normalized),
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: patientContext.sex,
            patientWeightKg: patientContext.weightKg,
            patientHeightCm: patientContext.heightCm,
          },
          payloadJson: normalized,
          assetsJson: {},
        };
      }
      case 'PIMAX': {
        const rawPimax = rawResult as PimaxResultPayload & {
          chartImages?: Record<string, string>;
        };
        const chartImages = rawPimax.chartImages && typeof rawPimax.chartImages === 'object' ? rawPimax.chartImages : {};
        const positionChartImages: Record<string, Record<string, string>> = {};
        const positionSeries = rawPimax.positionSeries?.map((series) => {
          const { chartImages: images, ...positionResult } = series.result;
          if (images && typeof images === 'object') positionChartImages[series.id] = images;
          return { ...series, result: positionResult };
        });
        const payloadWithoutCharts: PimaxResultPayload = {
          ...rawPimax,
          ...(positionSeries ? { positionSeries } : {}),
          patientContext: sharedContext.patientContext,
          professionalContext,
          finishedAt: rawPimax.finishedAt || new Date().toISOString(),
        };
        delete (payloadWithoutCharts as { chartImages?: Record<string, string> }).chartImages;
        const summary = summarizePimaxPayload(payloadWithoutCharts);
        const normalizedPayload: PimaxResultPayload = {
          ...payloadWithoutCharts,
          predicted: summary.predicted,
          percentPredicted: summary.percentPredicted,
          lowerLimit: summary.lowerLimit,
          belowLowerLimit: summary.belowLowerLimit,
        };

        return {
          summaryJson: {
            max: summary.max,
            sustained1s: summary.sustained1s,
            predicted: summary.predicted,
            percentPredicted: summary.percentPredicted,
            lowerLimit: summary.lowerLimit,
            belowLowerLimit: summary.belowLowerLimit,
            maneuversAnalyzed: summary.quality?.maneuversAnalyzed ?? summary.measurements.length,
            variationTop2Percent: summary.quality?.variationTop2Percent ?? null,
            meetsCriteria: summary.quality?.meetsCriteria ?? null,
            severity: summary.severity,
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg: sharedContext.patientContext.weightKg,
            patientHeightCm: sharedContext.patientContext.heightCm,
          },
          payloadJson: normalizedPayload,
          assetsJson: {
            chartImages,
            positionChartImages,
          },
        };
      }
      case 'PEMAX': {
        const rawPemax = rawResult as PemaxResultPayload;
        const payloadWithContext: PemaxResultPayload = {
          ...rawPemax,
          patientContext: sharedContext.patientContext,
          professionalContext,
          finishedAt: rawPemax.finishedAt || new Date().toISOString(),
        };
        const summary = summarizePemaxPayload(payloadWithContext);
        const normalizedPayload: PemaxResultPayload = {
          ...payloadWithContext,
          predicted: summary.predicted,
          percentPredicted: summary.percentPredicted,
          lowerLimit: summary.lowerLimit,
          belowLowerLimit: summary.belowLowerLimit,
        };

        return {
          summaryJson: {
            max: summary.max,
            predicted: summary.predicted,
            percentPredicted: summary.percentPredicted,
            lowerLimit: summary.lowerLimit,
            belowLowerLimit: summary.belowLowerLimit,
            maneuversAnalyzed: summary.quality.maneuversAnalyzed,
            variationTop2Percent: summary.quality.variationTop2Percent,
            meetsCriteria: summary.quality.meetsCriteria,
            severity: summary.severity,
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg: sharedContext.patientContext.weightKg,
            patientHeightCm: sharedContext.patientContext.heightCm,
          },
          payloadJson: normalizedPayload,
          assetsJson: {},
        };
      }
      case 'S_INDEX': {
        const rawSIndex = rawResult as SIndexResultPayload & {
          chartImages?: Record<string, string>;
        };
        const chartImages = rawSIndex.chartImages && typeof rawSIndex.chartImages === 'object' ? rawSIndex.chartImages : {};
        const payloadWithoutCharts: SIndexResultPayload = {
          ...rawSIndex,
          patientContext: sharedContext.patientContext,
          professionalContext,
          finishedAt: rawSIndex.finishedAt || new Date().toISOString(),
        };
        delete (payloadWithoutCharts as { chartImages?: Record<string, string> }).chartImages;
        const summary = summarizeSIndexPayload(payloadWithoutCharts);

        return {
          summaryJson: {
            maxPressure: summary.maxPressure,
            predictedSIndex: summary.predictedSIndex,
            percentPredicted: summary.percentPredicted,
            llnSIndex: summary.llnSIndex,
            belowLLN: summary.belowLLN,
            breathsAnalyzed: summary.breathsAnalyzed,
            referenceEquation: summary.referenceEquation,
            referenceLabel: summary.referenceLabel,
            severity: summary.severity,
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg: sharedContext.patientContext.weightKg,
            patientHeightCm: sharedContext.patientContext.heightCm,
          },
          payloadJson: payloadWithoutCharts,
          assetsJson: {
            chartImages,
          },
        };
      }
      case 'RESP_ENDURANCE': {
        const rawEndurance = rawResult as RespiratoryEnduranceResultPayload & {
          chartImages?: Record<string, string>;
        };
        const chartImages = rawEndurance.chartImages && typeof rawEndurance.chartImages === 'object' ? rawEndurance.chartImages : {};
        const payloadWithoutCharts: RespiratoryEnduranceResultPayload = {
          ...rawEndurance,
          patientContext: sharedContext.patientContext,
          professionalContext,
          finishedAt: rawEndurance.finishedAt || new Date().toISOString(),
        };
        delete (payloadWithoutCharts as { chartImages?: Record<string, string> }).chartImages;
        const summary = summarizeRespiratoryEndurancePayload(payloadWithoutCharts);
        const interpretation = buildEnduranceInterpretation(payloadWithoutCharts);

        return {
          summaryJson: {
            tlimSeconds: summary.tlimSeconds,
            tlimFormatted: summary.tlimFormatted,
            targetResistance: summary.targetResistance,
            breathsAfterWarmup: summary.breathsAfterWarmup,
            blockCount: summary.blockCount,
            flowReduction: summary.flowReduction,
            volumeReduction: summary.volumeReduction,
            workReduction: summary.workReduction,
            severity: interpretation.title,
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg: sharedContext.patientContext.weightKg,
            patientHeightCm: sharedContext.patientContext.heightCm,
          },
          payloadJson: payloadWithoutCharts,
          assetsJson: {
            chartImages,
          },
        };
      }
      case 'PEAK_COUGH_FLOW':
      case 'CV_POSTURAL':
      case 'SNIP': {
        const patientContext = resolvePatientContextForResult(rawResult, sharedContext.patientContext, testKey);
        const withContext = { ...rawResult, patientContext, professionalContext, finishedAt: rawResult.finishedAt || new Date().toISOString() };
        const normalized = testKey === 'PEAK_COUGH_FLOW'
          ? normalizePeakCoughFlowPayload(withContext)
          : testKey === 'CV_POSTURAL' ? normalizePosturalVitalCapacityPayload(withContext) : normalizeSnipPayload(withContext);
        if (!normalized) throw new Error('Os dados da avaliação estão inválidos.');
        const summary = testKey === 'PEAK_COUGH_FLOW'
          ? summarizePeakCoughFlowPayload(normalized)
          : testKey === 'CV_POSTURAL' ? summarizePosturalVitalCapacityPayload(normalized) : summarizeSnipPayload(normalized);
        return {
          summaryJson: { ...summary, patientAgeYears: testKey === 'PEAK_COUGH_FLOW' ? normalizePeakCoughFlowPayload(normalized)?.ageYears : patientContext.ageYears, patientSex: patientContext.sex },
          payloadJson: normalized,
          assetsJson: {},
        };
      }
      case 'PEAK_FLOW': {
        const rawPeakFlow = rawResult as PeakFlowResultPayload;
        const peakFlowPatientContext = rawPeakFlow.patientContext ?? sharedContext.patientContext;
        const peakFlowResult =
          normalizePeakFlowPayload({
            ...rawPeakFlow,
            patientContext: peakFlowPatientContext,
            professionalContext,
          }) ?? rawPeakFlow;
        const summary = summarizePeakFlowPayload(peakFlowResult);
        const interpretation =
          typeof peakFlowResult.interpretation === 'string' && peakFlowResult.interpretation.trim().length > 0
            ? peakFlowResult.interpretation.trim()
            : summary.interpretation;

        return {
          summaryJson: {
            bestPreFlow: summary.bestPreFlow,
            bestPostFlow: summary.bestPostFlow,
            preAttemptCount: summary.preAttemptCount,
            postAttemptCount: summary.postAttemptCount,
            referenceMode: summary.referenceMode,
            referenceValue: summary.referenceValue,
            referenceLabel: summary.referenceLabel,
            referenceSourceKey: summary.referenceSourceKey,
            referenceSourceLabel: summary.referenceSourceLabel,
            referencePopulationNote: summary.referencePopulationNote,
            percentReferencePre: summary.referencePercentPre,
            percentReferencePost: summary.referencePercentPost,
            zone: summary.zone,
            zoneLabel: summary.zoneLabel,
            bronchodilatorUsed: summary.bronchodilatorUsed,
            bronchodilatorResponseSupported: summary.bronchodilatorResponseSupported,
            bronchodilatorDeltaAbsolute: summary.bronchodilatorDeltaAbsolute,
            bronchodilatorDeltaPercent: summary.bronchodilatorDeltaPercent,
            symptomCount: summary.symptomCount,
            dangerSignCount: summary.dangerSignCount,
            classification: summary.severity,
            severity: summary.severity,
            actionLabel: summary.actionLabel,
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg: sharedContext.patientContext.weightKg,
            patientHeightCm: sharedContext.patientContext.heightCm,
          },
          payloadJson: {
            ...peakFlowResult,
            interpretation,
            patientContext: peakFlowPatientContext,
            professionalContext,
          },
          assetsJson: {},
        };
      }
      case 'TUG': {
        const rawTugResult = rawResult as TugResultPayload;
        const tugPatientContext = rawTugResult.patientContext ?? sharedContext.patientContext;
        const tugResult =
          normalizeTugPayload({
            ...rawTugResult,
            patientContext: tugPatientContext,
          }) ?? rawTugResult;
        const summary = summarizeTugResult(tugResult);
        const interpretation =
          typeof tugResult.interpretation === 'string' && tugResult.interpretation.trim().length > 0
            ? tugResult.interpretation.trim()
            : summary.interpretation;
        const referenceComparison = summary.referenceComparison;

        return {
          summaryJson: {
            attemptCount: summary.attemptCount,
            workflowStatus: summary.workflowStatus,
            bestAttempt: summary.bestAttempt,
            timeSeconds: summary.bestTimeSeconds,
            classification: summary.severity,
            severity: summary.severity,
            completed: summary.completed,
            walkingAid: summary.walkingAidLabel,
            physicalAssistance: summary.physicalAssistance,
            familiarizationPerformed: summary.familiarizationPerformed,
            comparableToReference: summary.comparableToReference,
            referenceStatus: summary.referenceStatus,
            predictedTimeSeconds: referenceComparison?.predictedTimeSeconds ?? null,
            upperLimitSeconds: referenceComparison?.upperLimitSeconds ?? null,
            percentPredicted: referenceComparison?.percentPredicted ?? null,
            activeClinicalFlags: summary.clinicalFlags.filter((flag) => flag.active).map((flag) => flag.key),
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg:
              typeof tugPatientContext.weightKg === 'number' ? tugPatientContext.weightKg : sharedContext.patientContext.weightKg,
            patientHeightCm:
              typeof tugPatientContext.heightCm === 'number' ? tugPatientContext.heightCm : sharedContext.patientContext.heightCm,
          },
          payloadJson: {
            ...tugResult,
            timeSeconds: summary.bestTimeSeconds,
            walkingAidLabel: summary.walkingAidLabel,
            interpretation,
            classification: summary.severity,
            referenceComparison,
            clinicalFlags: summary.clinicalFlags,
            referenceNote: tugResult.referenceNote || TUG_REFERENCE_NOTE,
            patientContext: tugPatientContext,
            professionalContext,
          },
          assetsJson: {},
        };
      }
      case '4MGS': {
        const rawGaitSpeedResult = rawResult as GaitSpeedResultPayload;
        const gaitSpeedPatientContext = rawGaitSpeedResult.patientContext ?? sharedContext.patientContext;
        const gaitSpeedResult =
          normalizeGaitSpeedPayload({
            ...rawGaitSpeedResult,
            patientContext: gaitSpeedPatientContext,
          }) ?? rawGaitSpeedResult;
        const summary = summarizeGaitSpeedResult(gaitSpeedResult);
        const interpretation =
          typeof gaitSpeedResult.interpretation === 'string' && gaitSpeedResult.interpretation.trim().length > 0
            ? gaitSpeedResult.interpretation.trim()
            : summary.interpretation;
        const referenceComparison = summary.referenceComparison;

        return {
          summaryJson: {
            attemptCount: summary.attemptCount,
            bestAttempt: summary.bestAttempt,
            timeSeconds: summary.bestTimeSeconds,
            speedMs: summary.bestSpeedMs,
            classification: summary.severity,
            severity: summary.severity,
            completed: summary.completed,
            walkingAid: summary.walkingAidLabel,
            physicalAssistance: summary.physicalAssistance,
            oxygenUsed: summary.oxygenUsed,
            comparableToReference: summary.comparableToReference,
            referenceStatus: summary.referenceStatus,
            predictedSpeedMs: referenceComparison?.predictedSpeedMs ?? null,
            lowerLimitSpeedMs: referenceComparison?.lowerLimitSpeedMs ?? null,
            percentPredicted: referenceComparison?.percentPredicted ?? null,
            activeClinicalFlags: summary.clinicalFlags.filter((flag) => flag.active).map((flag) => flag.key),
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg:
              typeof gaitSpeedPatientContext.weightKg === 'number'
                ? gaitSpeedPatientContext.weightKg
                : sharedContext.patientContext.weightKg,
            patientHeightCm:
              typeof gaitSpeedPatientContext.heightCm === 'number'
                ? gaitSpeedPatientContext.heightCm
                : sharedContext.patientContext.heightCm,
          },
          payloadJson: {
            ...gaitSpeedResult,
            timeSeconds: summary.bestTimeSeconds,
            speedMs: summary.bestSpeedMs,
            walkingAidLabel: summary.walkingAidLabel,
            interpretation,
            classification: summary.severity,
            referenceComparison,
            clinicalFlags: summary.clinicalFlags,
            referenceNote: gaitSpeedResult.referenceNote || GAIT_SPEED_REFERENCE_NOTE,
            patientContext: gaitSpeedPatientContext,
            professionalContext,
          },
          assetsJson: {},
        };
      }
      case '5xSTS': {
        const stsResult = normalizeFiveSTSPayload(rawResult as FiveSTSResultPayload) ?? (rawResult as FiveSTSResultPayload);
        const summary = summarizeStsResult('5xSTS', stsResult);

        return {
          summaryJson: {
            best: summary.best,
            attemptCount: summary.attemptCount,
            chairHeightCm: summary.chairHeightCm,
            usedArms: summary.usedArms,
            workflowStatus: summary.workflowStatus,
            classification: summary.severity,
            severity: summary.severity,
            interpretation: summary.interpretation,
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg: sharedContext.patientContext.weightKg,
            patientHeightCm: sharedContext.patientContext.heightCm,
          },
          payloadJson: {
            ...stsResult,
            workflowStatus: 'completed',
            patientContext: sharedContext.patientContext,
            professionalContext,
            finishedAt: stsResult.finishedAt || new Date().toISOString(),
          },
          assetsJson: {},
        };
      }
      case 'STS_30S': {
        const stsResult =
          normalizeStsRepetitionPayload('STS_30S', rawResult as STS30sResultPayload) ?? (rawResult as STS30sResultPayload);
        const summary = summarizeStsResult('STS_30S', stsResult);

        return {
          summaryJson: {
            best: summary.best,
            attemptCount: summary.attemptCount,
            workflowStatus: summary.workflowStatus,
            restIntervalSeconds: summary.restIntervalSeconds,
            chairHeightCm: summary.chairHeightCm,
            classification: summary.severity,
            severity: summary.severity,
            interpretation: summary.interpretation,
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg: sharedContext.patientContext.weightKg,
            patientHeightCm: sharedContext.patientContext.heightCm,
          },
          payloadJson: {
            ...stsResult,
            patientContext: sharedContext.patientContext,
            professionalContext,
            finishedAt: stsResult.finishedAt || new Date().toISOString(),
          },
          assetsJson: {},
        };
      }
      case 'STS_1MIN': {
        const stsResult =
          normalizeStsRepetitionPayload('STS_1MIN', rawResult as STS1MinResultPayload) ?? (rawResult as STS1MinResultPayload);
        const summary = summarizeStsResult('STS_1MIN', stsResult);

        return {
          summaryJson: {
            best: summary.best,
            attemptCount: summary.attemptCount,
            workflowStatus: summary.workflowStatus,
            restIntervalSeconds: summary.restIntervalSeconds,
            chairHeightCm: summary.chairHeightCm,
            classification: summary.severity,
            severity: summary.severity,
            interpretation: summary.interpretation,
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg: sharedContext.patientContext.weightKg,
            patientHeightCm: sharedContext.patientContext.heightCm,
          },
          payloadJson: {
            ...stsResult,
            patientContext: sharedContext.patientContext,
            professionalContext,
            finishedAt: stsResult.finishedAt || new Date().toISOString(),
          },
          assetsJson: {},
        };
      }
      case 'CAT': {
        const catResult = rawResult as CATResultPayload;
        const total =
          typeof catResult.total === 'number'
            ? catResult.total
            : catResult.answers.reduce((sum, value) => sum + value, 0);

        return {
          summaryJson: {
            total,
            severity: getCATSeverityLabel(total),
            answeredItems: catResult.answers.length,
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg: sharedContext.patientContext.weightKg,
            patientHeightCm: sharedContext.patientContext.heightCm,
          },
          payloadJson: {
            answers: catResult.answers,
            total,
            finishedAt: catResult.finishedAt || new Date().toISOString(),
            patientContext: sharedContext.patientContext,
            professionalContext,
          },
          assetsJson: {},
        };
      }
      case 'ACT': {
        const actResult = rawResult as ACTResultPayload;
        const total =
          typeof actResult.total === 'number'
            ? actResult.total
            : actResult.answers.reduce((sum, value) => sum + (typeof value === 'number' ? value : 0), 0);
        const classification = getACTSeverityLabel(total);

        return {
          summaryJson: {
            total,
            classification,
            severity: classification,
            answeredItems: actResult.answers.length,
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg: sharedContext.patientContext.weightKg,
            patientHeightCm: sharedContext.patientContext.heightCm,
          },
          payloadJson: {
            ...actResult,
            total,
            classification,
            patientContext: sharedContext.patientContext,
            professionalContext,
          },
          assetsJson: {},
        };
      }
      case 'mMRC': {
        const mmrcResult = rawResult as MMRCResultPayload;
        return {
          summaryJson: {
            grade: mmrcResult.grade,
            severity: getMMRCSeverityLabel(mmrcResult.grade),
            description: mmrcResult.description,
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg: sharedContext.patientContext.weightKg,
            patientHeightCm: sharedContext.patientContext.heightCm,
          },
          payloadJson: {
            grade: mmrcResult.grade,
            description: mmrcResult.description,
            finishedAt: mmrcResult.finishedAt || new Date().toISOString(),
            patientContext: sharedContext.patientContext,
            professionalContext,
          },
          assetsJson: {},
        };
      }
      case 'HADS': {
        const hadsResult = rawResult as HADSResultPayload;
        const overallSeverity = getHADSOverallSeverityLabel(hadsResult.anxietyScore, hadsResult.depressionScore);

        return {
          summaryJson: {
            anxietyScore: hadsResult.anxietyScore,
            depressionScore: hadsResult.depressionScore,
            anxietyClassification:
              hadsResult.anxietyScore <= 7 ? 'Normal' : hadsResult.anxietyScore <= 10 ? 'Limítrofe' : 'Anormal',
            depressionClassification:
              hadsResult.depressionScore <= 7 ? 'Normal' : hadsResult.depressionScore <= 10 ? 'Limítrofe' : 'Anormal',
            severity: overallSeverity,
            answeredItems: hadsResult.answers.length,
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg: sharedContext.patientContext.weightKg,
            patientHeightCm: sharedContext.patientContext.heightCm,
          },
          payloadJson: {
            answers: hadsResult.answers,
            anxiety: hadsResult.anxiety,
            depression: hadsResult.depression,
            anxietyScore: hadsResult.anxietyScore,
            depressionScore: hadsResult.depressionScore,
            finishedAt: hadsResult.finishedAt || new Date().toISOString(),
            patientContext: sharedContext.patientContext,
            professionalContext,
          },
          assetsJson: {},
        };
      }
      case 'PSQI': {
        const psqiResult = rawResult as PSQIResultPayload;
        const severity = getPSQISeverityLabel(psqiResult.total);

        return {
          summaryJson: {
            total: psqiResult.total,
            severity,
            classification: severity,
            sleepEfficiencyPercent: psqiResult.sleepEfficiencyPercent,
            timeInBedHours: psqiResult.timeInBedHours,
            sleepLatencyMinutes: psqiResult.sleepLatencyMinutes,
            componentScores: psqiResult.components,
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg: sharedContext.patientContext.weightKg,
            patientHeightCm: sharedContext.patientContext.heightCm,
          },
          payloadJson: {
            ...psqiResult,
            patientContext: sharedContext.patientContext,
            professionalContext,
          },
          assetsJson: {},
        };
      }
      case 'AQLQ': {
        const aqlqResult = rawResult as AQLQResultPayload;
        const severity = getAQLQSeverityLabel(aqlqResult.total);
        return {
          summaryJson: {
            total: aqlqResult.total,
            severity,
            answeredCount: aqlqResult.answeredCount,
            symptomsScore: aqlqResult.domainScores.symptoms.score,
            emotionalFunctionScore: aqlqResult.domainScores.emotionalFunction.score,
            activityLimitationScore: aqlqResult.domainScores.activityLimitation.score,
            environmentalStimuliScore: aqlqResult.domainScores.environmentalStimuli.score,
            sourceMissingItems: aqlqResult.sourceMissingItems,
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg: sharedContext.patientContext.weightKg,
            patientHeightCm: sharedContext.patientContext.heightCm,
          },
          payloadJson: {
            ...aqlqResult,
            patientContext: sharedContext.patientContext,
            professionalContext,
          },
          assetsJson: {},
        };
      }
      case 'LCADL': {
        const lcadlResult = rawResult as LCADLResultPayload;
        const severity = getLCADLSeverityLabel(lcadlResult.percentage);
        return {
          summaryJson: {
            totalScore: lcadlResult.totalScore,
            maxScore: lcadlResult.maxScore,
            percentage: lcadlResult.percentage,
            severity,
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg: sharedContext.patientContext.weightKg,
            patientHeightCm: sharedContext.patientContext.heightCm,
          },
          payloadJson: {
            ...lcadlResult,
            patientContext: sharedContext.patientContext,
            professionalContext,
          },
          assetsJson: {},
        };
      }
      case 'MLHFQ': {
        const mlhfqResult = rawResult as MLHFQResultPayload;
        const severity = getMLHFQSeverityLabel(mlhfqResult.total);
        return {
          summaryJson: {
            total: mlhfqResult.total,
            maxScore: mlhfqResult.maxScore,
            physicalScore: mlhfqResult.physical.score,
            emotionalScore: mlhfqResult.emotional.score,
            otherScore: mlhfqResult.other.score,
            severity,
            answeredItems: mlhfqResult.answers.length,
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg: sharedContext.patientContext.weightKg,
            patientHeightCm: sharedContext.patientContext.heightCm,
          },
          payloadJson: {
            ...mlhfqResult,
            patientContext: sharedContext.patientContext,
            professionalContext,
          },
          assetsJson: {},
        };
      }
      case 'PFSDQ-M': {
        const pfsdqResult = rawResult as PFSDQMResultPayload;
        const severity = getPFSDQSeverityLabel(pfsdqResult.total);
        return {
          summaryJson: {
            total: pfsdqResult.total,
            dyspneaScore: pfsdqResult.dyspnea.score,
            fatigueScore: pfsdqResult.fatigue.score,
            changeScore: pfsdqResult.change.score,
            dyspneaAnswered: pfsdqResult.manualCounts.dyspneaAnswered,
            fatigueAnswered: pfsdqResult.manualCounts.fatigueAnswered,
            changeAnswered: pfsdqResult.manualCounts.changeAnswered,
            severity,
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg: sharedContext.patientContext.weightKg,
            patientHeightCm: sharedContext.patientContext.heightCm,
          },
          payloadJson: {
            ...pfsdqResult,
            patientContext: sharedContext.patientContext,
            professionalContext,
          },
          assetsJson: {},
        };
      }
      case 'SAQ': {
        const saqResult = rawResult as SAQResultPayload;
        const overallScore = getSAQOverallScore(saqResult.domainScores);
        const severity = getSAQSeverityLabel(overallScore);
        return {
          summaryJson: {
            overallScore,
            severity,
            physicalLimitation: saqResult.domainScores.physicalLimitation,
            anginaStability: saqResult.domainScores.anginaStability,
            anginaFrequency: saqResult.domainScores.anginaFrequency,
            treatmentSatisfaction: saqResult.domainScores.treatmentSatisfaction,
            qualityOfLife: saqResult.domainScores.qualityOfLife,
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg: sharedContext.patientContext.weightKg,
            patientHeightCm: sharedContext.patientContext.heightCm,
          },
          payloadJson: {
            ...saqResult,
            patientContext: sharedContext.patientContext,
            professionalContext,
          },
          assetsJson: {},
        };
      }
      case 'SGRQ': {
        const sgrqResult = rawResult as SGRQResultPayload;
        const severity = getSGRQSeverityLabel(sgrqResult.total);
        return {
          summaryJson: {
            total: sgrqResult.total,
            symptoms: sgrqResult.symptoms,
            activity: sgrqResult.activity,
            impact: sgrqResult.impact,
            severity,
            patientAgeYears: sharedContext.patientAgeAtAssessment,
            patientSex: sharedContext.patientContext.sex,
            patientWeightKg: sharedContext.patientContext.weightKg,
            patientHeightCm: sharedContext.patientContext.heightCm,
          },
          payloadJson: {
            ...sgrqResult,
            patientContext: sharedContext.patientContext,
            professionalContext,
          },
          assetsJson: {},
        };
      }
    }
  };

  const buildAssessmentResultInput = (
    sessionId: string,
    testKey: AssessmentV2TestKey,
    rawResult: any,
    sharedContext: Awaited<ReturnType<typeof buildAssessmentSharedContext>>,
    groupIndex: number,
    groupTotal: number,
    status: AssessmentResultV2['status'],
  ) => {
    if (!user || !clientId || !selectedPatient) {
      throw new Error('Não foi possível identificar tenant, usuário ou paciente para salvar a avaliação.');
    }

    if (testKey === 'INITIAL_ASSESSMENT' && status === 'completed' && !isInitialAssessmentComplete(rawResult)) {
      throw new Error('Conclua o motivo e a síntese profissional antes de finalizar a avaliação.');
    }
    const content = buildResultContent(testKey, rawResult, sharedContext);
    const testVersion = getCurrentAssessmentTestVersion(testKey);
    const effectivePatientContext = resolvePatientContextForResult(rawResult, sharedContext.patientContext, testKey);
    const baseSummaryJson =
      content.summaryJson && typeof content.summaryJson === 'object' && !Array.isArray(content.summaryJson)
        ? {
            ...content.summaryJson,
            patientAgeYears: testKey === 'PEAK_COUGH_FLOW' ? effectivePatientContext.ageYears : sharedContext.patientAgeAtAssessment,
            patientSex: effectivePatientContext.sex,
            patientWeightKg: effectivePatientContext.weightKg,
            patientHeightCm: effectivePatientContext.heightCm,
          }
        : content.summaryJson;
    const basePayloadJson =
      content.payloadJson && typeof content.payloadJson === 'object' && !Array.isArray(content.payloadJson)
        ? {
            ...content.payloadJson,
            patientContext: effectivePatientContext,
          }
        : content.payloadJson;
    const clinicalReference = buildClinicalReferenceDecision({
      testKey,
      testVersion,
      payload: basePayloadJson,
      evaluatedAt: sharedContext.assessmentDateIso,
    });
    const summaryJson = {
      ...baseSummaryJson,
      instrumentVersion: clinicalReference.instrumentVersion,
      clinicalReferenceEngineVersion: clinicalReference.engineVersion,
    };
    const payloadJson = {
      ...basePayloadJson,
      instrumentVersion: clinicalReference.instrumentVersion,
      clinicalReference,
    };

    return {
      sessionId,
      clientId,
      patientId: selectedPatient.id,
      testKey,
      testVersion,
      assessmentDate: sharedContext.assessmentDateIso,
      groupIndex,
      groupTotal,
      status,
      notes: form.notes || null,
      patientNameSnapshot: selectedPatient.personalData?.name || selectedPatient.patientFullName || 'Paciente',
      patientMedicalRecordSnapshot: getPreferredPatientIdentifier(selectedPatient),
      patientBirthDateSnapshot: effectivePatientContext.birthDate,
      patientSexSnapshot: effectivePatientContext.sex,
      patientWeightSnapshot: effectivePatientContext.weightKg,
      patientHeightSnapshot: effectivePatientContext.heightCm,
      professionalUserId: user.userId,
      professionalNameSnapshot: user.userDisplayName || 'Profissional',
      professionalRegistrationSnapshot: sharedContext.professionalRegistration,
      professionalRoleSnapshot: sharedContext.professionalRole,
      summaryJson,
      payloadJson,
      assetsJson: content.assetsJson,
      legacySourceRecordId: null,
      createdByUserId: user.userId,
    };
  };

  const closeRunner = () => {
    resetRunnerState();
  };

  const attachSessionEvidenceToClinicalAssessment = async (sessionResults: AssessmentResultV2[]): Promise<AssessmentResultV2 | null> => {
    const clinical = sessionResults.find((result) => result.testKey === 'INITIAL_ASSESSMENT');
    if (!clinical || !clientId) return null;
    const payload = normalizeInitialAssessmentPayload(clinical.payloadJson);
    if (!payload) return null;
    const selected = new Map(payload.selectedResults.map((source) => [source.resultId, source]));
    sessionResults.filter((result) => result.resultId !== clinical.resultId && result.status === 'completed'
      && !isAssessmentV2ReportKey(result.testKey)).forEach((result) => selected.set(result.resultId, buildAssessmentReportSourceSnapshot(result)));
    return AssessmentResultsV2Service.update(clinical.resultId, clientId, {
      payloadJson: { ...clinical.payloadJson, selectedResults: [...selected.values()] },
    });
  };

  const handleFinishRunner = async () => {
    if (draftStepSaveInFlightRef.current) return;
    if (!user || !clientId || !selectedPatient) {
      toast.error('Não foi possível identificar tenant, usuário ou paciente para finalizar a avaliação.');
      return;
    }

    const blockReason = getPatientNewCareBlockReason(selectedPatient);
    if (blockReason) {
      toast.error(
        blockReason === 'clinical_inactive'
          ? 'Paciente está inativo assistencialmente e não pode receber novas avaliações.'
          : 'Paciente inativado ou arquivado não pode receber novas avaliações.',
      );
      return;
    }

    const testEntries = runnableSelectedTests
      .map((testKey) => [testKey, runnerResults[testKey]] as const)
      .filter((entry): entry is readonly [AssessmentV2TestKey, any] => isRunnerTestPayloadCompleted(entry[0], entry[1]));

    if (!testEntries.length || testEntries.length !== runnableSelectedTests.length) {
      toast.error('Conclua todos os testes selecionados antes de finalizar.');
      return;
    }

    let createdSessionId: string | null = null;

    try {
      setSaving(true);
      const sharedContext = await buildAssessmentSharedContext();

      for (const [testKey, rawResult] of testEntries) {
        if (testKey === 'INITIAL_ASSESSMENT') await InitialAssessmentIntegrityService.assertSourcesCurrent(rawResult, { clientId, patientId: selectedPatient.id });
      }

      const session = await AssessmentSessionsV2Service.create({
        clientId,
        patientId: selectedPatient.id,
        sessionDate: sharedContext.assessmentDateIso,
        notes: form.notes || null,
        selectedTests: runnableSelectedTests,
        status: 'completed',
        createdByUserId: user.userId,
      });
      createdSessionId = session.sessionId;

      const createdResults: AssessmentResultV2[] = [];
      for (const [index, [testKey, rawResult]] of testEntries.entries()) {
        const createdResult = await AssessmentResultsV2Service.create(
          buildAssessmentResultInput(
            session.sessionId,
            testKey,
            rawResult,
            sharedContext,
            index + 1,
            testEntries.length,
            'completed',
          ),
        );
        createdResults.push(createdResult);
      }
      const clinicalResult = await attachSessionEvidenceToClinicalAssessment(createdResults);

      toast.success(
        testEntries.length > 1
          ? `Avaliação concluída com ${testEntries.length} testes salvos individualmente.`
          : `${getAssessmentV2Label(testEntries[0][0])} salvo com sucesso.`,
      );
      closeRunner();
      setShowCreateModal(false);
      resetForm();
      await Promise.all([loadDashboardData(), loadDraftSessions()]);
      setSelectedResult(clinicalResult ?? createdResults[createdResults.length - 1] ?? null);
      returnToEvolutionAfterCompletion(session.sessionId, selectedPatient.id);
    } catch (error: any) {
      console.error('Erro ao finalizar avaliação:', error);
      if (createdSessionId) {
        try {
          await AssessmentSessionsV2Service.delete(createdSessionId, clientId);
        } catch (cleanupError) {
          console.error('Erro ao desfazer sessão V2 após falha de finalização:', cleanupError);
        }
      }
      toast.error(`Erro ao finalizar avaliação: ${error?.message || 'falha desconhecida'}`);
    } finally {
      setSaving(false);
    }
  };

  const openDraftSession = async (draft: DraftSessionSummary) => {
    const selectedTests = normalizeSelectedTests(draft.session.selectedTests);
    if (!selectedTests.length) {
      toast.error('Este rascunho não possui testes válidos para continuar.');
      return;
    }

    const patient = patients.find((item) => item.id === draft.session.patientId);
    const blockReason = getPatientNewCareBlockReason(patient);
    if (patient && blockReason) {
      toast.error(
        blockReason === 'clinical_inactive'
          ? 'Paciente está inativo assistencialmente e não pode receber novas avaliações.'
          : 'Paciente inativado ou arquivado não pode receber novas avaliações.',
      );
      return;
    }

    const savedResults = draft.results.reduce((accumulator, result) => {
      accumulator[result.testKey as AssessmentV2TestKey] = result;
      return accumulator;
    }, {} as Partial<Record<AssessmentV2TestKey, AssessmentResultV2>>);

    const savedPayloads = draft.results.reduce((accumulator, result) => {
      accumulator[result.testKey as AssessmentV2TestKey] = resolveRunnerPayload(result.testKey as AssessmentV2TestKey, result);
      return accumulator;
    }, {} as Partial<Record<AssessmentV2TestKey, any>>);

    setForm({
      patientId: draft.session.patientId,
      datetime: toLocalDateTimeInput(draft.session.sessionDate),
      notes: draft.session.notes || '',
      selectedTests,
    });
    setRunnerSession(draft.session);
    setRunnerSavedResults(savedResults);
    setRunnerResults(savedPayloads);
    setRunnerActiveTest(getNextPendingTest(selectedTests, savedPayloads, null));
    setShowCreateModal(false);
    setShowRunnerModal(true);
  };

  const handleDiscardDraft = async (draft: DraftSessionSummary) => {
    if (!clientId) return;

    try {
      setSaving(true);
      await AssessmentSessionsV2Service.delete(draft.session.sessionId, clientId);
      toast.success('Rascunho descartado com sucesso.');
      await Promise.all([loadDashboardData(), loadDraftSessions()]);
    } catch (error: any) {
      console.error('Erro ao descartar rascunho da V2:', error);
      toast.error(`Erro ao descartar rascunho: ${error?.message || 'falha desconhecida'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveRunnerTest = async (testKey: AssessmentV2TestKey) => {
    if (saving) return;

    if (runnableSelectedTests.length <= 1) {
      toast.error('Mantenha pelo menos um teste selecionado para a avaliação.');
      return;
    }

    const nextSelectedTests = runnableSelectedTests.filter((selectedTest) => selectedTest !== testKey);
    const removedSavedResult = runnerSavedResults[testKey];
    const nextRunnerResults = { ...runnerResults };
    delete nextRunnerResults[testKey];

    setForm((current) => ({
      ...current,
      selectedTests: nextSelectedTests,
    }));
    setRunnerResults((current) => {
      const next = { ...current };
      delete next[testKey];
      return next;
    });
    setRunnerSavedResults((current) => {
      const next = { ...current };
      delete next[testKey];
      return next;
    });
    setRunnerActiveTest((currentActiveTest) => {
      if (currentActiveTest && nextSelectedTests.includes(currentActiveTest)) {
        return currentActiveTest;
      }

      return getNextPendingTest(nextSelectedTests, nextRunnerResults, null);
    });

    if (!runnerSession || !clientId) {
      toast.info(`${getAssessmentV2Label(testKey)} removido da avaliação.`);
      return;
    }

    try {
      setSaving(true);
      const updatedSession = await AssessmentSessionsV2Service.update(runnerSession.sessionId, clientId, {
        selectedTests: nextSelectedTests,
        status: 'draft',
      });
      if (removedSavedResult) {
        await AssessmentResultsV2Service.delete(removedSavedResult.resultId, clientId);
      }

      setRunnerSession(updatedSession);
      await loadDraftSessions();
      toast.info(`${getAssessmentV2Label(testKey)} removido do rascunho.`);
    } catch (error: any) {
      console.error(`Erro ao remover ${testKey} do runner V2:`, error);
      toast.error(`Erro ao remover ${getAssessmentV2Label(testKey)}: ${error?.message || 'falha desconhecida'}`);
      const [refreshedSession, refreshedResults] = await Promise.all([
        AssessmentSessionsV2Service.getById(runnerSession.sessionId, clientId),
        AssessmentResultsV2Service.listBySessionId(clientId, runnerSession.sessionId),
      ]);
      await openDraftSession({
        session: refreshedSession,
        results: refreshedResults,
        patientName: selectedPatient?.personalData?.name || selectedPatient?.patientFullName || 'Paciente',
      });
    } finally {
      setSaving(false);
    }
  };

  const persistDraftStep = async (
    testKey: AssessmentV2TestKey,
    rawResult: any,
    options?: {
      advanceToNext?: boolean;
      successMessage?: string;
    },
  ): Promise<boolean> => {
    if (!user || !clientId || !selectedPatient) {
      toast.error('Não foi possível identificar tenant, usuário ou paciente para salvar o rascunho.');
      return false;
    }
    if (activeClientIdRef.current !== clientId) return false;
    if (draftStepSaveInFlightRef.current) return false;
    draftStepSaveInFlightRef.current = true;

    try {
      setSaving(true);
      const sharedContext = await buildAssessmentSharedContext();
      const selectedTests = runnableSelectedTests;
      if (activeClientIdRef.current !== clientId) return false;

      let session = runnerSession;
      if (!session) {
        session = await AssessmentSessionsV2Service.create({
          clientId,
          patientId: selectedPatient.id,
          sessionDate: sharedContext.assessmentDateIso,
          notes: form.notes || null,
          selectedTests,
          status: 'draft',
          createdByUserId: user.userId,
        });
        if (activeClientIdRef.current !== clientId) return false;
        setRunnerSession(session);
      } else {
        session = await AssessmentSessionsV2Service.update(session.sessionId, clientId, {
          sessionDate: sharedContext.assessmentDateIso,
          notes: form.notes || null,
          selectedTests,
          status: 'draft',
        });
        if (activeClientIdRef.current !== clientId) return false;
        setRunnerSession(session);
      }

      const groupIndex = selectedTests.indexOf(testKey) + 1;
      const savedResult = await AssessmentResultsV2Service.upsert(
        buildAssessmentResultInput(
          session.sessionId,
          testKey,
          rawResult,
          sharedContext,
          groupIndex > 0 ? groupIndex : 1,
          selectedTests.length || 1,
          'draft',
        ),
      );

      if (activeClientIdRef.current !== clientId) return false;
      setRunnerSavedResults((current) => ({
        ...current,
        [testKey]: savedResult,
      }));
      setRunnerResults((current) => {
        const next = {
          ...current,
          [testKey]: rawResult,
        };

        if (options?.advanceToNext === false) {
          setRunnerActiveTest(testKey);
          return next;
        }

        setRunnerActiveTest(getNextPendingTest(selectedTests, next, testKey));
        return next;
      });
      await loadDraftSessions();

      const nextState = { ...runnerResults, [testKey]: rawResult };
      const nextTest = getNextPendingTest(selectedTests, nextState, testKey);
      if (options?.successMessage) {
        toast.success(options.successMessage);
      } else if (options?.advanceToNext === false) {
        toast.success(`${getAssessmentV2Label(testKey)} salvo como rascunho parcial. Você pode continuar esta avaliação depois.`);
      } else if (nextTest) {
        toast.success(`${getAssessmentV2Label(testKey)} salvo como rascunho. Continue para ${getAssessmentV2Label(nextTest)}.`);
      } else {
        toast.success(`${getAssessmentV2Label(testKey)} salvo como rascunho. Todos os testes já foram preenchidos.`);
      }
      return true;
    } catch (error: any) {
      console.error(`Erro ao salvar ${testKey} como rascunho na V2:`, error);
      toast.error(`Erro ao salvar ${getAssessmentV2Label(testKey)} como rascunho: ${error?.message || 'falha desconhecida'}`);
      return false;
    } finally {
      setSaving(false);
      draftStepSaveInFlightRef.current = false;
    }
  };

  const finalizeDraftSession = async () => {
    if (draftStepSaveInFlightRef.current) return;
    if (!clientId || !runnerSession) {
      await handleFinishRunner();
      return;
    }

    const testEntries = runnableSelectedTests
      .map((testKey) => runnerSavedResults[testKey])
      .filter((result): result is AssessmentResultV2 =>
        !!result &&
        isRunnerTestPayloadCompleted(result.testKey as AssessmentV2TestKey, resolveRunnerPayload(result.testKey as AssessmentV2TestKey, result)),
      );

    if (!testEntries.length || testEntries.length !== runnableSelectedTests.length) {
      toast.error('Conclua todos os testes selecionados antes de finalizar.');
      return;
    }

    try {
      setSaving(true);
      for (const result of testEntries) {
        if (result.testKey === 'INITIAL_ASSESSMENT') await InitialAssessmentIntegrityService.assertSourcesCurrent(result.payloadJson, { clientId, patientId: result.patientId });
      }
      const { completedResults, clinicalResult } = await completeAssessmentDraft({
        results: testEntries,
        completeResult: (result, index) => AssessmentResultsV2Service.setStatus(result.resultId, clientId, 'completed', {
          groupIndex: index + 1,
          groupTotal: testEntries.length,
        }),
        attachEvidence: attachSessionEvidenceToClinicalAssessment,
        completeSession: async () => { await AssessmentSessionsV2Service.update(runnerSession.sessionId, clientId, {
          sessionDate: new Date(form.datetime).toISOString(), notes: form.notes || null,
          selectedTests: runnableSelectedTests, status: 'completed',
        }); },
        restoreSession: async () => { await AssessmentSessionsV2Service.update(runnerSession.sessionId, clientId, { status: 'draft' }); },
        restoreResult: async (original) => {
          await AssessmentResultsV2Service.setStatus(original.resultId, clientId, original.status, { groupIndex: original.groupIndex, groupTotal: original.groupTotal });
          await AssessmentResultsV2Service.update(original.resultId, clientId, { payloadJson: original.payloadJson, summaryJson: original.summaryJson, assetsJson: original.assetsJson });
        },
      });

      toast.success(
        testEntries.length > 1
          ? `Avaliação concluída com ${testEntries.length} testes salvos individualmente.`
          : `${getAssessmentV2Label(testEntries[0].testKey)} salvo com sucesso.`,
      );
      closeRunner();
      setShowCreateModal(false);
      resetForm();
      await Promise.all([loadDashboardData(), loadDraftSessions()]);
      const lastCompleted = completedResults[completedResults.length - 1];
      setSelectedResult(clinicalResult ?? lastCompleted ?? testEntries[testEntries.length - 1] ?? null);
      returnToEvolutionAfterCompletion(runnerSession.sessionId, runnerSession.patientId);
    } catch (error: any) {
      console.error('Erro ao concluir rascunho da V2:', error);
      toast.error(`Erro ao concluir avaliação: ${error?.message || 'falha desconhecida'}`);
    } finally {
      setSaving(false);
    }
  };

  const renderRunnerTest = (testKey: AssessmentV2TestKey) => {
    const commonProps = {
      onCancel: closeRunner,
    };

    switch (testKey) {
      case 'INITIAL_ASSESSMENT':
        return (
          <InitialAssessmentTest
            {...commonProps}
            initial={getRunnerTestInitialValue('INITIAL_ASSESSMENT')}
            patientId={selectedPatient?.id}
            clientId={clientId}
            resultId={runnerSavedResults.INITIAL_ASSESSMENT?.resultId}
            sourceResults={initialAssessmentSources}
            sourceResultsLoading={initialAssessmentSourcesLoading}
            buildSourceSnapshot={buildAssessmentReportSourceSnapshot}
            initialAnamnesisRecordId={new URLSearchParams(location.search).get('anamnesisId') || undefined}
            patientContext={getRunnerPatientContext('INITIAL_ASSESSMENT')}
            onSaveDraft={async (result) => {
              const saved = await persistDraftStep('INITIAL_ASSESSMENT', result, { advanceToNext: false });
              if (!saved) throw new Error('Não foi possível manter o rascunho. Os registros já salvos no prontuário foram preservados; tente salvar a avaliação novamente.');
            }}
            onRequestTests={addPlannedAssessmentTests}
            onCreateAnamnesis={(input) => {
              if (!selectedPatient?.id) {
                return Promise.reject(new Error('Selecione um paciente para registrar a anamnese.'));
              }
              return createAnamnesisFromAssessment(selectedPatient.id, input);
            }}
            onNext={(result) => void persistDraftStep('INITIAL_ASSESSMENT', result)}
          />
        );
      case 'TC6':
        return (
          <TC6Test
            {...commonProps}
            initial={getRunnerTestInitialValue('TC6')}
            patientContext={getRunnerPatientContext('TC6')}
            onDraftAttempt={(result) =>
              persistDraftStep('TC6', result, {
                advanceToNext: false,
                successMessage: 'Primeira tentativa do TC6 salva como rascunho. Você pode encerrar com 1 tentativa ou iniciar a 2ª.',
              })
            }
            onNext={(result) => void persistDraftStep('TC6', result)}
          />
        );
      case 'ISWT':
        return (
          <ISWTTest
            {...commonProps}
            initial={getRunnerTestInitialValue('ISWT')}
            patientContext={getRunnerPatientContext('ISWT')}
            onNext={(result) => void persistDraftStep('ISWT', result)}
          />
        );
      case 'ESWT':
        return (
          <ESWTTest
            {...commonProps}
            initial={getRunnerTestInitialValue('ESWT')}
            patientContext={getRunnerPatientContext('ESWT')}
            iswtReference={getRunnerISWTReference()}
            onNext={(result) => void persistDraftStep('ESWT', result)}
          />
        );
      case '6MST':
        return (
          <MST6Test
            {...commonProps}
            initial={getRunnerTestInitialValue('6MST')}
            patientContext={getRunnerPatientContext('6MST')}
            onNext={(result) => void persistDraftStep('6MST', result)}
          />
        );
      case 'CHESTER':
        return (
          <ChesterStepTest
            {...commonProps}
            initial={getRunnerTestInitialValue('CHESTER')}
            patientContext={getRunnerPatientContext('CHESTER')}
            onNext={(result) => void persistDraftStep('CHESTER', result)}
          />
        );
      case 'HANDGRIP':
        return (
          <HandgripTest
            {...commonProps}
            initial={getRunnerTestInitialValue('HANDGRIP')}
            patientContext={getRunnerPatientContext('HANDGRIP')}
            onNext={(result) => void persistDraftStep('HANDGRIP', result)}
          />
        );
      case 'PERIPHERAL_MUSCLE_STRENGTH':
        return (
          <PeripheralMuscleStrengthTest
            {...commonProps}
            initial={getRunnerTestInitialValue('PERIPHERAL_MUSCLE_STRENGTH')}
            patientContext={getRunnerPatientContext('PERIPHERAL_MUSCLE_STRENGTH')}
            onNext={(result) => void persistDraftStep('PERIPHERAL_MUSCLE_STRENGTH', result)}
          />
        );
      case 'ONE_RM':
        return (
          <OneRMTest
            {...commonProps}
            initial={getRunnerTestInitialValue('ONE_RM')}
            patientContext={getRunnerPatientContext('ONE_RM')}
            onNext={(result) => void persistDraftStep('ONE_RM', result)}
          />
        );
      case 'PIMAX':
        return (
          <PimaxTest
            {...commonProps}
            initial={getRunnerTestInitialValue('PIMAX')}
            patientContext={getRunnerPatientContext('PIMAX')}
            onNext={(result) => void persistDraftStep('PIMAX', result)}
          />
        );
      case 'PEMAX':
        return (
          <PemaxTest
            {...commonProps}
            initial={getRunnerTestInitialValue('PEMAX')}
            patientContext={getRunnerPatientContext('PEMAX')}
            onNext={(result) => void persistDraftStep('PEMAX', result)}
          />
        );
      case 'S_INDEX':
        return (
          <SIndexTest
            {...commonProps}
            initial={getRunnerTestInitialValue('S_INDEX')}
            patientContext={getRunnerPatientContext('S_INDEX')}
            referencePimaxMax={getRunnerPimaxReference()}
            referenceHandgripKg={getRunnerHandgripReference()}
            onNext={(result) => void persistDraftStep('S_INDEX', result)}
          />
        );
      case 'RESP_ENDURANCE':
        return (
          <RespiratoryEnduranceTest
            {...commonProps}
            initial={getRunnerTestInitialValue('RESP_ENDURANCE')}
            patientContext={getRunnerPatientContext('RESP_ENDURANCE')}
            onNext={(result) => void persistDraftStep('RESP_ENDURANCE', result)}
          />
        );
      case 'PEAK_COUGH_FLOW':
        return <PeakCoughFlowTest {...commonProps} initial={getRunnerTestInitialValue('PEAK_COUGH_FLOW')} patientContext={getRunnerPatientContext('PEAK_COUGH_FLOW')} onNext={(result) => void persistDraftStep('PEAK_COUGH_FLOW', result)} />;
      case 'CV_POSTURAL':
        return <PosturalVitalCapacityTest {...commonProps} initial={getRunnerTestInitialValue('CV_POSTURAL')} patientContext={getRunnerPatientContext('CV_POSTURAL')} onNext={(result) => void persistDraftStep('CV_POSTURAL', result)} />;
      case 'SNIP':
        return <SnipTest {...commonProps} initial={getRunnerTestInitialValue('SNIP')} patientContext={getRunnerPatientContext('SNIP')} onNext={(result) => void persistDraftStep('SNIP', result)} />;
      case 'PEAK_FLOW':
        return (
          <PeakFlowTest
            {...commonProps}
            initial={getRunnerTestInitialValue('PEAK_FLOW')}
            patientContext={getRunnerPatientContext('PEAK_FLOW')}
            onNext={(result) => void persistDraftStep('PEAK_FLOW', result)}
          />
        );
      case 'TUG':
        return (
          <TUGTest
            {...commonProps}
            initial={getRunnerTestInitialValue('TUG')}
            patientContext={getRunnerPatientContext('TUG')}
            onDraftAttempt={(result) =>
              persistDraftStep('TUG', result, {
                advanceToNext: false,
                successMessage: 'Primeira tentativa do TUG salva como rascunho. Você pode encerrar com 1 tentativa ou iniciar a 2ª.',
              })
            }
            onNext={(result) => void persistDraftStep('TUG', result)}
          />
        );
      case '4MGS':
        return (
          <GaitSpeedTest
            {...commonProps}
            initial={getRunnerTestInitialValue('4MGS')}
            patientContext={getRunnerPatientContext('4MGS')}
            onNext={(result) => void persistDraftStep('4MGS', result)}
          />
        );
      case '5xSTS':
        return (
          <FiveSTSTest
            {...commonProps}
            initial={getRunnerTestInitialValue('5xSTS')}
            patientContext={getRunnerPatientContext('5xSTS')}
            onNext={(result) => void persistDraftStep('5xSTS', result)}
          />
        );
      case 'STS_30S':
        return (
          <STS30sTest
            {...commonProps}
            initial={getRunnerTestInitialValue('STS_30S')}
            patientContext={getRunnerPatientContext('STS_30S')}
            onDraftAttempt={(result) =>
              persistDraftStep('STS_30S', result, {
                advanceToNext: false,
                successMessage: 'Primeira tentativa do STS 30s salva como rascunho. Quando quiser, retome para registrar a 2ª.',
              })
            }
            onNext={(result) => void persistDraftStep('STS_30S', result)}
          />
        );
      case 'STS_1MIN':
        return (
          <STS1MinTest
            {...commonProps}
            initial={getRunnerTestInitialValue('STS_1MIN')}
            patientContext={getRunnerPatientContext('STS_1MIN')}
            onDraftAttempt={(result) =>
              persistDraftStep('STS_1MIN', result, {
                advanceToNext: false,
                successMessage: 'Primeira tentativa do STS 1min salva como rascunho. Você pode encerrar com 1 tentativa ou registrar a 2ª depois.',
              })
            }
            onNext={(result) => void persistDraftStep('STS_1MIN', result)}
          />
        );
      case 'CAT':
        return (
          <CATTest
            {...commonProps}
            initial={getRunnerTestInitialValue('CAT')}
            onNext={(result) => void persistDraftStep('CAT', result)}
          />
        );
      case 'ACT':
        return (
          <ACTTest
            {...commonProps}
            initial={getRunnerTestInitialValue('ACT')}
            onNext={(result) => void persistDraftStep('ACT', result)}
          />
        );
      case 'mMRC':
        return (
          <MMRCTest
            {...commonProps}
            initial={getRunnerTestInitialValue('mMRC')}
            onNext={(result) => void persistDraftStep('mMRC', result)}
          />
        );
      case 'PFSDQ-M':
        return (
          <PFSDQMTest
            {...commonProps}
            initial={getRunnerTestInitialValue('PFSDQ-M')}
            onNext={(result) => void persistDraftStep('PFSDQ-M', result)}
          />
        );
      case 'HADS':
        return (
          <HADSTest
            {...commonProps}
            initial={getRunnerTestInitialValue('HADS')}
            onNext={(result) => void persistDraftStep('HADS', result)}
          />
        );
      case 'PSQI':
        return (
          <PSQITest
            {...commonProps}
            initial={getRunnerTestInitialValue('PSQI')}
            onNext={(result) => void persistDraftStep('PSQI', result)}
          />
        );
      case 'AQLQ':
        return (
          <AQLQTest
            {...commonProps}
            initial={getRunnerTestInitialValue('AQLQ')}
            onNext={(result) => void persistDraftStep('AQLQ', result)}
          />
        );
      case 'SGRQ':
        return (
          <SGRQTest
            {...commonProps}
            initial={getRunnerTestInitialValue('SGRQ')}
            onNext={(result) => void persistDraftStep('SGRQ', result)}
          />
        );
      case 'LCADL':
        return (
          <LCADLTest
            {...commonProps}
            initial={getRunnerTestInitialValue('LCADL')}
            onNext={(result) => void persistDraftStep('LCADL', result)}
          />
        );
      case 'MLHFQ':
        return (
          <MLHFQTest
            {...commonProps}
            initial={getRunnerTestInitialValue('MLHFQ')}
            onNext={(result) => void persistDraftStep('MLHFQ', result)}
          />
        );
      case 'SAQ':
        return (
          <SAQTest
            {...commonProps}
            initial={getRunnerTestInitialValue('SAQ')}
            onNext={(result) => void persistDraftStep('SAQ', result)}
          />
        );
    }
  };

  const supportsPdfHistoryChoice = (testKey: string) => ['TC6', 'PIMAX', 'PEAK_COUGH_FLOW', 'CV_POSTURAL', 'SNIP'].includes(testKey);

  const closeTc6PdfExportState = () => setTc6PdfExportState(null);

  const resolveTc6ExportComparisonPayload = (
    result: AssessmentResultV2,
    previousResults: AssessmentResultV2[],
  ): Tc6ResultPayload | null => {
    if (!previousResults.length) return null;

    if (selectedResult?.resultId === result.resultId && selectedTc6Comparison) {
      return selectedTc6Comparison.payload;
    }

    return resolveRunnerPayload('TC6', previousResults[0]) as Tc6ResultPayload;
  };

  const executePdfAction = async (
    result: AssessmentResultV2,
    action: 'download' | 'print',
    previousResults: AssessmentResultV2[],
    options?: {
      includeHistory?: boolean;
    },
  ) => {
    const branding = snapshot.branding;
    if (!branding) {
      throw new Error('A identidade visual da clínica ainda não está disponível para gerar o PDF.');
    }
    const pdfPreviousResults = supportsPdfHistoryChoice(result.testKey) && options?.includeHistory !== true ? [] : previousResults;
    const tc6ComparisonPayload =
      result.testKey === 'TC6' && options?.includeHistory ? resolveTc6ExportComparisonPayload(result, previousResults) : null;
    const tc6ChartImagesOverride =
      result.testKey === 'TC6'
        ? await TC6ChartCaptureService.captureFromPayload(resolveRunnerPayload('TC6', result) as Tc6ResultPayload, {
            comparisonPayload: tc6ComparisonPayload,
          })
        : null;

    if (action === 'download') {
      await AssessmentPDFV2Service.downloadResultPDF({
        result,
        branding,
        previousResults: pdfPreviousResults,
        tc6ChartImagesOverride,
        tenantSlug: selectedClient?.clientSlug || null,
        tenantName: selectedClient?.clientName || branding.displayName,
        generatedByUserId: user?.userId || null,
        generatedByName: user?.userDisplayName || null,
      });
    } else {
      await AssessmentPDFV2Service.printResultPDF({
        result,
        branding,
        previousResults: pdfPreviousResults,
        tc6ChartImagesOverride,
        tenantSlug: selectedClient?.clientSlug || null,
        tenantName: selectedClient?.clientName || branding.displayName,
        generatedByUserId: user?.userId || null,
        generatedByName: user?.userDisplayName || null,
      });
    }
  };

  const runPdfAction = async (result: AssessmentResultV2, action: 'download' | 'print') => {
    if (!snapshot.branding || !clientId) {
      toast.error('A identidade visual da clínica ainda não está disponível para gerar o PDF.');
      return;
    }
    if (result.clientId !== clientId) {
      toast.error('A avaliação selecionada pertence a outro tenant. Recarregue a página.');
      return;
    }

    const requestedClientId = clientId;
    try {
      setResultActionLoading(true);
      const currentResults = await AssessmentResultsV2Service.listByIds(
        requestedClientId,
        [result.resultId],
      );
      if (activeClientIdRef.current !== requestedClientId) return;

      const freshResult = currentResults[0];
      if (!freshResult) {
        throw new Error('A avaliação não foi encontrada no tenant ativo.');
      }

      const previousResults = isAnyAssessmentReportResult(freshResult)
        ? []
        : await AssessmentResultsV2Service.listHistoryForPatient(
          requestedClientId,
          freshResult.patientId,
          freshResult.testKey,
          {
            excludeResultId: freshResult.resultId,
            beforeAssessmentDate: freshResult.assessmentDate,
            limit: 5,
          },
        );
      if (activeClientIdRef.current !== requestedClientId) return;

      if (supportsPdfHistoryChoice(freshResult.testKey) && previousResults.length > 0) {
        setTc6PdfExportState({
          action,
          result: freshResult,
          previousResults,
        });
        return;
      }

      await executePdfAction(freshResult, action, previousResults);
    } catch (error: unknown) {
      if (activeClientIdRef.current !== requestedClientId) return;
      console.error('Erro ao gerar PDF da avaliação V2:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao gerar o PDF da avaliação.');
    } finally {
      if (activeClientIdRef.current === requestedClientId) {
        setResultActionLoading(false);
      }
    }
  };

  const openVerificationPage = (verificationToken: string) => {
    window.open(buildVerificationUrl(verificationToken), '_blank', 'noopener,noreferrer');
  };

  const handleSignResult = async (result: AssessmentResultV2) => {
    if (!user?.userId || !clientId || !selectedClient) {
      toast.error('Usuário ou tenant não identificado para assinar a avaliação.');
      return;
    }
    if (result.clientId !== clientId) {
      toast.error('A avaliação selecionada pertence a outro tenant. Recarregue a página.');
      return;
    }

    if (result.status === 'cancelled') {
      toast.error('Avaliações invalidadas não podem ser assinadas.');
      return;
    }

    if (getAssessmentSignatureMetadata(result.assetsJson)?.verificationToken) {
      toast.info('Esta avaliação já está assinada eletronicamente.');
      return;
    }

    const shouldSign = window.confirm(
      'Assinar eletronicamente esta avaliação bloqueará novas edições do resultado e liberará o QR code de autenticidade no PDF.\n\nDeseja continuar?',
    );

    if (!shouldSign) {
      return;
    }

    const requestedClientId = clientId;
    const requestedUserId = user.userId;
    const requestedUserName = user.userDisplayName;
    const requestedUserRole = selectedClient.userRole || user.userRole || null;
    try {
      setSigningResultId(result.resultId);

      const authorDirectory = await fetchMedicalRecordAuthors(
        [requestedUserId],
        requestedClientId,
      );
      if (activeClientIdRef.current !== requestedClientId) return;
      const signer =
        authorDirectory.get(requestedUserId) ??
        buildMedicalRecordAuthorSnapshot({
          userId: requestedUserId,
          name: requestedUserName || null,
          role: requestedUserRole,
        });

      if (requiresProfessionalRegistration(signer.role) && !signer.councilNumber) {
        toast.error('Não foi encontrado um registro profissional para o assinante atual.');
        return;
      }

      const currentResults = await AssessmentResultsV2Service.listByIds(
        requestedClientId,
        [result.resultId],
      );
      if (activeClientIdRef.current !== requestedClientId) return;
      const freshResult = currentResults[0];
      if (!freshResult) {
        throw new Error('Avaliação não encontrada para assinatura.');
      }
      if (freshResult.status === 'cancelled') {
        toast.error('A avaliação foi invalidada em outra sessão e não pode ser assinada.');
        await loadDashboardData();
        return;
      }

      const currentSignature = getAssessmentSignatureMetadata(freshResult.assetsJson);
      if (currentSignature?.verificationToken) {
        toast.info('Esta avaliação já foi assinada em outra sessão.');
        await loadDashboardData();
        return;
      }

      if (freshResult.testKey === 'INITIAL_ASSESSMENT') {
        await InitialAssessmentIntegrityService.assertSourcesCurrent(freshResult.payloadJson, { clientId: requestedClientId, patientId: freshResult.patientId });
      }
      if (isAssessmentReportResult(freshResult)) {
        await AssessmentReportV2Service.assertReportReferencesAreCurrent(freshResult);
        await AssessmentPDFV2Service.assertAssessmentReportPackageIsCurrent(freshResult);
        const reportPayload = normalizeAssessmentReportPayload(freshResult.payloadJson);
        if (!reportPayload) {
          throw new Error('O relatório não possui um conteúdo estruturado válido para assinatura.');
        }
        if (
          !reportPayload.authorship.professional.userId
          || reportPayload.authorship.professional.userId !== requestedUserId
        ) {
          toast.error(
            'Somente o profissional identificado como autor atual do relatório pode assiná-lo.',
          );
          return;
        }
        if (reportPayload.mode === 'physiotherapy_technical_report') {
          if (signer.role !== 'fisioterapeuta' || !signer.councilNumber) {
            toast.error(
              'O relatório técnico só pode ser assinado por fisioterapeuta com CREFITO cadastrado.',
            );
            return;
          }
        }
      }

      const nextSignature = await buildSignedDocumentMetadata({
        hashPayload: {
          documentType: 'assessment_result',
          resultId: freshResult.resultId,
          sessionId: freshResult.sessionId,
          testKey: freshResult.testKey,
          assessmentDate: freshResult.assessmentDate,
          notes: freshResult.notes,
          summaryJson: freshResult.summaryJson,
          payloadJson: freshResult.payloadJson,
          assetsJson: freshResult.assetsJson,
          patientNameSnapshot: freshResult.patientNameSnapshot,
          patientMedicalRecordSnapshot: freshResult.patientMedicalRecordSnapshot,
          professionalNameSnapshot: freshResult.professionalNameSnapshot,
          professionalRegistrationSnapshot: freshResult.professionalRegistrationSnapshot,
          professionalRoleSnapshot: freshResult.professionalRoleSnapshot,
          signedByUserId: signer.userId,
          signedByName: signer.name,
          signedByRole: signer.professionalTitle,
          signedByRegistration: signer.councilNumber,
        },
        signedByUserId: requestedUserId,
        signedByName: signer.name,
        signedByRole: signer.professionalTitle,
        signedByRegistration: signer.councilNumber,
        templateVersion: 2,
      });

      const nextAssetsJson = {
        ...(freshResult.assetsJson && typeof freshResult.assetsJson === 'object' ? freshResult.assetsJson : {}),
        signature: nextSignature,
      };

      if (activeClientIdRef.current !== requestedClientId) return;
      const updatedResult = await AssessmentResultsV2Service.updateIfRevisionMatches(
        freshResult.resultId,
        requestedClientId,
        {
          updatedAt: freshResult.updatedAt,
          createdAt: freshResult.createdAt,
        },
        {
          assetsJson: nextAssetsJson,
        },
      );
      if (activeClientIdRef.current !== requestedClientId) return;

      setResults((current) => current.map((item) => (item.resultId === updatedResult.resultId ? updatedResult : item)));
      setSelectedResult((current) => (current?.resultId === updatedResult.resultId ? updatedResult : current));
      toast.success('Avaliação assinada com sucesso. O PDF agora exibirá o QR code de autenticidade.');
    } catch (error: any) {
      if (activeClientIdRef.current !== requestedClientId) return;
      console.error('Erro ao assinar avaliação:', error);
      const message =
        error?.message === 'SIGNED_ASSESSMENT_RESULT_LOCKED'
          ? 'Esta avaliação já está assinada e bloqueada para edição.'
          : error?.message === 'ASSESSMENT_RESULT_CONCURRENT_MODIFICATION'
            ? 'A avaliação foi alterada ou assinada em outra sessão. Recarregue e revise a versão atual.'
          : error?.message || 'Não foi possível assinar a avaliação.';
      toast.error(message);
      if (error?.message === 'ASSESSMENT_RESULT_CONCURRENT_MODIFICATION') {
        await loadDashboardData();
      }
    } finally {
      setSigningResultId((current) => (current === result.resultId ? null : current));
    }
  };

  const handleConfirmTc6PdfExport = async (includeHistory: boolean) => {
    if (!tc6PdfExportState || !snapshot.branding || !clientId) return;
    if (tc6PdfExportState.result.clientId !== clientId) {
      closeTc6PdfExportState();
      toast.error('A avaliação selecionada pertence a outro tenant. Gere o PDF novamente.');
      return;
    }

    const requestedClientId = clientId;
    const requestedState = tc6PdfExportState;
    try {
      setResultActionLoading(true);
      const [freshResult] = await AssessmentResultsV2Service.listByIds(
        requestedClientId,
        [requestedState.result.resultId],
      );
      if (activeClientIdRef.current !== requestedClientId) return;
      if (!freshResult) {
        throw new Error('A avaliação não foi encontrada no tenant ativo.');
      }
      const freshHistory = await AssessmentResultsV2Service.listHistoryForPatient(
        requestedClientId,
        freshResult.patientId,
        freshResult.testKey,
        {
          excludeResultId: freshResult.resultId,
          beforeAssessmentDate: freshResult.assessmentDate,
          limit: 5,
        },
      );
      if (activeClientIdRef.current !== requestedClientId) return;
      await executePdfAction(
        freshResult,
        requestedState.action,
        freshHistory,
        { includeHistory: includeHistory },
      );
      closeTc6PdfExportState();
    } catch (error: unknown) {
      if (activeClientIdRef.current !== requestedClientId) return;
      console.error('Erro ao gerar PDF com configuração de histórico:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao gerar o PDF da avaliação.');
    } finally {
      if (activeClientIdRef.current === requestedClientId) {
        setResultActionLoading(false);
      }
    }
  };

  const handleInvalidateResult = async (resultId: string, justification: string) => {
    if (!user || !clientId) {
      toast.error('Usuário ou tenant não identificado para invalidar a avaliação.');
      return;
    }

    const currentResult =
      results.find((item) => item.resultId === resultId) ??
      (selectedResult?.resultId === resultId ? selectedResult : null);

    if (!currentResult) {
      toast.error('Avaliação não encontrada para invalidação.');
      return;
    }

    const updatedResult = await AssessmentResultsV2Service.invalidate(resultId, clientId, {
      userId: user.userId,
      userName: user.userDisplayName || 'Profissional',
      userEmail: user.userEmail || '',
      justification,
    });

    const remainingActiveResults = await AssessmentResultsV2Service.countNonCancelledBySession(clientId, updatedResult.sessionId);
    if (remainingActiveResults === 0) {
      await AssessmentSessionsV2Service.updateStatus(updatedResult.sessionId, clientId, 'cancelled');
    }

    await loadDashboardData();
    setSelectedResult((current) => {
      if (current?.resultId !== updatedResult.resultId) return current;
      return statusFilter === 'active' ? null : updatedResult;
    });
    toast.success('Avaliação invalidada com sucesso.');
  };

  const handleRestoreResult = async (resultId: string, justification: string) => {
    if (!user || !clientId) {
      toast.error('Usuário ou tenant não identificado para restaurar a avaliação.');
      return;
    }

    const currentResult =
      results.find((item) => item.resultId === resultId) ??
      (selectedResult?.resultId === resultId ? selectedResult : null);

    if (!currentResult) {
      toast.error('Avaliação não encontrada para restauração.');
      return;
    }

    const updatedResult = await AssessmentResultsV2Service.restore(resultId, clientId, {
      userId: user.userId,
      userName: user.userDisplayName || 'Profissional',
      userEmail: user.userEmail || '',
      justification,
    });

    await AssessmentSessionsV2Service.updateStatus(updatedResult.sessionId, clientId, 'completed');
    await loadDashboardData();
    setSelectedResult(updatedResult);
    toast.success('Avaliação restaurada com sucesso.');
  };

  const openTc6PayloadEditor = () => {
    if (!selectedResult || selectedResult.testKey !== 'TC6') return;
    setShowTc6PayloadEditor(true);
  };

  const cancelTc6PayloadEditor = () => {
    setShowTc6PayloadEditor(false);
  };

  const openSelectedResultPayloadEditor = () => {
    if (!selectedResult || isAnyAssessmentReportResult(selectedResult)) return;
    if (isAssessmentV2LegacyReadOnlyResult(selectedResult)) {
      toast.info('Este registro usa uma versão legada e foi preservado somente para consulta, sem reclassificação retroativa.');
      return;
    }
    if (getAssessmentSignatureMetadata(selectedResult.assetsJson)?.verificationToken) {
      toast.info('Esta avaliação já está assinada e não pode mais ser editada.');
      return;
    }

    if (selectedResult.testKey === 'TC6') {
      setShowTc6PayloadEditor(true);
      return;
    }

    setResultInterpretationDraft(
      getAssessmentInterpretationDraft(
        resolveRunnerPayload(selectedResult.testKey as AssessmentV2TestKey, selectedResult),
      ),
    );
    setShowResultPayloadEditor(true);
  };

  const cancelSelectedResultPayloadEditor = () => {
    setShowResultPayloadEditor(false);
    setResultInterpretationDraft('');
  };

  const handleSaveTc6PayloadEdit = async (nextPayload: Tc6ResultPayload) => {
    if (!selectedResult || selectedResult.testKey !== 'TC6' || !clientId) {
      toast.error('Resultado TC6 não encontrado para edição.');
      return;
    }
    if (getAssessmentSignatureMetadata(selectedResult.assetsJson)?.verificationToken) {
      toast.error('Este resultado já está assinado e não pode mais ser editado.');
      return;
    }

    try {
      setTc6PayloadSaving(true);
      if (!nextPayload || typeof nextPayload !== 'object' || !nextPayload.attempt1) {
        throw new Error('O TC6 precisa conter pelo menos a 1ª tentativa para ser salvo.');
      }

      const nextContent = buildEditableTc6ResultContent(selectedResult, nextPayload, {
        userId: user?.userId,
        userName: user?.userDisplayName || 'Profissional',
        userEmail: user?.userEmail || '',
      });

      const updatedResult = await AssessmentResultsV2Service.update(selectedResult.resultId, clientId, {
        summaryJson: nextContent.summaryJson,
        payloadJson: nextContent.payloadJson,
        assetsJson: nextContent.assetsJson,
        patientBirthDateSnapshot:
          typeof nextContent.payloadJson.patientContext?.birthDate === 'string'
            ? nextContent.payloadJson.patientContext.birthDate
            : selectedResult.patientBirthDateSnapshot,
        patientSexSnapshot:
          typeof nextContent.payloadJson.patientContext?.sex === 'string'
            ? nextContent.payloadJson.patientContext.sex
            : selectedResult.patientSexSnapshot,
        patientWeightSnapshot:
          typeof nextContent.payloadJson.patientContext?.weightKg === 'number'
            ? nextContent.payloadJson.patientContext.weightKg
            : selectedResult.patientWeightSnapshot,
        patientHeightSnapshot:
          typeof nextContent.payloadJson.patientContext?.heightCm === 'number'
            ? nextContent.payloadJson.patientContext.heightCm
            : selectedResult.patientHeightSnapshot,
        professionalNameSnapshot:
          typeof nextContent.payloadJson.professionalContext?.name === 'string'
            ? nextContent.payloadJson.professionalContext.name
            : selectedResult.professionalNameSnapshot,
        professionalRegistrationSnapshot:
          typeof nextContent.payloadJson.professionalContext?.registration === 'string'
            ? nextContent.payloadJson.professionalContext.registration
            : selectedResult.professionalRegistrationSnapshot,
        professionalRoleSnapshot:
          typeof nextContent.payloadJson.professionalContext?.role === 'string'
            ? nextContent.payloadJson.professionalContext.role
            : selectedResult.professionalRoleSnapshot,
      });

      setResults((current) => current.map((item) => (item.resultId === updatedResult.resultId ? updatedResult : item)));
      setSelectedResult(updatedResult);
      setShowTc6PayloadEditor(false);
      toast.success('Ajustes do TC6 salvos com sucesso.');
    } catch (error: any) {
      console.error('Erro ao atualizar payload do TC6:', error);
      const message = error?.message || 'Falha ao atualizar o payload do TC6.';
      toast.error(message);
    } finally {
      setTc6PayloadSaving(false);
    }
  };

  const buildEditableSharedContext = (
    result: AssessmentResultV2,
    rawPayload: unknown,
  ): Awaited<ReturnType<typeof buildAssessmentSharedContext>> => {
    const patientContext = buildEditableResultPatientContext(result, rawPayload);
    const professionalContext = buildEditableResultProfessionalContext(result, rawPayload);

    return {
      professionalName: professionalContext.name || result.professionalNameSnapshot || 'Profissional',
      professionalRegistration: professionalContext.registration || '',
      professionalRole: professionalContext.role,
      assessmentDateIso: result.assessmentDate,
      patientAgeAtAssessment: patientContext.ageYears,
      patientContext,
    };
  };

  const buildEditedResultContent = (
    result: AssessmentResultV2,
    rawPayload: unknown,
    interpretationDraft: string,
  ) => {
    const nextContent = buildResultContent(
      result.testKey as AssessmentV2TestKey,
      rawPayload,
      buildEditableSharedContext(result, rawPayload),
    );

    return {
      summaryJson: nextContent.summaryJson,
      payloadJson: withAssessmentInterpretationOverride(nextContent.payloadJson, interpretationDraft),
      assetsJson: {
        ...(result.assetsJson && typeof result.assetsJson === 'object' ? result.assetsJson : {}),
        ...(nextContent.assetsJson && typeof nextContent.assetsJson === 'object' ? nextContent.assetsJson : {}),
        payloadEditorAudit: {
          editedAt: new Date().toISOString(),
          userId: user?.userId || null,
          userName: user?.userDisplayName || null,
          userEmail: user?.userEmail || null,
        },
      },
    };
  };

  const handleSaveSelectedResultPayloadEdit = async (nextPayload: unknown) => {
    if (!selectedResult || !clientId || isAnyAssessmentReportResult(selectedResult)) {
      toast.error('Resultado não encontrado para edição.');
      return;
    }
    if (getAssessmentSignatureMetadata(selectedResult.assetsJson)?.verificationToken) {
      toast.error('Este resultado já está assinado e não pode mais ser editado.');
      return;
    }

    try {
      setResultPayloadSaving(true);

      if (selectedResult.testKey === 'INITIAL_ASSESSMENT') {
        if (!isInitialAssessmentComplete(nextPayload)) throw new Error('Conclua o motivo e a síntese profissional.');
        await InitialAssessmentIntegrityService.assertSourcesCurrent(nextPayload, { clientId, patientId: selectedResult.patientId });
      }

      const nextContent = buildEditedResultContent(selectedResult, nextPayload, resultInterpretationDraft);
      const updatedResult = await AssessmentResultsV2Service.update(selectedResult.resultId, clientId, {
        summaryJson: nextContent.summaryJson,
        payloadJson: nextContent.payloadJson,
        assetsJson: nextContent.assetsJson,
        patientBirthDateSnapshot:
          typeof nextContent.payloadJson.patientContext?.birthDate === 'string'
            ? nextContent.payloadJson.patientContext.birthDate
            : selectedResult.patientBirthDateSnapshot,
        patientSexSnapshot:
          typeof nextContent.payloadJson.patientContext?.sex === 'string'
            ? nextContent.payloadJson.patientContext.sex
            : selectedResult.patientSexSnapshot,
        patientWeightSnapshot:
          typeof nextContent.payloadJson.patientContext?.weightKg === 'number'
            ? nextContent.payloadJson.patientContext.weightKg
            : selectedResult.patientWeightSnapshot,
        patientHeightSnapshot:
          typeof nextContent.payloadJson.patientContext?.heightCm === 'number'
            ? nextContent.payloadJson.patientContext.heightCm
            : selectedResult.patientHeightSnapshot,
        professionalNameSnapshot:
          typeof nextContent.payloadJson.professionalContext?.name === 'string'
            ? nextContent.payloadJson.professionalContext.name
            : selectedResult.professionalNameSnapshot,
        professionalRegistrationSnapshot:
          typeof nextContent.payloadJson.professionalContext?.registration === 'string'
            ? nextContent.payloadJson.professionalContext.registration
            : selectedResult.professionalRegistrationSnapshot,
        professionalRoleSnapshot:
          typeof nextContent.payloadJson.professionalContext?.role === 'string'
            ? nextContent.payloadJson.professionalContext.role
            : selectedResult.professionalRoleSnapshot,
      });

      setResults((current) => current.map((item) => (item.resultId === updatedResult.resultId ? updatedResult : item)));
      setSelectedResult(updatedResult);
      setShowResultPayloadEditor(false);
      setResultInterpretationDraft('');
      toast.success(`Ajustes de ${getAssessmentV2Label(selectedResult.testKey)} salvos com sucesso.`);
    } catch (error: any) {
      console.error(`Erro ao atualizar payload de ${selectedResult.testKey}:`, error);
      toast.error(error?.message || 'Falha ao atualizar o resultado.');
    } finally {
      setResultPayloadSaving(false);
    }
  };

  const getSelectedResultInitialValue = (result: AssessmentResultV2) => {
    const payload = resolveRunnerPayload(result.testKey as AssessmentV2TestKey, result);

    switch (result.testKey as AssessmentV2TestKey) {
      case 'INITIAL_ASSESSMENT':
      case 'TC6':
      case 'ISWT':
      case 'ESWT':
      case '6MST':
      case 'CHESTER':
      case '5xSTS':
      case 'STS_30S':
      case 'STS_1MIN':
      case 'HANDGRIP':
      case 'PERIPHERAL_MUSCLE_STRENGTH':
      case 'ONE_RM':
      case 'PIMAX':
      case 'PEMAX':
      case 'S_INDEX':
      case 'RESP_ENDURANCE':
      case 'PEAK_FLOW':
      case 'PEAK_COUGH_FLOW':
      case 'CV_POSTURAL':
      case 'SNIP':
      case 'TUG':
      case '4MGS':
      case 'AQLQ':
      case 'PSQI':
      case 'LCADL':
      case 'PFSDQ-M':
      case 'SAQ':
      case 'SGRQ':
        return payload;
      case 'CAT':
      case 'ACT':
      case 'HADS':
      case 'MLHFQ':
        return Array.isArray(payload.answers) ? payload.answers : undefined;
      case 'mMRC':
        return typeof payload.grade === 'number' ? payload.grade : undefined;
      default:
        return undefined;
    }
  };

  const renderSelectedResultPayloadEditor = (result: AssessmentResultV2) => {
    const testKey = result.testKey as AssessmentV2TestKey;
    const payload = resolveRunnerPayload(testKey, result);
    const initial = getSelectedResultInitialValue(result);
    const patientContext = buildEditableResultPatientContext(result, payload);
    const editorProps = {
      onCancel: cancelSelectedResultPayloadEditor,
    };

    const editor = (() => {
      switch (testKey) {
        case 'INITIAL_ASSESSMENT':
          return (
            <InitialAssessmentTest
              {...editorProps}
              initial={initial as InitialAssessmentResultPayload}
              patientId={result.patientId}
              clientId={result.clientId}
              resultId={result.resultId}
              sourceResults={initialAssessmentSources}
              sourceResultsLoading={initialAssessmentSourcesLoading}
              buildSourceSnapshot={buildAssessmentReportSourceSnapshot}
              patientContext={patientContext}
              isEditing
              onCreateAnamnesis={(input) => createAnamnesisFromAssessment(result.patientId, input)}
              onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)}
            />
          );
        case 'ISWT':
          return <ISWTTest {...editorProps} initial={initial} patientContext={patientContext} onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)} />;
        case 'ESWT': {
          const eswtPayload = payload as ShuttleEswtResultPayload | undefined;
          return (
            <ESWTTest
              {...editorProps}
              initial={initial}
              patientContext={patientContext}
              iswtReference={{
                peakLevelReached: eswtPayload?.protocol.sourceIswtLevel ?? null,
                peakSpeedKmh: eswtPayload?.protocol.sourceIswtSpeedKmh ?? null,
              }}
              onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)}
            />
          );
        }
        case '6MST':
          return (
            <MST6Test
              {...editorProps}
              initial={initial}
              isEditing
              patientContext={patientContext}
              onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)}
            />
          );
        case 'CHESTER':
          return (
            <ChesterStepTest
              {...editorProps}
              initial={initial}
              isEditing
              patientContext={patientContext}
              onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)}
            />
          );
        case 'HANDGRIP':
          return (
            <HandgripTest
              {...editorProps}
              initial={initial}
              patientContext={patientContext}
              onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)}
            />
          );
        case 'PERIPHERAL_MUSCLE_STRENGTH':
          return (
            <PeripheralMuscleStrengthTest
              {...editorProps}
              initial={initial}
              patientContext={patientContext}
              onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)}
            />
          );
        case 'ONE_RM':
          return (
            <OneRMTest
              {...editorProps}
              initial={initial}
              patientContext={patientContext}
              onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)}
            />
          );
        case 'PIMAX':
          return (
            <PimaxTest
              {...editorProps}
              initial={initial}
              patientContext={patientContext}
              onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)}
            />
          );
        case 'PEMAX':
          return (
            <PemaxTest
              {...editorProps}
              initial={initial}
              patientContext={patientContext}
              onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)}
            />
          );
        case 'S_INDEX':
          return (
            <SIndexTest
              {...editorProps}
              initial={initial}
              patientContext={patientContext}
              referencePimaxMax={extractPimaxMaxFromPayload(payload)}
              referenceHandgripKg={extractHandgripMaxFromPayload(payload)}
              onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)}
            />
          );
        case 'RESP_ENDURANCE':
          return <RespiratoryEnduranceTest {...editorProps} initial={initial} patientContext={patientContext} onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)} />;
        case 'PEAK_COUGH_FLOW':
          return <PeakCoughFlowTest {...editorProps} initial={initial} patientContext={patientContext} onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)} />;
        case 'CV_POSTURAL':
          return <PosturalVitalCapacityTest {...editorProps} initial={initial} patientContext={patientContext} onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)} />;
        case 'SNIP':
          return <SnipTest {...editorProps} initial={initial} patientContext={patientContext} onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)} />;
        case 'PEAK_FLOW':
          return <PeakFlowTest {...editorProps} initial={initial} patientContext={patientContext} onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)} />;
        case 'TUG':
          return (
            <TUGTest
              {...editorProps}
              initial={initial}
              patientContext={patientContext}
              onDraftAttempt={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)}
              onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)}
            />
          );
        case '4MGS':
          return <GaitSpeedTest {...editorProps} initial={initial} patientContext={patientContext} onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)} />;
        case '5xSTS':
          return <FiveSTSTest {...editorProps} initial={initial} patientContext={patientContext} onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)} />;
        case 'STS_30S':
          return (
            <STS30sTest
              {...editorProps}
              initial={initial}
              patientContext={patientContext}
              onDraftAttempt={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)}
              onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)}
            />
          );
        case 'STS_1MIN':
          return (
            <STS1MinTest
              {...editorProps}
              initial={initial}
              patientContext={patientContext}
              onDraftAttempt={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)}
              onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)}
            />
          );
        case 'CAT':
          return <CATTest {...editorProps} initial={initial} onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)} />;
        case 'ACT':
          return <ACTTest {...editorProps} initial={initial} onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)} />;
        case 'mMRC':
          return <MMRCTest {...editorProps} initial={initial} onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)} />;
        case 'PFSDQ-M':
          return <PFSDQMTest {...editorProps} initial={initial} onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)} />;
        case 'HADS':
          return <HADSTest {...editorProps} initial={initial} onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)} />;
        case 'PSQI':
          return <PSQITest {...editorProps} initial={initial} onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)} />;
        case 'AQLQ':
          return <AQLQTest {...editorProps} initial={initial} onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)} />;
        case 'SGRQ':
          return <SGRQTest {...editorProps} initial={initial} onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)} />;
        case 'LCADL':
          return <LCADLTest {...editorProps} initial={initial} onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)} />;
        case 'MLHFQ':
          return <MLHFQTest {...editorProps} initial={initial} onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)} />;
        case 'SAQ':
          return <SAQTest {...editorProps} initial={initial} onNext={(nextResult) => void handleSaveSelectedResultPayloadEdit(nextResult)} />;
        default:
          return null;
      }
    })();

    if (!editor) {
      return (
        <div className="rounded-[24px] border border-slate-100 bg-white p-6 text-sm text-slate-600 shadow-sm">
          Este resultado ainda não possui editor configurado.
        </div>
      );
    }

    return (
      <div className="space-y-5">
        {testKey !== 'INITIAL_ASSESSMENT' ? (
          <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.25em] text-slate-400">Laudo / interpretação clínica</div>
            <div className="mt-3 text-sm text-slate-600">
              Se este campo ficar vazio, a interpretação automática do teste continuará sendo usada.
            </div>
            <textarea
              value={resultInterpretationDraft}
              onChange={(event) => setResultInterpretationDraft(event.target.value)}
              rows={5}
              disabled={resultPayloadSaving}
              className="mt-4 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 disabled:cursor-not-allowed disabled:bg-slate-50"
              placeholder="Digite aqui a interpretação clínica editada deste resultado."
            />
          </div>
        ) : null}
        {editor}
      </div>
    );
  };


  const getAssessmentReportPatientSnapshot = (patient?: AssessmentV2Patient | null) => ({
    patientId: patient?.id || '',
    name: patient?.personalData?.name || patient?.patientFullName || '',
    medicalRecord: patient ? getPreferredPatientIdentifier(patient) : null,
    birthDate: patient?.patientDateOfBirth || null,
  });

  const getCurrentAssessmentReportProfessional = async () => {
    if (!user) {
      return { userId: null, name: null, registration: null, role: null };
    }

    const role = selectedClient?.userRole || user.userRole || 'admin';
    const hasCouncilRegistration = role === 'doctor' || role === 'fisioterapeuta';
    const professional = hasCouncilRegistration
      ? await DoctorService.getDoctorByuserId(user.userId).catch(() => null)
      : null;
    const userRegistration = user as typeof user & {
      crm?: string | null;
      crefito?: string | null;
    };
    const registration =
      role === 'fisioterapeuta'
        ? professional?.doctorCrefito
          || userRegistration.crefito
          || null
        : role === 'doctor'
          ? professional?.doctorCrm
            || userRegistration.crm
            || null
          : null;

    return {
      userId: user.userId,
      name: user.userDisplayName || 'Profissional',
      registration,
      role,
    };
  };

  const loadAssessmentReportCandidates = async (patientId: string) => {
    const requestId = ++assessmentReportCandidateRequestRef.current;
    const requestedClientId = clientId;
    if (!requestedClientId || !patientId) {
      if (requestId === assessmentReportCandidateRequestRef.current) {
        setAssessmentReportCandidates([]);
        setAssessmentReportCandidatesLoading(false);
      }
      return [];
    }

    setAssessmentReportCandidatesLoading(true);
    try {
      const pageSize = 100;
      const firstPage = await AssessmentResultsV2Service.listCompletedForPatient(
        requestedClientId,
        patientId,
        { page: 1, pageSize },
      );
      if (
        requestId !== assessmentReportCandidateRequestRef.current
        || activeClientIdRef.current !== requestedClientId
      ) {
        return [];
      }
      const allResults = [...firstPage.items];
      const pageCount = Math.ceil(firstPage.total / pageSize);
      for (let page = 2; page <= pageCount; page += 1) {
        const nextPage = await AssessmentResultsV2Service.listCompletedForPatient(
          requestedClientId,
          patientId,
          { page, pageSize },
        );
        if (
          requestId !== assessmentReportCandidateRequestRef.current
          || activeClientIdRef.current !== requestedClientId
        ) {
          return [];
        }
        allResults.push(...nextPage.items);
      }

      const hydrated = await hydrateAssessmentResultsProfessionalSnapshots(
        allResults,
        requestedClientId,
      );
      const ordered = hydrated.sort(
        (left, right) =>
          new Date(right.assessmentDate).getTime() - new Date(left.assessmentDate).getTime(),
      );
      if (
        requestId !== assessmentReportCandidateRequestRef.current
        || activeClientIdRef.current !== requestedClientId
      ) {
        return [];
      }
      setAssessmentReportCandidates(ordered);
      return ordered;
    } catch (error: unknown) {
      if (
        requestId !== assessmentReportCandidateRequestRef.current
        || activeClientIdRef.current !== requestedClientId
      ) {
        return [];
      }
      console.error('Erro ao carregar avaliações para o relatório:', error);
      setAssessmentReportCandidates([]);
      const message = error instanceof Error ? error.message : 'falha desconhecida';
      toast.error(`Não foi possível carregar o histórico do paciente: ${message}`);
      return [];
    } finally {
      if (
        requestId === assessmentReportCandidateRequestRef.current
        && activeClientIdRef.current === requestedClientId
      ) {
        setAssessmentReportCandidatesLoading(false);
      }
    }
  };

  const buildAssessmentReportSourceSnapshot = (
    result: AssessmentResultV2,
  ): AssessmentReportSourceSnapshotV2 => {
    const classification = getResultSeverityLabel(result);
    const primaryMetric = getResultPrimaryMetric(result);
    return buildAssessmentReportSourceSuggestion({
      result,
      domain: getAssessmentReportDomainForTest(result.testKey),
      testLabel: getAssessmentV2Label(result.testKey),
      primaryMetric,
      originalClassification:
        classification && classification !== '—' ? classification : null,
      capturedAt: new Date().toISOString(),
    });
  };

  const openAssessmentReportModal = async (patientId?: string, options?: {
    sourceResult?: AssessmentResultV2;
    documentModel?: 'summary' | 'initial' | 'final' | 'longitudinal' | 'findings';
    technical?: boolean;
  }) => {
    if (!user) {
      toast.error('Não foi possível identificar o profissional responsável.');
      return;
    }

    const requestId = ++assessmentReportCandidateRequestRef.current;
    const requestedPatient = patientId ? patients.find((patient) => patient.id === patientId) : null;
    const professional = await getCurrentAssessmentReportProfessional();
    if (requestId !== assessmentReportCandidateRequestRef.current) return;
    if (options?.technical && (professional.role !== 'fisioterapeuta' || !professional.registration)) {
      toast.error('O diagnóstico CBDF exige fisioterapeuta responsável com CREFITO cadastrado.');
      return;
    }
    const nextForm = createInitialAssessmentReportForm({
      mode: options?.technical ? 'physiotherapy_technical_report' : 'assessment_summary',
      patient: getAssessmentReportPatientSnapshot(requestedPatient),
      professional,
    });
    if (options?.documentModel) nextForm.documentModel = options.documentModel;
    if (options?.documentModel === 'initial') nextForm.title = 'Avaliação fisioterapêutica inicial';
    if (options?.documentModel === 'final') nextForm.title = 'Avaliação fisioterapêutica final';
    if (options?.documentModel === 'longitudinal') nextForm.title = 'Relatório longitudinal de avaliação';

    setAssessmentReportEditingResultId(null);
    setAssessmentReportEditingRevision(null);
    setAssessmentReportGeneratedAt(null);
    setAssessmentReportCandidates([]);
    setAssessmentReportForm(nextForm);
    setShowAssessmentReportModal(true);
    if (requestedPatient) {
      const loadingCandidates = loadAssessmentReportCandidates(requestedPatient.id);
      const candidatesRequest = assessmentReportCandidateRequestRef.current;
      const candidates = await loadingCandidates;
      if (candidatesRequest !== assessmentReportCandidateRequestRef.current) return;
      if (options?.sourceResult && activeClientIdRef.current === clientId) {
        const anchor = candidates.find((item) => item.resultId === options.sourceResult?.resultId);
        if (!anchor) {
          toast.error('A avaliação selecionada não está mais disponível para o relatório.');
          return;
        }
        const initial = anchor.testKey === 'INITIAL_ASSESSMENT' ? normalizeInitialAssessmentPayload(anchor.payloadJson) : null;
        const evidenceIds = new Set<string>();
        const includeClinicalMoment = (clinical: AssessmentResultV2) => {
          evidenceIds.add(clinical.resultId);
          const payload = normalizeInitialAssessmentPayload(clinical.payloadJson);
          (payload?.selectedResults ?? []).forEach((source) => evidenceIds.add(source.resultId));
          if (!Object.prototype.hasOwnProperty.call(clinical.payloadJson, 'selectedResults')) {
            candidates.filter((item) => item.sessionId === clinical.sessionId).forEach((item) => evidenceIds.add(item.resultId));
          }
        };
        try {
          if (initial) await InitialAssessmentIntegrityService.assertSourcesCurrent(anchor.payloadJson, { clientId: anchor.clientId, patientId: anchor.patientId });
          includeClinicalMoment(anchor);
          if (options.documentModel === 'longitudinal' && initial?.baselineResultId) {
            const baseline = candidates.find((item) => item.resultId === initial.baselineResultId);
            if (!baseline) throw new Error('A avaliação basal selecionada não está disponível. Revise a ficha clínica.');
            await InitialAssessmentIntegrityService.assertSourcesCurrent(baseline.payloadJson, { clientId: baseline.clientId, patientId: baseline.patientId });
            includeClinicalMoment(baseline);
          }
        } catch (error) {
          toast.error(error instanceof Error ? error.message : 'Revise as fontes da ficha clínica antes de gerar o relatório.');
          return;
        }
        if (candidatesRequest !== assessmentReportCandidateRequestRef.current) return;
        const sources = candidates.filter((item) => evidenceIds.has(item.resultId)).map(buildAssessmentReportSourceSnapshot);
        setAssessmentReportForm((current) => current.patient.patientId === requestedPatient.id
          ? reconcileAssessmentReportSuggestions(current, { ...current, sources }, { fillEmpty: true }) : current);
        setSelectedResult(null);
      }
    }
  };

  const closeAssessmentReportModal = () => {
    if (assessmentReportSaving || assessmentReportSaveInFlightRef.current) return;
    assessmentReportCandidateRequestRef.current += 1;
    setShowAssessmentReportModal(false);
    setAssessmentReportEditingResultId(null);
    setAssessmentReportEditingRevision(null);
    setAssessmentReportGeneratedAt(null);
    setAssessmentReportCandidates([]);
    setAssessmentReportCandidatesLoading(false);
    setAssessmentReportForm(createInitialAssessmentReportForm());
  };

  const handleAssessmentReportPatientChange = async (patientId: string) => {
    const patient = patients.find((item) => item.id === patientId);
    if (!patient) return;

    const nextForm = createInitialAssessmentReportForm({
      mode: assessmentReportForm.mode,
      documentModel: assessmentReportForm.documentModel,
      patient: getAssessmentReportPatientSnapshot(patient),
      professional: assessmentReportForm.authorship.professional,
    });
    nextForm.title = assessmentReportForm.title;
    setAssessmentReportForm(nextForm);
    setAssessmentReportCandidates([]);
    await loadAssessmentReportCandidates(patientId);
  };

  const withoutAssessmentReportSource = (
    current: AssessmentReportEditorForm,
    resultId: string,
  ): AssessmentReportEditorForm => {
      const sources = current.sources.filter((source) => source.resultId !== resultId);
      const sourceIds = new Set(sources.map((source) => source.resultId));
      const cleanEvidence = (ids: string[]) => ids.filter((id) => sourceIds.has(id));
      return {
        ...current,
        sources,
        exportOptions: {
          ...current.exportOptions,
          attachments: {
            ...current.exportOptions.attachments,
            resultIds: current.exportOptions.attachments.resultIds.filter((id) => sourceIds.has(id)),
          },
          history: {
            ...current.exportOptions.history,
            selections: current.exportOptions.history.selections.filter(
              (selection) => sourceIds.has(selection.sourceResultId),
            ),
          },
        },
        cbdf: {
          ...current.cbdf,
          entries: current.cbdf.entries.map((entry) => ({
            ...entry,
            evidenceResultIds: cleanEvidence(entry.evidenceResultIds),
          })),
        },
        icf: {
          ...current.icf,
          domains: {
            bodyFunctions: current.icf.domains.bodyFunctions.map((entry) => ({
              ...entry,
              evidenceResultIds: cleanEvidence(entry.evidenceResultIds),
            })),
            bodyStructures: current.icf.domains.bodyStructures.map((entry) => ({
              ...entry,
              evidenceResultIds: cleanEvidence(entry.evidenceResultIds),
            })),
            activitiesAndParticipation: current.icf.domains.activitiesAndParticipation.map((entry) => ({
              ...entry,
              evidenceResultIds: cleanEvidence(entry.evidenceResultIds),
            })),
            environmentalFactors: current.icf.domains.environmentalFactors.map((entry) => ({
              ...entry,
              evidenceResultIds: cleanEvidence(entry.evidenceResultIds),
            })),
          },
        },
      };
  };

  const removeAssessmentReportSource = (resultId: string) => {
    setAssessmentReportForm((current) => {
      const next = withoutAssessmentReportSource(current, resultId);
      return reconcileAssessmentReportSuggestions(current, next, {
        fillEmpty: false,
      });
    });
  };

  const toggleAssessmentReportSource = (result: AssessmentResultV2) => {
    setAssessmentReportForm((current) => {
      if (result.patientId !== current.patient.patientId) return current;
      const sourceExists = current.sources.some((source) => source.resultId === result.resultId);
      if (sourceExists) {
        const next = withoutAssessmentReportSource(current, result.resultId);
        return reconcileAssessmentReportSuggestions(current, next, {
          fillEmpty: false,
        });
      }

      const next: AssessmentReportEditorForm = {
        ...current,
        sources: [...current.sources, buildAssessmentReportSourceSnapshot(result)].sort(
          (left, right) =>
            new Date(left.snapshot.assessmentDate).getTime()
            - new Date(right.snapshot.assessmentDate).getTime(),
        ),
      };
      return reconcileAssessmentReportSuggestions(current, next, {
        // O primeiro resultado inicializa a minuta. Nas inclusões seguintes,
        // um campo vazio pode ter sido apagado de propósito pelo profissional.
        fillEmpty: current.sources.length === 0,
      });
    });
  };

  const toAssessmentReportEditorForm = (
    payload: AssessmentReportPayloadV2,
  ): AssessmentReportEditorForm => {
    const base = {
      documentModel: payload.documentModel,
      title: payload.title,
      patient: payload.patient,
      audience: payload.audience,
      purpose: payload.purpose,
      authorship: payload.authorship,
      sources: payload.sources,
      exportOptions: payload.exportOptions,
      cbdf: payload.cbdf,
      icf: payload.icf,
    };
    return payload.mode === 'assessment_summary'
      ? { ...base, mode: payload.mode, content: payload.content }
      : { ...base, mode: payload.mode, content: payload.content };
  };

  const openAssessmentReportEditor = async (result: AssessmentResultV2) => {
    if (!isAssessmentReportResult(result) || !clientId) return;
    if (result.clientId !== clientId) {
      toast.error('O relatório aberto pertence a outro tenant. Recarregue a página.');
      return;
    }

    const requestId = ++assessmentReportCandidateRequestRef.current;
    try {
      const [currentResults, professional] = await Promise.all([
        AssessmentResultsV2Service.listByIds(clientId, [result.resultId]),
        getCurrentAssessmentReportProfessional(),
      ]);
      if (requestId !== assessmentReportCandidateRequestRef.current) return;

      const freshResult = currentResults[0];
      if (!freshResult || !isAssessmentReportResult(freshResult)) {
        toast.error('O relatório não foi encontrado neste tenant.');
        return;
      }
      if (getAssessmentSignatureMetadata(freshResult.assetsJson)) {
        toast.error('Relatórios assinados não podem ser alterados. Gere uma nova versão.');
        return;
      }

      const payload = normalizeAssessmentReportPayload(freshResult.payloadJson);
      if (!payload) {
        toast.error('Este relatório não possui uma versão editável compatível.');
        return;
      }
      if (
        payload.mode === 'physiotherapy_technical_report'
        && (
          professional.role !== 'fisioterapeuta'
          || !professional.registration
        )
      ) {
        toast.error(
          'Somente o fisioterapeuta ativo com CREFITO cadastrado pode editar este relatório técnico.',
        );
        return;
      }

      const editorForm = toAssessmentReportEditorForm(payload);
      setAssessmentReportEditingResultId(freshResult.resultId);
      setAssessmentReportEditingRevision({
        updatedAt: freshResult.updatedAt,
        createdAt: freshResult.createdAt,
      });
      setAssessmentReportGeneratedAt(payload.generatedAt);
      setAssessmentReportForm({
        ...editorForm,
        authorship: {
          ...editorForm.authorship,
          professional,
          authoredAt: new Date().toISOString(),
        },
      });
      setAssessmentReportCandidates([]);
      setShowAssessmentReportModal(true);
      await loadAssessmentReportCandidates(freshResult.patientId);
    } catch (error: unknown) {
      if (requestId !== assessmentReportCandidateRequestRef.current) return;
      console.error('Erro ao abrir o editor do relatório de avaliações:', error);
      toast.error(
        error instanceof Error
          ? error.message
          : 'Não foi possível carregar a versão atual do relatório.',
      );
    }
  };

  const handleSaveAssessmentReport = async () => {
    if (!user || !clientId || !selectedAssessmentReportPatient) {
      toast.error('Selecione um paciente válido antes de salvar.');
      return;
    }
    if (assessmentReportSaveInFlightRef.current) return;
    if (assessmentReportEditingResultId && !assessmentReportEditingRevision) {
      toast.error('A revisão original do relatório não foi identificada. Reabra o editor.');
      return;
    }

    const requestedClientId = clientId;
    const requestedPatient = selectedAssessmentReportPatient;
    const requestedForm = assessmentReportForm;
    const requestedEditingResultId = assessmentReportEditingResultId;
    const requestedEditingRevision = assessmentReportEditingRevision;
    const requestedGeneratedAt = assessmentReportGeneratedAt;
    assessmentReportSaveInFlightRef.current = true;
    setAssessmentReportSaving(true);
    try {
      const now = new Date().toISOString();
      const activeProfessional = await getCurrentAssessmentReportProfessional();
      if (activeClientIdRef.current !== requestedClientId) return;
      const finalForm: AssessmentReportEditorForm = {
        ...requestedForm,
        authorship: {
          ...requestedForm.authorship,
          professional: activeProfessional,
          authoredAt: now,
        },
      };
      const validation = validateAssessmentReportForm(finalForm);
      if (!validation.valid) {
        toast.error(validation.errors[0]?.message || 'Revise os campos obrigatórios do relatório.');
        return;
      }

      const clinicalSourceIds = finalForm.sources.filter((source) => source.snapshot.testKey === 'INITIAL_ASSESSMENT').map((source) => source.resultId);
      if (clinicalSourceIds.length) {
        const clinicalSources = await AssessmentResultsV2Service.listByIds(requestedClientId, clinicalSourceIds);
        if (clinicalSources.length !== clinicalSourceIds.length) throw new Error('Uma ficha clínica selecionada não está disponível. Revise as fontes.');
        for (const source of clinicalSources) {
          if (source.patientId !== requestedPatient.id) throw new Error('A ficha clínica selecionada pertence a outro paciente.');
          await InitialAssessmentIntegrityService.assertSourcesCurrent(source.payloadJson, { clientId: requestedClientId, patientId: requestedPatient.id });
        }
      }
      if (activeClientIdRef.current !== requestedClientId) return;

      const payload = createAssessmentReportPayloadV2(finalForm, {
        generatedAt: requestedGeneratedAt ?? now,
        updatedAt: now,
      });
      const findingCounts = countAssessmentReportFindings(payload.sources);
      const period = getAssessmentReportPeriod(payload.sources);
      const summaryJson = {
        title: payload.title,
        documentModel: payload.documentModel,
        mode: payload.mode,
        includedCount: payload.sources.length,
        alteredCount: findingCounts.altered,
        borderlineCount: findingCounts.borderline,
        withinExpectedCount: findingCounts.withinExpected,
        unresolvedCount: findingCounts.notClassified + findingCounts.notInterpretable,
        periodStart: period?.startDate ?? null,
        periodEnd: period?.endDate ?? null,
        severity: payload.mode === 'assessment_summary'
          ? 'Resumo revisado'
          : 'Relatório técnico revisado',
      };
      const patientSnapshot = {
        patientNameSnapshot:
          requestedPatient.personalData?.name
          || requestedPatient.patientFullName
          || 'Paciente',
        patientMedicalRecordSnapshot: getPreferredPatientIdentifier(requestedPatient),
        patientBirthDateSnapshot: requestedPatient.patientDateOfBirth || null,
        patientSexSnapshot: requestedPatient.patientGender || null,
        patientWeightSnapshot: normalizePatientWeightKg(requestedPatient.patientWeightKg),
        patientHeightSnapshot: normalizePatientHeightCm(requestedPatient.patientHeightCm),
      };
      const professionalSnapshot = {
        professionalNameSnapshot: finalForm.authorship.professional.name,
        professionalRegistrationSnapshot: finalForm.authorship.professional.registration,
        professionalRoleSnapshot: finalForm.authorship.professional.role,
      };
      const persistenceInput = {
        notes: `Documento estruturado a partir de ${payload.sources.length} avaliação(ões) revisada(s).`,
        professionalUserId: activeProfessional.userId,
        patientSnapshot,
        professionalSnapshot,
        summaryJson,
        payloadJson: payload,
        assetsJson: {
          generator: {
            schema: 'revahub.assessment-report',
            version: 2,
            generatedByUserId: user.userId,
            generatedByUserName: user.userDisplayName || 'Profissional',
            generatedAt: now,
          },
        },
      };

      const savedResult = requestedEditingResultId
        ? await AssessmentReportV2Service.update(
            requestedEditingResultId,
            requestedClientId,
            requestedPatient.id,
            {
              ...persistenceInput,
              expectedRevision: requestedEditingRevision!,
            },
          )
        : await AssessmentReportV2Service.create({
            ...persistenceInput,
            clientId: requestedClientId,
            patientId: requestedPatient.id,
            assessmentDate: now,
            createdByUserId: user.userId,
          });
      if (activeClientIdRef.current !== requestedClientId) return;

      toast.success(
        requestedEditingResultId
          ? 'Relatório atualizado. Revise o PDF antes de assinar.'
          : 'Relatório criado. Revise o PDF antes de assinar.',
      );
      setShowAssessmentReportModal(false);
      setAssessmentReportEditingResultId(null);
      setAssessmentReportEditingRevision(null);
      setAssessmentReportGeneratedAt(null);
      setAssessmentReportCandidates([]);
      setAssessmentReportForm(createInitialAssessmentReportForm());
      await Promise.all([loadDashboardData(), loadDraftSessions()]);
      if (activeClientIdRef.current === requestedClientId) {
        setSelectedResult(savedResult);
      }
    } catch (error: unknown) {
      if (activeClientIdRef.current !== requestedClientId) return;
      console.error('Erro ao salvar relatório de avaliações:', error);
      const message =
        error instanceof Error && error.message === 'ASSESSMENT_RESULT_CONCURRENT_MODIFICATION'
          ? 'O relatório foi alterado em outra sessão. Feche e reabra o editor antes de salvar.'
          : error instanceof Error
            ? error.message
            : 'Não foi possível salvar o relatório.';
      toast.error(message);
    } finally {
      assessmentReportSaveInFlightRef.current = false;
      setAssessmentReportSaving(false);
    }
  };

  const filteredResults = useMemo(() => {
    return results.filter((result) => {
      const matchesPatient = resultsPatientFilterId === 'all' || result.patientId === resultsPatientFilterId;
      const matchesProfessional =
        resultsProfessionalFilter === 'all' || (result.professionalNameSnapshot || '') === resultsProfessionalFilter;
      const matchesStatus =
        statusFilter === 'all' ? true : statusFilter === 'active' ? result.status !== 'cancelled' : result.status === 'cancelled';

      if (!matchesPatient) return false;
      if (!matchesProfessional) return false;
      if (!matchesStatus) return false;
      return true;
    });
  }, [results, statusFilter, resultsPatientFilterId, resultsProfessionalFilter]);

  const patientResultGroups = useMemo(() => {
    const groups = new Map<
      string,
      {
        patientId: string;
        patientName: string;
        patientIdentifier: string | null;
        latestDate: string;
        results: AssessmentResultV2[];
      }
    >();

    filteredResults.forEach((result) => {
      const patientId = result.patientId || result.patientNameSnapshot || 'sem-paciente';
      const existing = groups.get(patientId);

      if (!existing) {
        groups.set(patientId, {
          patientId,
          patientName: result.patientNameSnapshot || 'Paciente',
          patientIdentifier: getAssessmentResultIdentifier(result) ?? getPreferredPatientIdentifier(patientLookup.get(result.patientId)),
          latestDate: result.assessmentDate,
          results: [result],
        });
        return;
      }

      existing.results.push(result);
      if (!existing.patientIdentifier) {
        existing.patientIdentifier =
          getAssessmentResultIdentifier(result) ?? getPreferredPatientIdentifier(patientLookup.get(result.patientId));
      }
      if (new Date(result.assessmentDate).getTime() > new Date(existing.latestDate).getTime()) {
        existing.latestDate = result.assessmentDate;
      }
    });

    return Array.from(groups.values()).sort(
      (a, b) => new Date(b.latestDate).getTime() - new Date(a.latestDate).getTime(),
    );
  }, [filteredResults, patientLookup]);

  const runnerTestStatuses = getRunnerTestStatuses();
  const runnerCompletedCount = runnableSelectedTests.filter((testKey) =>
    isRunnerTestPayloadCompleted(testKey, runnerResults[testKey] ?? resolveRunnerPayload(testKey, runnerSavedResults[testKey])),
  ).length;
  const allRunnerTestsCompleted =
    runnableSelectedTests.length > 0 &&
    runnableSelectedTests.every((testKey) =>
      isRunnerTestPayloadCompleted(testKey, runnerResults[testKey] ?? resolveRunnerPayload(testKey, runnerSavedResults[testKey])),
    );

  if (!clientId) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-amber-900">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5" />
          <div>
            <h2 className="text-lg font-semibold">Tenant não selecionado</h2>
            <p className="mt-1 text-sm">Selecione uma clínica antes de acessar a nova área de avaliações.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Avaliações Clínicas</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <ActionButton
            type="button"
            onClick={() => void handleRefresh()}
            disabled={loading || patientsLoading}
            variant="neutral"
          >
            <RefreshCw className={`h-4 w-4 ${loading || patientsLoading ? 'animate-spin' : ''}`} />
            Atualizar
          </ActionButton>
          <ActionButton
            type="button"
            onClick={openCreateModal}
            variant="primary"
          >
            <PlusCircle className="h-4 w-4" />
            Nova avaliação
          </ActionButton>
          <ActionButton
            type="button"
            onClick={() =>
              void openAssessmentReportModal(
                resultsPatientFilterId !== 'all' ? resultsPatientFilterId : undefined,
              )
            }
            disabled={patients.length === 0}
            variant="secondary"
          >
            <FileText className="h-4 w-4" />
            Gerar relatório
          </ActionButton>
        </div>
      </div>

      {showPeakFlowDiary && (
        <PeakFlowDiaryDialog patients={patients} patientsLoading={patientsLoading} selectedPatientId={diaryPatientId}
          onPatientSelect={(patientId) => {
            setDiaryPatientId(patientId);
            if (!getPatientNewCareBlockReason(patientLookup.get(patientId))) setForm(current => ({ ...current, patientId }));
          }} onClose={() => setShowPeakFlowDiary(false)} onBack={returnToCreateFromDiary}>
            {diaryPatient && getPatientNewCareBlockReason(diaryPatient) ? (
                <p className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600">{getPatientNewCareBlockReason(diaryPatient)}</p>
              ) : diaryPatient && selectedClient?.clientSlug && diaryPatient.patientDateOfBirth ? (
                <PeakFlowDiaryManager key={diaryPatient.id} clientId={clientId} clientSlug={selectedClient.clientSlug} patientId={diaryPatient.id} patientBirthDate={diaryPatient.patientDateOfBirth} patientName={diaryPatient.personalData?.name || diaryPatient.patientFullName || 'Paciente'} createdByUserId={user?.userId} timezone={snapshot.branding?.rawSettings?.timeZone || undefined} onExport={exportPeakFlowDiary} />
              ) : diaryPatient ? <p className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600">Complete a data de nascimento do paciente e o endereço da clínica para gerar o acesso individual.</p> : null}
        </PeakFlowDiaryDialog>
      )}

      {draftSessions.length > 0 && (
        <div className="rounded-lg border bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Layers3 className="h-5 w-5 text-primary-600" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Rascunhos</h3>
                <p className="text-sm text-gray-500">Sessões iniciadas e salvas por etapa, prontas para retomada.</p>
              </div>
            </div>
            <div className="rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700">
              {draftSessions.length} rascunho(s)
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {draftSessions.map((draft) => {
              const selectedTests = normalizeSelectedTests(draft.session.selectedTests);
              const completedTests = draft.results
                .filter((result) =>
                  isRunnerTestPayloadCompleted(
                    result.testKey as AssessmentV2TestKey,
                    resolveRunnerPayload(result.testKey as AssessmentV2TestKey, result),
                  ),
                )
                .map((result) => result.testKey);

              return (
                <div key={draft.session.sessionId} className="rounded-lg border border-amber-200 bg-amber-50/50 p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-xs font-black uppercase tracking-[0.2em] text-amber-700">Rascunho ativo</div>
                      <h4 className="mt-1 text-lg font-semibold text-gray-900">{draft.patientName}</h4>
                      <p className="text-sm text-gray-600">
                        {new Date(draft.session.sessionDate).toLocaleString('pt-BR')}
                      </p>
                    </div>
                    <div className="rounded-full bg-white px-3 py-1 text-sm font-medium text-gray-700">
                      {completedTests.length}/{selectedTests.length} testes salvos
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {selectedTests.map((testKey) => {
                      const completed = completedTests.includes(testKey);
                      return (
                        <span
                          key={`${draft.session.sessionId}-${testKey}`}
                          className={`rounded-full px-3 py-1 text-xs font-medium ${completed ? 'bg-emerald-100 text-emerald-700' : 'bg-white text-gray-600'}`}
                        >
                          {getAssessmentV2Label(testKey)}
                        </span>
                      );
                    })}
                  </div>

                  {draft.session.notes && (
                    <div className="mt-4 rounded-md bg-white p-3 text-sm text-gray-700">
                      {draft.session.notes.length > 160 ? `${draft.session.notes.slice(0, 160)}...` : draft.session.notes}
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => void openDraftSession(draft)}
                      disabled={saving}
                      className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Continuar
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDiscardDraft(draft)}
                      disabled={saving}
                      className="inline-flex items-center rounded-md border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <AlertTriangle className="mr-2 h-4 w-4" />
                      Descartar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_220px_240px_auto]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              className="w-full rounded-md border py-2 pl-10 pr-4 focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
              placeholder="Buscar em todas as avaliações por paciente, profissional, teste ou resultado..."
              value={search}
              onChange={(event) => handleResultsSearchChange(event.target.value)}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(event) =>
              void handleStatusFilterChange(event.target.value as 'active' | 'cancelled' | 'all')
            }
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
          >
            <option value="active">Somente ativas</option>
            <option value="cancelled">Somente invalidadas</option>
            <option value="all">Todas</option>
          </select>
          <select
            value={resultsProfessionalFilter}
            onChange={(event) => setResultsProfessionalFilter(event.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">Todos os profissionais</option>
            {resultsProfessionalOptions.map((professionalName) => (
              <option key={professionalName} value={professionalName}>
                {professionalName}
              </option>
            ))}
          </select>
          <div className="flex w-full overflow-x-auto rounded-md border border-gray-200 bg-gray-50 p-1 sm:inline-flex sm:w-auto">
            <button
              type="button"
              onClick={() => setResultsViewMode('list')}
              className={`inline-flex min-w-[92px] items-center justify-center rounded px-3 py-1.5 text-sm font-medium transition ${
                resultsViewMode === 'list'
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="mr-1.5 h-4 w-4" />
              Lista
            </button>
            <button
              type="button"
              onClick={() => setResultsViewMode('cards')}
              className={`inline-flex min-w-[92px] items-center justify-center rounded px-3 py-1.5 text-sm font-medium transition ${
                resultsViewMode === 'cards'
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Layers3 className="mr-1.5 h-4 w-4" />
              Cards
            </button>
            <button
              type="button"
              onClick={() => setResultsViewMode('patient')}
              className={`inline-flex min-w-[118px] items-center justify-center rounded px-3 py-1.5 text-sm font-medium transition ${
                resultsViewMode === 'patient'
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Users className="mr-1.5 h-4 w-4" />
              Por paciente
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-lg border bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-3">
          <FileText className="h-5 w-5 text-primary-600" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Resultados salvos</h3>
            <p className="text-sm text-gray-500">Resultados organizados por teste, com visualização e exportação individual.</p>
          </div>
        </div>

        {filteredPatientLabel && (
          <div className="mb-4 rounded-lg border border-primary-100 bg-primary-50 px-4 py-3 text-sm text-primary-900">
            Filtrando avaliações para <span className="font-semibold">{filteredPatientLabel}</span>.
          </div>
        )}

        {filteredProfessionalLabel && (
          <div className="mb-4 rounded-lg border border-sky-100 bg-sky-50 px-4 py-3 text-sm text-sky-900">
            Filtrando avaliações do profissional <span className="font-semibold">{filteredProfessionalLabel}</span>.
          </div>
        )}

        {loading ? (
          <div className="py-10 text-center text-sm text-gray-500">Carregando resultados...</div>
        ) : filteredResults.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-500">
            Nenhuma avaliação encontrada para este tenant.
          </div>
        ) : resultsViewMode === 'list' ? (
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <div className="hidden grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)_130px_150px_auto] gap-3 bg-gray-50 px-4 py-3 text-xs font-semibold uppercase text-gray-500 lg:grid">
              <div>Paciente</div>
              <div>Teste</div>
              <div>Resultado</div>
              <div>Status</div>
              <div className="text-right">Ações</div>
            </div>
            <div className="divide-y divide-gray-100">
              {filteredResults.map((result) => {
                const isCancelled = result.status === 'cancelled';
                const isSigned = Boolean(getAssessmentSignatureMetadata(result.assetsJson)?.verificationToken);
                const severityTone = getResultSeverityTone(result);
                const statusLabel = isCancelled ? 'Invalidada' : getResultSeverityLabel(result);

                return (
                  <div
                    key={result.resultId}
                    className={`grid grid-cols-1 gap-3 px-4 py-3 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)_130px_150px_auto] lg:items-center ${
                      isCancelled ? 'bg-red-50/40' : 'bg-white'
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-gray-900">{result.patientNameSnapshot || 'Paciente'}</div>
                      <div className="mt-1 text-xs text-gray-500">
                        {(getAssessmentResultIdentifier(result) ?? getPreferredPatientIdentifier(patientLookup.get(result.patientId))) || 'Sem identificador'} • {new Date(result.assessmentDate).toLocaleString('pt-BR')}
                      </div>
                    </div>

                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-primary-700">{getAssessmentV2ResultLabel(result)}</div>
                      <div className="mt-1 truncate text-xs text-gray-500">{result.professionalNameSnapshot || 'Profissional não informado'}</div>
                    </div>

                    <div>
                      <div className="text-xs uppercase text-gray-500 lg:hidden">Resultado</div>
                      <div className="text-sm font-semibold text-gray-900">{getResultPrimaryMetric(result)}</div>
                    </div>

                    <div>
                      <div className="text-xs uppercase text-gray-500 lg:hidden">Status</div>
                      <div className="flex flex-wrap gap-2">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${severityTone}`}>{statusLabel}</span>
                        {isSigned && (
                          <span className="inline-flex rounded-full bg-sky-100 px-2.5 py-1 text-xs font-medium text-sky-700">
                            Assinada
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 lg:justify-end">
                      <ActionButton
                        type="button"
                        onClick={() => setSelectedResult(result)}
                        variant="neutral"
                        size="sm"
                      >
                        <Eye className="h-4 w-4" />
                        Ver
                      </ActionButton>
                      <ActionButton
                        type="button"
                        onClick={() => void runPdfAction(result, 'download')}
                        disabled={resultActionLoading}
                        variant="neutral"
                        size="sm"
                      >
                        <Download className="h-4 w-4" />
                        PDF
                      </ActionButton>
                      {!isCancelled && (
                        isSigned ? (
                          <ActionButton
                            type="button"
                            onClick={() => openVerificationPage(getAssessmentSignatureMetadata(result.assetsJson)!.verificationToken)}
                            variant="secondary"
                            size="sm"
                          >
                            <Eye className="h-4 w-4" />
                            Verificar
                          </ActionButton>
                        ) : (
                          <ActionButton
                            type="button"
                            onClick={() => void handleSignResult(result)}
                            disabled={signingResultId === result.resultId}
                            variant="success"
                            size="sm"
                          >
                            <FileText className="h-4 w-4" />
                            Assinar
                          </ActionButton>
                        )
                      )}
                      {isCancelled ? (
                        <ActionButton
                          type="button"
                          onClick={() => setStatusModalState({ mode: 'restore', result })}
                          variant="success"
                          size="sm"
                        >
                          <RotateCcw className="h-4 w-4" />
                          Restaurar
                        </ActionButton>
                      ) : (
                        <ActionButton
                          type="button"
                          onClick={() => setStatusModalState({ mode: 'invalidate', result })}
                          variant="danger"
                          size="sm"
                        >
                          <AlertTriangle className="h-4 w-4" />
                          Invalidar
                        </ActionButton>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : resultsViewMode === 'patient' ? (
          <div className="space-y-4">
            {patientResultGroups.map((group) => (
              <div key={group.patientId} className="overflow-hidden rounded-lg border border-gray-200">
                <div className="flex flex-wrap items-center justify-between gap-3 bg-gray-50 px-4 py-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-primary-700">
                      <Users className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <h4 className="truncate text-base font-semibold text-gray-900">{group.patientName}</h4>
                      <p className="text-sm text-gray-500">
                        {group.patientIdentifier || 'Sem identificador'} • Última avaliação {new Date(group.latestDate).toLocaleString('pt-BR')}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-white px-3 py-1 text-sm font-medium text-gray-700">
                      {group.results.length} {group.results.length === 1 ? 'avaliação' : 'avaliações'}
                    </span>
                    <ActionButton
                      type="button"
                      onClick={() => void openAssessmentReportModal(group.patientId)}
                      variant="secondary"
                      size="sm"
                    >
                      <FileText className="h-4 w-4" />
                      Relatório
                    </ActionButton>
                  </div>
                </div>

                <div className="divide-y divide-gray-100">
                  {group.results.map((result) => {
                    const isCancelled = result.status === 'cancelled';
                    const isSigned = Boolean(getAssessmentSignatureMetadata(result.assetsJson)?.verificationToken);
                    const severityTone = getResultSeverityTone(result);
                    const statusLabel = isCancelled ? 'Invalidada' : getResultSeverityLabel(result);

                    return (
                      <div
                        key={result.resultId}
                        className={`grid grid-cols-1 gap-3 px-4 py-3 lg:grid-cols-[minmax(0,1.2fr)_150px_170px_auto] lg:items-center ${
                          isCancelled ? 'bg-red-50/40' : 'bg-white'
                        }`}
                      >
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="truncate text-sm font-semibold text-gray-900">{getAssessmentV2ResultLabel(result)}</div>
                            <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${severityTone}`}>{statusLabel}</span>
                            {isSigned && (
                              <span className="rounded-full bg-sky-100 px-2.5 py-1 text-xs font-medium text-sky-700">
                                Assinada
                              </span>
                            )}
                          </div>
                          <div className="mt-1 text-xs text-gray-500">
                            {new Date(result.assessmentDate).toLocaleString('pt-BR')} • {result.professionalNameSnapshot || 'Profissional não informado'}
                          </div>
                        </div>

                        <div>
                          <div className="text-xs uppercase text-gray-500">Resultado</div>
                          <div className="mt-0.5 text-sm font-semibold text-gray-900">{getResultPrimaryMetric(result)}</div>
                        </div>

                        <div>
                          <div className="text-xs uppercase text-gray-500">Registro</div>
                          <div className="mt-0.5 text-sm text-gray-700">{result.professionalRegistrationSnapshot || 'Sem registro'}</div>
                        </div>

                        <div className="flex flex-wrap gap-2 lg:justify-end">
                          <ActionButton
                            type="button"
                            onClick={() => setSelectedResult(result)}
                            variant="neutral"
                            size="sm"
                          >
                            <Eye className="h-4 w-4" />
                            Ver
                          </ActionButton>
                          <ActionButton
                            type="button"
                            onClick={() => void runPdfAction(result, 'download')}
                            disabled={resultActionLoading}
                            variant="neutral"
                            size="sm"
                          >
                            <Download className="h-4 w-4" />
                            PDF
                          </ActionButton>
                          {!isCancelled && (
                            isSigned ? (
                              <ActionButton
                                type="button"
                                onClick={() => openVerificationPage(getAssessmentSignatureMetadata(result.assetsJson)!.verificationToken)}
                                variant="secondary"
                                size="sm"
                              >
                                <Eye className="h-4 w-4" />
                                Verificar
                              </ActionButton>
                            ) : (
                              <ActionButton
                                type="button"
                                onClick={() => void handleSignResult(result)}
                                disabled={signingResultId === result.resultId}
                                variant="success"
                                size="sm"
                              >
                                <FileText className="h-4 w-4" />
                                Assinar
                              </ActionButton>
                            )
                          )}
                          {isCancelled ? (
                            <ActionButton
                              type="button"
                              onClick={() => setStatusModalState({ mode: 'restore', result })}
                              variant="success"
                              size="sm"
                            >
                              <RotateCcw className="h-4 w-4" />
                              Restaurar
                            </ActionButton>
                          ) : (
                            <ActionButton
                              type="button"
                              onClick={() =>
                                setStatusModalState({
                                  mode: 'invalidate',
                                  result,
                                })
                              }
                              variant="danger"
                              size="sm"
                            >
                              <AlertTriangle className="h-4 w-4" />
                              Invalidar
                            </ActionButton>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {filteredResults.map((result) => {
              const isCancelled = result.status === 'cancelled';
              const isSigned = Boolean(getAssessmentSignatureMetadata(result.assetsJson)?.verificationToken);
              const severityTone = getResultSeverityTone(result);
              const statusLabel = isCancelled ? 'Invalidada' : getResultSeverityLabel(result);
              const latestInvalidation = getLatestAssessmentResultStatusEntry(result, 'invalidated');

              return (
                <div key={result.resultId} className={`rounded-lg border p-5 ${isCancelled ? 'border-red-200 bg-red-50/40' : 'border-gray-200'}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">{result.patientNameSnapshot || 'Paciente'}</h4>
                      <p className="text-sm font-medium text-primary-700">{getAssessmentV2ResultLabel(result)}</p>
                      <p className="text-sm text-gray-500">{new Date(result.assessmentDate).toLocaleString('pt-BR')}</p>
                    </div>
                    <div className="flex flex-wrap justify-end gap-2">
                      <span className={`rounded-full px-3 py-1 text-xs font-medium ${severityTone}`}>{statusLabel}</span>
                      {isSigned && (
                        <span className="rounded-full bg-sky-100 px-3 py-1 text-xs font-medium text-sky-700">Assinada</span>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                    <div className="rounded-md bg-gray-50 p-3">
                      <div className="text-xs uppercase text-gray-500">
                        {isAnyAssessmentReportResult(result)
                          ? 'Composição'
                          : result.testKey === 'TC6'
                          ? 'Capacidade funcional'
                          : result.testKey === '6MST'
                          ? 'Subidas totais'
                          : result.testKey === 'CHESTER'
                          ? 'Nível alcançado'
                          : result.testKey === '5xSTS'
                          ? 'Tempo'
                          : result.testKey === 'STS_30S' || result.testKey === 'STS_1MIN'
                          ? 'Repetições'
                          : result.testKey === 'ONE_RM'
                          ? 'Grupos musculares'
                          : result.testKey === 'TUG'
                          ? 'Tempo'
                          : result.testKey === '4MGS'
                          ? 'Velocidade'
                          : result.testKey === 'ACT'
                          ? 'Controle da asma'
                          : result.testKey === 'AQLQ'
                            ? 'Qualidade de vida na asma'
                          : result.testKey === 'mMRC'
                          ? 'Grau'
                          : result.testKey === 'SGRQ'
                            ? 'Qualidade respiratória'
                          : result.testKey === 'SAQ'
                            ? 'Controle da angina'
                          : result.testKey === 'PFSDQ-M'
                            ? 'Status funcional'
                          : result.testKey === 'HADS'
                            ? 'Domínios'
                            : result.testKey === 'MLHFQ'
                              ? 'Qualidade de vida'
                              : 'Pontuação'}
                      </div>
                      <div className="mt-1 text-2xl font-semibold text-gray-900">{getResultPrimaryMetric(result)}</div>
                      {isAnyAssessmentReportResult(result) && (
                        <div className="mt-1 text-xs text-gray-500">{result.summaryJson?.title || 'Relatório de avaliações'}</div>
                      )}
                      {result.testKey === 'ACT' && (
                        <div className="mt-1 text-xs text-gray-500">{result.summaryJson?.classification ?? 'Classificação indisponível'}</div>
                      )}
                      {result.testKey === 'AQLQ' && (
                        <div className="mt-1 text-xs text-gray-500">
                          Sintomas {result.summaryJson?.symptomsScore ?? '—'} • Atividades {result.summaryJson?.activityLimitationScore ?? '—'}
                        </div>
                      )}
                      {result.testKey === 'LCADL' && typeof result.summaryJson?.percentage === 'number' && (
                        <div className="mt-1 text-xs text-gray-500">{result.summaryJson.percentage.toFixed(1)}% de comprometimento</div>
                      )}
                      {result.testKey === 'SAQ' && (
                        <div className="mt-1 text-xs text-gray-500">
                          Física {result.summaryJson?.physicalLimitation ?? '—'} • Frequência {result.summaryJson?.anginaFrequency ?? '—'}
                        </div>
                      )}
                      {result.testKey === 'SGRQ' && (
                        <div className="mt-1 text-xs text-gray-500">
                          Sintomas {result.summaryJson?.symptoms ?? '—'} • Atividade {result.summaryJson?.activity ?? '—'}
                        </div>
                      )}
                      {result.testKey === 'PFSDQ-M' && (
                        <div className="mt-1 text-xs text-gray-500">
                          D {result.summaryJson?.dyspneaScore ?? '—'} • F {result.summaryJson?.fatigueScore ?? '—'} • M {result.summaryJson?.changeScore ?? '—'}
                        </div>
                      )}
                      {result.testKey === 'MLHFQ' && (
                        <div className="mt-1 text-xs text-gray-500">
                          Físico {result.summaryJson?.physicalScore ?? '—'} • Emocional {result.summaryJson?.emotionalScore ?? '—'}
                        </div>
                      )}
                      {result.testKey === 'TC6' && (
                        <div className="mt-1 text-xs text-gray-500">
                          {typeof result.summaryJson?.percentPredicted === 'number'
                            ? `${result.summaryJson.percentPredicted.toFixed(1)}% do previsto`
                            : 'Sem percentual previsto'}
                          {' • '}
                          {result.summaryJson?.selectedReferenceLabel || 'Sem referência'}
                        </div>
                      )}
                      {result.testKey === '6MST' && (
                        <div className="mt-1 text-xs text-gray-500">
                          {typeof result.summaryJson?.percentPredicted === 'number'
                            ? `${result.summaryJson.percentPredicted.toFixed(1)}% do previsto`
                            : 'Sem percentual previsto'}
                          {' • '}
                          {result.summaryJson?.normativeBandLabel || 'Faixa normativa indisponível'}
                        </div>
                      )}
                      {result.testKey === 'CHESTER' && (
                        <div className="mt-1 text-xs text-gray-500">
                          {result.summaryJson?.terminationReasonLabel || 'Encerramento não informado'}
                          {' • '}
                          {typeof result.summaryJson?.maxCadence === 'number'
                            ? `${result.summaryJson.maxCadence} passos/min`
                            : 'Cadência máxima indisponível'}
                        </div>
                      )}
                      {result.testKey === 'TUG' && (
                        <div className="mt-1 text-xs text-gray-500">
                          {result.summaryJson?.walkingAid || 'Sem dispositivo'}
                          {' • '}
                          {result.summaryJson?.comparableToReference ? 'Comparável ao protocolo' : 'Interpretar com cautela'}
                        </div>
                      )}
                      {result.testKey === '4MGS' && (
                        <div className="mt-1 text-xs text-gray-500">
                          {typeof result.summaryJson?.percentPredicted === 'number'
                            ? `${result.summaryJson.percentPredicted}% do previsto`
                            : 'Referência não calculada'}
                          {' • '}
                          {result.summaryJson?.walkingAid || 'Sem dispositivo'}
                        </div>
                      )}
                      {(result.testKey === '5xSTS' || result.testKey === 'STS_30S' || result.testKey === 'STS_1MIN') && (
                        <div className="mt-1 text-xs text-gray-500">
                          {typeof result.summaryJson?.attemptCount === 'number'
                            ? `${result.summaryJson.attemptCount} tentativa(s)`
                            : 'Tentativas indisponíveis'}
                          {' • '}
                          {result.summaryJson?.workflowStatus === 'awaiting_second_attempt' ? 'Aguardando 2ª tentativa' : 'Workflow concluído'}
                        </div>
                      )}
                    </div>
                    <div className="rounded-md bg-gray-50 p-3">
                      <div className="text-xs uppercase text-gray-500">Profissional</div>
                      <div className="mt-1 font-medium text-gray-900">{result.professionalNameSnapshot || '—'}</div>
                      <div className="text-xs text-gray-500">{result.professionalRegistrationSnapshot || 'Sem registro'}</div>
                    </div>
                  </div>

                  {result.notes && (
                    <div className="mt-3 rounded-md bg-blue-50 p-3 text-sm text-blue-900">
                      {result.notes.length > 160 ? `${result.notes.slice(0, 160)}...` : result.notes}
                    </div>
                  )}

                  {isCancelled && latestInvalidation && (
                    <div className="mt-3 rounded-md border border-red-200 bg-white p-3 text-sm text-red-900">
                      <div className="font-semibold">Avaliação invalidada</div>
                      <div className="mt-1">{latestInvalidation.justification}</div>
                      <div className="mt-1 text-xs text-red-700">
                        {latestInvalidation.userName} • {new Date(latestInvalidation.timestamp).toLocaleString('pt-BR')}
                      </div>
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedResult(result)}
                      className="inline-flex items-center rounded-md border px-3 py-1.5 text-sm hover:bg-gray-50"
                    >
                      <Eye className="mr-1 h-4 w-4" />
                      Ver resultado
                    </button>
                    <button
                      type="button"
                      onClick={() => void runPdfAction(result, 'download')}
                      disabled={resultActionLoading}
                      className="inline-flex items-center rounded-md border px-3 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <Download className="mr-1 h-4 w-4" />
                      PDF
                    </button>
                    {!isCancelled && (
                      isSigned ? (
                        <button
                          type="button"
                          onClick={() => openVerificationPage(getAssessmentSignatureMetadata(result.assetsJson)!.verificationToken)}
                          className="inline-flex items-center rounded-md border border-sky-200 px-3 py-1.5 text-sm text-sky-700 hover:bg-sky-50"
                        >
                          <Eye className="mr-1 h-4 w-4" />
                          Verificar
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => void handleSignResult(result)}
                          disabled={signingResultId === result.resultId}
                          className="inline-flex items-center rounded-md border border-emerald-200 px-3 py-1.5 text-sm text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <FileText className="mr-1 h-4 w-4" />
                          Assinar
                        </button>
                      )
                    )}
                    {isCancelled ? (
                      <button
                        type="button"
                        onClick={() => setStatusModalState({ mode: 'restore', result })}
                        className="inline-flex items-center rounded-md border border-emerald-200 px-3 py-1.5 text-sm text-emerald-700 hover:bg-emerald-50"
                      >
                        <RotateCcw className="mr-1 h-4 w-4" />
                        Restaurar
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setStatusModalState({ mode: 'invalidate', result })}
                        className="inline-flex items-center rounded-md border border-red-200 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50"
                      >
                        <AlertTriangle className="mr-1 h-4 w-4" />
                        Invalidar
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-6 flex flex-col gap-3 border-t border-gray-200 pt-4 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between">
          <div>
            Exibindo <span className="font-semibold text-gray-900">{resultsRangeStart}–{resultsRangeEnd}</span> de{' '}
            <span className="font-semibold text-gray-900">{resultsTotal}</span> avaliações
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2">
              <span>Por página</span>
              <select
                value={resultsPageSize}
                onChange={(event) =>
                  void handleResultsPageSizeChange(Number(event.target.value) as AssessmentResultsPageSize)
                }
                disabled={loading}
                className="rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 disabled:opacity-60"
                aria-label="Resultados por página"
              >
                {ASSESSMENT_RESULTS_PAGE_SIZES.map((pageSize) => (
                  <option key={pageSize} value={pageSize}>{pageSize}</option>
                ))}
              </select>
            </label>
            <div className="flex items-center gap-2">
              <ActionButton
                type="button"
                onClick={() => void handleResultsPageChange(resultsPage - 1)}
                disabled={loading || resultsPage <= 1}
                variant="neutral"
                size="sm"
              >
                <ChevronLeft className="h-4 w-4" />
                Anterior
              </ActionButton>
              <span className="min-w-[96px] text-center font-medium text-gray-700">
                Página {resultsPage} de {resultsPageCount}
              </span>
              <ActionButton
                type="button"
                onClick={() => void handleResultsPageChange(resultsPage + 1)}
                disabled={loading || resultsPage >= resultsPageCount}
                variant="neutral"
                size="sm"
              >
                Próxima
                <ChevronRight className="h-4 w-4" />
              </ActionButton>
            </div>
          </div>
        </div>
      </div>

      {showCreateModal && (
        <AssessmentCreateDialog
          form={form}
          patients={patients}
          patientsLoading={patientsLoading}
          selectedTests={runnableSelectedTests}
          quickTests={quickAssessmentTests}
          usageByKey={assessmentTestUsageByKey}
          hasTenantUsage={hasTenantAssessmentUsage}
          groups={filteredAssessmentGroups}
          visibleTestCount={visibleAssessmentTestCount}
          search={assessmentPickerSearch}
          openGroupIds={openAssessmentGroupIds}
          openSectionIds={openAssessmentSectionIds}
          saving={saving}
          onPatientSelect={(patientId) => {
            const patient = patients.find(item => item.id === patientId);
            const blockReason = getPatientNewCareBlockReason(patient);
            if (patient && blockReason) {
              toast.error(blockReason === 'clinical_inactive'
                ? 'Paciente está inativo assistencialmente e não pode receber novas avaliações.'
                : 'Paciente inativado ou arquivado não pode receber novas avaliações.');
              return;
            }
            setForm(current => ({ ...current, patientId }));
          }}
          onDateTimeChange={(datetime) => setForm(current => ({ ...current, datetime }))}
          onNotesChange={(notes) => setForm(current => ({ ...current, notes }))}
          onSearchChange={setAssessmentPickerSearch}
          onToggleTest={toggleSelectedTest}
          onToggleGroup={toggleAssessmentGroup}
          onToggleSection={toggleAssessmentSection}
          onClose={closeCreateModal}
          onStart={handleStartRunner}
          onGeneratePublicInvite={() => void handleGeneratePublicInvite()}
          onOpenPeakFlowDiary={openPeakFlowDiaryFromCreate}
        />
      )}

      {showRunnerModal && selectedPatient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Executar avaliação</h3>
                <p className="text-sm text-gray-500">
                  Paciente: {selectedPatient.personalData?.name || selectedPatient.patientFullName}
                </p>
              </div>
              <button className="text-gray-400 hover:text-gray-600" onClick={closeRunner} disabled={saving}>
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="min-h-0 space-y-4 overflow-y-auto px-6 py-5">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                <div className="rounded-lg border bg-gray-50 p-3 text-sm">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Layers3 className="h-4 w-4" />
                    Progresso
                  </div>
                  <div className="mt-1 font-medium text-gray-900">
                    {runnerCompletedCount}/{runnableSelectedTests.length} testes salvos
                  </div>
                </div>
                <div className="rounded-lg border bg-gray-50 p-3 text-sm">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Calendar className="h-4 w-4" />
                    Data
                  </div>
                  <div className="mt-1 font-medium text-gray-900">{new Date(form.datetime).toLocaleString('pt-BR')}</div>
                </div>
                <div className="rounded-lg border bg-gray-50 p-3 text-sm">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Stethoscope className="h-4 w-4" />
                    Teste ativo
                  </div>
                  <div className="mt-1 font-medium text-gray-900">
                    {runnerActiveTest ? getAssessmentV2Label(runnerActiveTest) : 'Nenhum teste selecionado'}
                  </div>
                </div>
                <div className="rounded-lg border bg-gray-50 p-3 text-sm">
                  <div className="flex items-center gap-2 text-gray-500">
                    <FileText className="h-4 w-4" />
                    Observações
                  </div>
                  <div className="mt-1 font-medium text-gray-900">{form.notes || 'Sem observações'}</div>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div className="mb-3 flex flex-wrap gap-2">
                  {runnableSelectedTests.map((testKey) => {
                    const status = runnerTestStatuses[testKey];
                    const testLabel = getAssessmentV2Label(testKey);
                    return (
                      <div key={testKey} className="relative inline-flex pt-1 pr-1">
                        <button
                          type="button"
                          onClick={() => setRunnerActiveTest(testKey)}
                          className={`rounded-full border px-3 py-1.5 pr-7 text-sm font-medium transition ${
                            status === 'completed'
                              ? 'border-emerald-200 bg-emerald-100 text-emerald-700'
                              : status === 'in_progress'
                                ? 'border-primary-200 bg-primary-100 text-primary-700'
                                : 'border-gray-200 bg-white text-gray-700 hover:border-primary-200 hover:text-primary-700'
                          }`}
                        >
                          {testLabel}
                        </button>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleRemoveRunnerTest(testKey);
                          }}
                          disabled={saving}
                          aria-label={`Remover ${testLabel} da avaliação`}
                          title={`Remover ${testLabel} da avaliação`}
                          className="absolute right-0 top-0 inline-flex h-5 w-5 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
                <p className="text-sm text-gray-600">
                  Cada teste concluído é salvo individualmente. O PDF e a impressão permanecem os mesmos de hoje, um por teste.
                </p>
                {runnerSession && (
                  <p className="mt-2 text-xs text-amber-700">
                    Rascunho em andamento. Você pode fechar este runner e retomar depois sem perder os testes já concluídos.
                  </p>
                )}
              </div>

              {runnerActiveTest ? (
                renderRunnerTest(runnerActiveTest)
              ) : (
                <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-500">
                  Nenhum teste ativo para execução.
                </div>
              )}

              {saving && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                  {allRunnerTestsCompleted
                    ? 'Finalizando a avaliação e promovendo os resultados para concluídos...'
                    : 'Salvando rascunho da sessão e mantendo os resultados individuais por teste...'}
                </div>
              )}

              <div className="flex flex-wrap items-center justify-end gap-3 border-t pt-4">
                <button
                  type="button"
                  onClick={closeRunner}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {runnerSession ? 'Fechar e manter rascunho' : 'Fechar'}
                </button>
                <button
                  type="button"
                  onClick={() => void (runnerSession ? finalizeDraftSession() : handleFinishRunner())}
                  disabled={saving || !allRunnerTestsCompleted}
                  className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Save className="mr-2 h-4 w-4" />
                  Finalizar avaliação
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {publicInviteMonitor && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-200 px-6 py-5">
              <div className="min-w-0">
                <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Link público</div>
                <h3 className="mt-1 text-lg font-semibold text-slate-900">
                  {publicInviteMonitor.status === 'completed'
                    ? 'Preenchimento concluído'
                    : publicInviteMonitor.status === 'started'
                      ? 'Paciente preenchendo'
                      : publicInviteMonitor.status === 'expired'
                        ? 'Link expirado'
                        : publicInviteMonitor.status === 'revoked'
                          ? 'Link revogado'
                          : 'Aguardando preenchimento'}
                </h3>
                <p className="mt-1 text-sm text-slate-600">{getPublicInviteMonitorCopy(publicInviteMonitor)}</p>
              </div>
              <button
                type="button"
                onClick={closePublicInviteMonitor}
                className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 px-6 py-5">
              <div className={`rounded-2xl border p-4 ${getPublicInviteMonitorTone(publicInviteMonitor.status).frame}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className={`rounded-full p-2 ${getPublicInviteMonitorTone(publicInviteMonitor.status).icon}`}>
                      {publicInviteMonitor.status === 'completed' ? (
                        <ClipboardList className="h-5 w-5" />
                      ) : ['active', 'started'].includes(publicInviteMonitor.status) ? (
                        <RefreshCw className="h-5 w-5 animate-spin" />
                      ) : (
                        <AlertTriangle className="h-5 w-5" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900">{publicInviteMonitor.patientName}</div>
                      <div className="text-xs text-slate-500">Convite de {publicInviteMonitor.tenantName}</div>
                      <div className="text-xs text-slate-600">
                        Expira em {new Date(publicInviteMonitor.expiresAt).toLocaleString('pt-BR')}
                      </div>
                    </div>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getPublicInviteMonitorTone(publicInviteMonitor.status).badge}`}>
                    {publicInviteMonitor.status === 'completed'
                      ? 'Concluído'
                      : publicInviteMonitor.status === 'started'
                        ? 'Em andamento'
                        : publicInviteMonitor.status === 'expired'
                          ? 'Expirado'
                          : publicInviteMonitor.status === 'revoked'
                            ? 'Revogado'
                            : 'Aguardando'}
                  </span>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Mensagem pronta</div>
                <div className="mt-2 whitespace-pre-wrap rounded-lg bg-white p-3 text-sm text-slate-800">
                  {publicInviteMonitor.shareMessage}
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Link público</div>
                <div className="mt-2 break-all text-sm font-medium text-slate-900">{publicInviteMonitor.url}</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {publicInviteMonitor.selectedTests.map((testKey) => (
                    <span key={`monitor-${publicInviteMonitor.inviteId}-${testKey}`} className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-700 shadow-sm">
                      {getAssessmentV2Label(testKey)}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  onClick={closePublicInviteMonitor}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900"
                >
                  Fechar acompanhamento
                </button>
                <button
                  type="button"
                  onClick={() => void copyGeneratedInviteLink(publicInviteMonitor.url)}
                  className="inline-flex items-center justify-center rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copiar link
                </button>
                <button
                  type="button"
                  onClick={() => void copyGeneratedInviteMessage(publicInviteMonitor.shareMessage)}
                  className="inline-flex items-center justify-center rounded-md border border-sky-200 bg-sky-50 px-4 py-2 text-sm font-medium text-sky-800 hover:bg-sky-100"
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copiar mensagem
                </button>
                {publicInviteMonitor.patientPhone && (
                  <button
                    type="button"
                    onClick={() => {
                      const whatsappUrl = buildWhatsAppUrl(publicInviteMonitor.patientPhone, publicInviteMonitor.shareMessage);
                      if (!whatsappUrl) {
                        toast.error('Não foi possível montar o link do WhatsApp para este paciente.');
                        return;
                      }
                      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
                    }}
                    className="inline-flex items-center justify-center rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
                  >
                    <MessageCircle className="mr-2 h-4 w-4" />
                    Abrir WhatsApp
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}


      <AssessmentReportEditorModal
        open={showAssessmentReportModal}
        form={assessmentReportForm}
        patients={patients}
        candidates={assessmentReportCandidates}
        candidatesLoading={assessmentReportCandidatesLoading}
        saving={assessmentReportSaving}
        editing={Boolean(assessmentReportEditingResultId)}
        canCreateTechnicalReport={canCreateTechnicalAssessmentReport}
        onPatientChange={(patientId) => void handleAssessmentReportPatientChange(patientId)}
        onToggleSource={toggleAssessmentReportSource}
        onRemoveSource={removeAssessmentReportSource}
        onChange={setAssessmentReportForm}
        onClose={closeAssessmentReportModal}
        onSubmit={() => void handleSaveAssessmentReport()}
        getResultMetric={getResultPrimaryMetric}
        getResultClassification={getResultSeverityLabel}
      />

      {tc6PdfExportState && (
        <div className="fixed inset-0 z-[65] flex items-center justify-center bg-slate-900/70 p-4">
          <div className="w-full max-w-xl rounded-[28px] border border-slate-200 bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">Exportação de avaliação</div>
                <h3 className="mt-2 text-xl font-black tracking-tight text-slate-900">
                  {tc6PdfExportState.action === 'download' ? 'Baixar PDF' : 'Imprimir PDF'} · {getAssessmentV2Label(tc6PdfExportState.result.testKey)}
                </h3>
                <p className="mt-2 text-sm text-slate-600">
                  {tc6PdfExportState.result.testKey === 'TC6' ? 'Escolha entre um PDF sem comparações ou a versão com tabela comparativa e linha histórica tracejada.' : 'Escolha se deseja incluir a comparação com avaliações anteriores.'}
                </p>
              </div>
              <button
                type="button"
                onClick={closeTc6PdfExportState}
                disabled={resultActionLoading}
                className="text-slate-400 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {tc6PdfComparisonPreviewLabel ? (
              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                Histórico selecionado para o overlay: <span className="font-semibold text-slate-900">{tc6PdfComparisonPreviewLabel}</span>
              </div>
            ) : null}

            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closeTc6PdfExportState}
                disabled={resultActionLoading}
                className="rounded-xl px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void handleConfirmTc6PdfExport(false)}
                disabled={resultActionLoading}
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {resultActionLoading ? 'Gerando...' : 'Exportar sem histórico'}
              </button>
              <button
                type="button"
                onClick={() => void handleConfirmTc6PdfExport(true)}
                disabled={resultActionLoading}
                className="inline-flex items-center justify-center rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {resultActionLoading ? 'Gerando...' : 'Exportar com histórico'}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-6xl overflow-y-auto rounded-[28px] bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-5">
              <div className="flex items-center gap-4">
                <div className="rounded-2xl border border-primary-100 bg-primary-50 p-3">
                  <FileText className="h-6 w-6 text-primary-700" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">
                    {isAnyAssessmentReportResult(selectedResult) ? getAssessmentV2ResultLabel(selectedResult) : `Resultado ${getAssessmentV2Label(selectedResult.testKey)}`}
                  </h3>
                  <p className="text-xs font-medium uppercase tracking-[0.25em] text-slate-500">
                    {snapshot.branding?.displayName || selectedClient?.clientName || 'Tenant ativo'}
                  </p>
                </div>
              </div>
              <button
                className="rounded-xl p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={() => setSelectedResult(null)}
                disabled={tc6PayloadSaving || resultPayloadSaving}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-6 bg-slate-50 px-6 py-6">
              {selectedResultSignature && (
                <div className="rounded-[24px] border border-sky-200 bg-sky-50 p-5 text-sky-900 shadow-sm">
                  <div className="text-sm font-bold uppercase tracking-[0.2em] text-sky-700">Documento assinado</div>
                  <div className="mt-2 text-sm">
                    Esta avaliação foi assinada eletronicamente e o PDF agora usa QR code de autenticidade.
                  </div>
                  <div className="mt-2 text-xs text-sky-800">
                    {selectedResultSignature.signedByName} • {new Date(selectedResultSignature.signedAt).toLocaleString('pt-BR')} • código{' '}
                    {selectedResultSignature.verificationToken}
                  </div>
                </div>
              )}

              {selectedResult.status === 'cancelled' &&
                (() => {
                  const latestInvalidation = getLatestAssessmentResultStatusEntry(selectedResult, 'invalidated');
                  return (
                    <div className="rounded-[24px] border border-red-200 bg-red-50 p-5 text-red-900 shadow-sm">
                      <div className="text-sm font-bold uppercase tracking-[0.2em] text-red-700">Avaliação invalidada</div>
                      <div className="mt-2 text-sm">
                        {latestInvalidation?.justification || 'Esta avaliação foi invalidada e está oculta da visualização padrão.'}
                      </div>
                      {latestInvalidation && (
                        <div className="mt-2 text-xs text-red-700">
                          {latestInvalidation.userName} • {new Date(latestInvalidation.timestamp).toLocaleString('pt-BR')}
                        </div>
                      )}
                    </div>
                  );
                })()}

              {isAssessmentV2LegacyReadOnlyResult(selectedResult) && (
                <div className="rounded-[24px] border border-amber-200 bg-amber-50 p-5 text-amber-950 shadow-sm">
                  <div className="text-sm font-bold uppercase tracking-[0.2em] text-amber-800">Versão legada preservada</div>
                  <div className="mt-2 text-sm leading-relaxed">
                    Este registro permanece disponível para consulta e PDF, mas não pode ser editado nem receber a pontuação ou a classificação da versão clínica atual.
                  </div>
                </div>
              )}

              <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                  <div>
                    <p className="mb-1 text-[10px] font-black uppercase tracking-[0.25em] text-primary-600">Paciente</p>
                    <h4 className="text-lg font-bold text-slate-900">{selectedResult.patientNameSnapshot || 'Paciente'}</h4>
                  </div>
                  <div>
                    <p className="mb-1 text-[10px] font-black uppercase tracking-[0.25em] text-slate-500">Identificação</p>
                    <h4 className="text-lg font-bold text-slate-900">
                      {(getAssessmentResultIdentifier(selectedResult) ??
                        getPreferredPatientIdentifier(patientLookup.get(selectedResult.patientId))) ||
                        'Sem identificador'}
                    </h4>
                  </div>
                  <div>
                    <p className="mb-1 text-[10px] font-black uppercase tracking-[0.25em] text-slate-500">Data</p>
                    <h4 className="text-lg font-bold text-slate-900">{new Date(selectedResult.assessmentDate).toLocaleString('pt-BR')}</h4>
                  </div>
                  <div>
                    <p className="mb-1 text-[10px] font-black uppercase tracking-[0.25em] text-slate-500">Profissional</p>
                    <h4 className="text-lg font-bold text-slate-900">{selectedResult.professionalNameSnapshot || '—'}</h4>
                    <p className="text-sm text-slate-500">{selectedResult.professionalRegistrationSnapshot || 'Sem registro'}</p>
                  </div>
                </div>
              </div>

              {isAssessmentReportResult(selectedResult) ? (
                <AssessmentReportRenderer payloadJson={selectedResult.payloadJson} />
              ) : isLegacyCompositeReportResult(selectedResult) ? (
                <CompositeReportRenderer payload={selectedResult.payloadJson} />
              ) : showResultPayloadEditor ? (
                renderSelectedResultPayloadEditor(selectedResult)
              ) : selectedResult.testKey === 'INITIAL_ASSESSMENT' ? (
                <div className="space-y-4">
                  <InitialAssessmentResultRenderer payload={selectedResult.payloadJson} />
                  <div className="flex flex-wrap gap-3 rounded-2xl border border-slate-200 bg-white p-4">
                    <ActionButton type="button" variant="secondary" onClick={() => void openAssessmentReportModal(selectedResult.patientId, {
                      sourceResult: selectedResult,
                      documentModel: selectedResult.payloadJson.assessmentMoment === 'final' ? 'final' : 'initial',
                    })}><FileText className="h-4 w-4" />Emitir avaliação clínica</ActionButton>
                    <ActionButton type="button" variant="secondary" onClick={() => void openAssessmentReportModal(selectedResult.patientId, {
                      sourceResult: selectedResult, documentModel: 'longitudinal',
                    })}><Activity className="h-4 w-4" />Relatório longitudinal</ActionButton>
                    <ActionButton type="button" variant="primary" onClick={() => void openAssessmentReportModal(selectedResult.patientId, {
                      sourceResult: selectedResult, documentModel: 'findings', technical: true,
                    })}><Stethoscope className="h-4 w-4" />Diagnóstico CBDF e achados</ActionButton>
                  </div>
                </div>
              ) : selectedResult.testKey === 'TC6' ? (
                <TC6ResultRenderer
                  payload={resolveRunnerPayload('TC6', selectedResult)}
                  assessmentDate={selectedResult.assessmentDate}
                  chartImages={selectedResult.assetsJson?.chartImages as Tc6ChartImages | undefined}
                  comparisonHistory={tc6ComparisonHistory}
                  selectedComparisonId={selectedTc6ComparisonId}
                  onComparisonChange={setSelectedTc6ComparisonId}
                />
              ) : selectedResult.testKey === 'ISWT' ? (
                <ISWTResultRenderer payload={selectedResult.payloadJson as ShuttleIswtResultPayload} />
              ) : selectedResult.testKey === 'ESWT' ? (
                <ESWTResultRenderer payload={selectedResult.payloadJson as ShuttleEswtResultPayload} />
              ) : selectedResult.testKey === '6MST' ? (
                <MST6ResultRenderer payload={selectedResult.payloadJson as SixMSTResultPayload} />
              ) : selectedResult.testKey === 'CHESTER' ? (
                <ChesterStepResultRenderer payload={selectedResult.payloadJson as ChesterResultPayload} />
              ) : selectedResult.testKey === 'HANDGRIP' ? (
                <HandgripResultRenderer
                  payload={resolveRunnerPayload('HANDGRIP', selectedResult)}
                  chartImages={selectedResult.assetsJson?.chartImages as HandgripChartImages | undefined}
                />
              ) : selectedResult.testKey === 'PERIPHERAL_MUSCLE_STRENGTH' ? (
                <PeripheralMuscleStrengthResultRenderer
                  payload={
                    resolveRunnerPayload('PERIPHERAL_MUSCLE_STRENGTH', selectedResult) as PeripheralMuscleStrengthResultPayload
                  }
                />
              ) : selectedResult.testKey === 'ONE_RM' ? (
                <OneRMResultRenderer payload={resolveRunnerPayload('ONE_RM', selectedResult) as OneRMResultPayload} />
              ) : selectedResult.testKey === 'PIMAX' ? (
                <PimaxResultRenderer
                  payload={resolveRunnerPayload('PIMAX', selectedResult) as PimaxResultPayload}
                  chartImages={selectedResult.assetsJson?.chartImages as Record<string, string> | undefined}
                  assessmentDate={selectedResult.assessmentDate}
                  comparisonHistory={structuredComparisonHistory}
                />
              ) : selectedResult.testKey === 'PEMAX' ? (
                <PemaxResultRenderer
                  payload={resolveRunnerPayload('PEMAX', selectedResult) as PemaxResultPayload}
                  assessmentDate={selectedResult.assessmentDate}
                  comparisonHistory={structuredComparisonHistory}
                />
              ) : selectedResult.testKey === 'S_INDEX' ? (
                <SIndexResultRenderer
                  payload={resolveRunnerPayload('S_INDEX', selectedResult) as SIndexResultPayload}
                  chartImages={selectedResult.assetsJson?.chartImages as Record<string, string> | undefined}
                  assessmentDate={selectedResult.assessmentDate}
                  comparisonHistory={structuredComparisonHistory}
                />
              ) : selectedResult.testKey === 'RESP_ENDURANCE' ? (
                <RespiratoryEnduranceResultRenderer
                  payload={resolveRunnerPayload('RESP_ENDURANCE', selectedResult) as RespiratoryEnduranceResultPayload}
                  chartImages={selectedResult.assetsJson?.chartImages as Record<string, string> | undefined}
                  assessmentDate={selectedResult.assessmentDate}
                  comparisonHistory={structuredComparisonHistory}
                />
              ) : selectedResult.testKey === 'PEAK_COUGH_FLOW' ? (
                <PeakCoughFlowResultRenderer payload={selectedResult.payloadJson as PeakCoughFlowResultPayload} assessmentDate={selectedResult.assessmentDate} comparisonHistory={structuredComparisonHistory} />
              ) : selectedResult.testKey === 'CV_POSTURAL' ? (
                <PosturalVitalCapacityResultRenderer payload={selectedResult.payloadJson as PosturalVitalCapacityResultPayload} assessmentDate={selectedResult.assessmentDate} comparisonHistory={structuredComparisonHistory} />
              ) : selectedResult.testKey === 'SNIP' ? (
                <SnipResultRenderer payload={selectedResult.payloadJson as SnipResultPayload} assessmentDate={selectedResult.assessmentDate} comparisonHistory={structuredComparisonHistory} />
              ) : selectedResult.testKey === 'PEAK_FLOW' ? (
                <PeakFlowResultRenderer
                  payload={resolveRunnerPayload('PEAK_FLOW', selectedResult) as PeakFlowResultPayload}
                  assessmentDate={selectedResult.assessmentDate}
                  comparisonHistory={structuredComparisonHistory}
                />
              ) : selectedResult.testKey === 'TUG' ? (
                <TUGResultRenderer payload={buildTugPreviewPayload(selectedResult, patientLookup.get(selectedResult.patientId))} />
              ) : selectedResult.testKey === '4MGS' ? (
                <GaitSpeedResultRenderer
                  payload={selectedResult.payloadJson as GaitSpeedResultPayload}
                  assessmentDate={selectedResult.assessmentDate}
                  comparisonHistory={structuredComparisonHistory}
                />
              ) : selectedResult.testKey === '5xSTS' || selectedResult.testKey === 'STS_30S' || selectedResult.testKey === 'STS_1MIN' ? (
                <STSResultRenderer
                  testKey={selectedResult.testKey as StsTestKey}
                  payload={buildStsPreviewPayload(selectedResult, patientLookup.get(selectedResult.patientId))}
                  assessmentDate={selectedResult.assessmentDate}
                  comparisonHistory={structuredComparisonHistory}
                />
              ) : selectedResult.testKey === 'ACT' ? (
                <ACTResultRenderer payload={selectedResult.payloadJson} />
              ) : selectedResult.testKey === 'AQLQ' ? (
                <AQLQResultRenderer payload={selectedResult.payloadJson} />
              ) : selectedResult.testKey === 'mMRC' ? (
                <MMRCResultRenderer payload={selectedResult.payloadJson} />
              ) : selectedResult.testKey === 'SGRQ' ? (
                <SGRQResultRenderer payload={selectedResult.payloadJson} />
              ) : selectedResult.testKey === 'SAQ' ? (
                <SAQResultRenderer payload={selectedResult.payloadJson} />
              ) : selectedResult.testKey === 'PFSDQ-M' ? (
                <PFSDQMResultRenderer payload={selectedResult.payloadJson} />
              ) : selectedResult.testKey === 'HADS' ? (
                <HADSResultRenderer payload={selectedResult.payloadJson} />
              ) : selectedResult.testKey === 'PSQI' ? (
                <PSQIResultRenderer payload={selectedResult.payloadJson} />
              ) : selectedResult.testKey === 'MLHFQ' ? (
                <MLHFQResultRenderer payload={selectedResult.payloadJson} />
              ) : selectedResult.testKey === 'LCADL' ? (
                <LCADLResultRenderer payload={selectedResult.payloadJson} />
              ) : (
                <CATResultRenderer payload={selectedResult.payloadJson} />
              )}

              {selectedResult.testKey === 'TC6' && showTc6PayloadEditor && (
                <TC6ResultEditor
                  payload={resolveRunnerPayload('TC6', selectedResult)}
                  saving={tc6PayloadSaving}
                  onCancel={cancelTc6PayloadEditor}
                  onSave={(payload) => void handleSaveTc6PayloadEdit(payload)}
                />
              )}

              {selectedResult.notes && (
                <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
                  <div className="mb-2 text-xs font-black uppercase tracking-[0.25em] text-slate-400">Observações</div>
                  <div className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{selectedResult.notes}</div>
                </div>
              )}

              {!isAnyAssessmentReportResult(selectedResult)
              && selectedResult.testKey !== 'TC6'
              && !supportsAssessmentHistoryComparison(selectedResult.testKey) ? (
                <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
                  <div className="mb-3 flex items-center justify-between">
                    <div>
                      <div className="text-xs font-black uppercase tracking-[0.25em] text-slate-400">
                        {isAssessmentV2QuestionnaireTest(selectedResult.testKey)
                          ? 'Comparação com resultados anteriores'
                          : 'Histórico recente'}
                      </div>
                      <div className="mt-1 font-semibold text-slate-900">
                        {getAssessmentV2Label(selectedResult.testKey)} anteriores deste paciente
                      </div>
                      {isAssessmentV2QuestionnaireTest(selectedResult.testKey) && (
                        <div className="mt-1 text-xs text-slate-500">
                          A diferença corresponde ao resultado atual menos o resultado anterior.
                        </div>
                      )}
                    </div>
                    {loadingResultHistory && <div className="text-sm text-slate-500">Carregando...</div>}
                  </div>
                  {selectedResultHistory.length === 0 ? (
                    <div className="text-sm text-slate-500">Nenhuma avaliação anterior deste mesmo teste encontrada.</div>
                  ) : isAssessmentV2QuestionnaireTest(selectedResult.testKey) ? (
                    <div className="overflow-x-auto rounded-2xl border border-slate-200">
                      <table className="min-w-[760px] w-full border-collapse text-left text-sm">
                        <thead className="bg-slate-50 text-xs font-black uppercase tracking-[0.12em] text-slate-500">
                          <tr>
                            <th className="px-4 py-3">Data</th>
                            <th className="px-4 py-3">Resultado anterior</th>
                            <th className="px-4 py-3">Diferença para o atual</th>
                            <th className="px-4 py-3">Classificação</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {selectedResultHistory.map((historyItem) => (
                            <tr key={historyItem.resultId} className="text-slate-700">
                              <td className="whitespace-nowrap px-4 py-3">
                                {new Date(historyItem.assessmentDate).toLocaleString('pt-BR')}
                              </td>
                              <td className="px-4 py-3 font-bold text-slate-900">{getResultPrimaryMetric(historyItem)}</td>
                              <td className="px-4 py-3 font-semibold text-slate-700">
                                {formatQuestionnaireDifference(selectedResult, historyItem)}
                              </td>
                              <td className="px-4 py-3 text-slate-600">{getResultSeverityLabel(historyItem)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {selectedResultHistory.map((historyItem) => {
                        return (
                          <div
                            key={historyItem.resultId}
                            className="flex flex-col gap-2 rounded-2xl bg-slate-50 px-4 py-3 text-sm md:flex-row md:items-center md:justify-between"
                          >
                            <div className="text-slate-700">{new Date(historyItem.assessmentDate).toLocaleString('pt-BR')}</div>
                            <div className="font-bold text-slate-900">{getResultPrimaryMetric(historyItem)}</div>
                            <div className="text-slate-600">{getResultSeverityLabel(historyItem)}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : null}

              {getAssessmentResultStatusHistory(selectedResult).length > 0 && (
                <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
                  <div className="mb-3 text-xs font-black uppercase tracking-[0.25em] text-slate-400">Histórico de auditoria</div>
                  <div className="space-y-3">
                    {getAssessmentResultStatusHistory(selectedResult).map((entry, index) => (
                      <div key={`${entry.action}-${entry.timestamp}-${index}`} className="rounded-2xl bg-slate-50 px-4 py-3 text-sm">
                        <div className="font-semibold text-slate-900">
                          {entry.action === 'invalidated' ? 'Invalidação' : 'Restauração'}
                        </div>
                        <div className="mt-1 text-slate-700">{entry.justification}</div>
                        <div className="mt-1 text-xs text-slate-500">
                          {entry.userName} • {new Date(entry.timestamp).toLocaleString('pt-BR')}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 pt-4">
                {selectedResult.status !== 'cancelled' && (
                  <>
                    {isAssessmentReportResult(selectedResult) && !selectedResultSignature && (
                      <button
                        type="button"
                        onClick={() => void openAssessmentReportEditor(selectedResult)}
                        disabled={assessmentReportSaving}
                        className="inline-flex items-center rounded-xl border border-violet-200 bg-white px-4 py-2 text-sm font-semibold text-violet-700 hover:bg-violet-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        Editar relatório
                      </button>
                    )}
                    {!isAnyAssessmentReportResult(selectedResult)
                    && !selectedResultSignature
                    && !isAssessmentV2LegacyReadOnlyResult(selectedResult) && (
                      <button
                        type="button"
                        onClick={() => {
                          if (showTc6PayloadEditor || showResultPayloadEditor) {
                            if (showTc6PayloadEditor) {
                              cancelTc6PayloadEditor();
                            } else {
                              cancelSelectedResultPayloadEditor();
                            }
                            return;
                          }
                          openSelectedResultPayloadEditor();
                        }}
                        disabled={tc6PayloadSaving || resultPayloadSaving}
                        className="inline-flex items-center rounded-xl border border-amber-200 bg-white px-4 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {showTc6PayloadEditor || showResultPayloadEditor
                          ? `Fechar editor de ${getAssessmentV2Label(selectedResult.testKey)}`
                          : `Editar resultado e laudo`}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => void runPdfAction(selectedResult, 'download')}
                      disabled={
                        resultActionLoading ||
                        tc6PayloadSaving ||
                        resultPayloadSaving ||
                        showTc6PayloadEditor ||
                        showResultPayloadEditor
                      }
                      className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Baixar PDF
                    </button>
                    {selectedResultSignature ? (
                      <button
                        type="button"
                        onClick={() => openVerificationPage(selectedResultSignature.verificationToken)}
                        className="inline-flex items-center rounded-xl border border-sky-200 bg-white px-4 py-2 text-sm font-semibold text-sky-700 hover:bg-sky-50"
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        Conferir autenticidade
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => void handleSignResult(selectedResult)}
                        disabled={signingResultId === selectedResult.resultId}
                        className="inline-flex items-center rounded-xl border border-emerald-200 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <FileText className="mr-2 h-4 w-4" />
                        Assinar
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() =>
                        setStatusModalState({
                          mode: 'invalidate',
                          result: selectedResult,
                        })
                      }
                      disabled={tc6PayloadSaving || resultPayloadSaving || showTc6PayloadEditor || showResultPayloadEditor}
                      className="inline-flex items-center rounded-xl border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <AlertTriangle className="mr-2 h-4 w-4" />
                      Invalidar
                    </button>
                  </>
                )}
                {selectedResult.status === 'cancelled' && (
                  <>
                    {selectedResultSignature && (
                      <button
                        type="button"
                        onClick={() => openVerificationPage(selectedResultSignature.verificationToken)}
                        className="inline-flex items-center rounded-xl border border-sky-200 bg-white px-4 py-2 text-sm font-semibold text-sky-700 hover:bg-sky-50"
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        Conferir autenticidade
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() =>
                        setStatusModalState({
                          mode: 'restore',
                          result: selectedResult,
                        })
                      }
                      className="inline-flex items-center rounded-xl border border-emerald-200 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50"
                    >
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Restaurar
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => setSelectedResult(null)}
                  disabled={tc6PayloadSaving || resultPayloadSaving}
                  className="inline-flex items-center rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {statusModalState && (
        <AssessmentResultStatusModal
          result={statusModalState.result}
          mode={statusModalState.mode}
          onClose={() => setStatusModalState(null)}
          onConfirm={(resultId, justification) =>
            statusModalState.mode === 'invalidate'
              ? handleInvalidateResult(resultId, justification)
              : handleRestoreResult(resultId, justification)
          }
        />
      )}
    </div>
  );
};

export default NewAssessmentsV2Page;
